import maplibregl, {
  SourceSpecification,
  LayerSpecification,
  StyleSpecification,
  LngLatBoundsLike,
  LngLatLike,
  PointLike,
  // TerrainSpecification,
  RasterDEMSourceSpecification,
  FitBoundsOptions,
  MapGeoJSONFeature,
  MapMouseEvent,
  // DataDrivenPropertyValueSpecification
} from 'maplibre-gl';

import { Popup } from 'maplibre-gl';

import type { Feature, Geometry, Position } from 'geojson';

(() => {
  const original = maplibregl.setRTLTextPlugin.bind(maplibregl);
  maplibregl.setRTLTextPlugin = ((url: string, lazy?: boolean) => {
    try {
      if (maplibregl.getRTLTextPluginStatus() !== 'unavailable') {
        return Promise.resolve();
      }
    } catch {
      // Fall through and try the original once.
    }
    try {
      return Promise.resolve(original(url, lazy as boolean)).catch(() => undefined);
    } catch {
      return Promise.resolve();
    }
  }) as typeof maplibregl.setRTLTextPlugin;
})();

import expression from "./Expression";
import {
  polygon,
  point,
  booleanPointInPolygon,
  bbox as turfBbox,
  buffer as turfBuffer,
  booleanOverlap,
  booleanContains,
  booleanDisjoint,
  union as turfUnion,
} from '@turf/turf';
import * as Utils from './utils';
import { PointVectorLayer } from './PointVectorLayer';
import { LineVectorLayer } from './LineVectorLayer';
import { PolygonVectorLayer } from './PolygonVectorLayer';
import { baseMaps } from './const/baseMaps';
import {
  catalogStyleAuthForMapUuid,
  prepareVectorBasemapStyle,
  appendApikey,
} from './vectorBasemapAuth';

import SRStyle from './const/drawStyle.json';

import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import MapboxDraw, { DrawCustomMode } from '@mapbox/mapbox-gl-draw';

import SRMode from "./libs/draw-toolbox/ScaleAndRotateModes";
import { ensureDrawTransformIcons } from './libs/draw-toolbox/utils/drawTransformIcons';
import SplitPolygonMode from "./libs/draw-toolbox/SplitPolygonMode";
import SplitLineMode from "./libs/draw-toolbox/SplitLineMode";
import CutPolygonMode from "./libs/draw-toolbox/CutPolygonMode";
import PassingMode from "./libs/draw-toolbox/utils/PassingMode";
// import ExtendDrawBar from "./libs/draw-toolbox/utils/ExtendDrawBar";
// import ExtendDrawBarCheckboxes from "./libs/draw-toolbox/utils/ExtendDrawBarCheckboxes";
import SnapLineMode from "./libs/draw-toolbox/SnapLineMode";
import SnapPolygonMode from "./libs/draw-toolbox/SnapPolygonMode";
import SnapFreehandPolygonMode from "./libs/draw-toolbox/SnapFreehandPolygonMode.js";
import SnapPointMode from "./libs/draw-toolbox/SnapPointMode";
import SimpleSelect from "./libs/draw-toolbox/SimpleSelect";
import DirectSelect from "./libs/draw-toolbox/DirectSelect";
import PaintMode from "./libs/draw-toolbox/PaintMode";
import CircleMode from "./libs/draw-toolbox/CircleMode";
import DragCircleMode from "./libs/draw-toolbox/DragCircleMode";
import DrawAssistedRectangle from "./libs/draw-toolbox/RectangleMode";
import BezierMode from "./libs/draw-toolbox/BezierMode";
import PartialUpdateMode from "./libs/draw-toolbox/PartialUpdateMode";
import CreateHoleMode from "./libs/draw-toolbox/CreateHoleMode";

// import MapboxDraw from './libs/draw-toolbox/mapbox-gl-draw';
// import { SRMode, SRCenter, SRStyle } from "./libs/draw-toolbox/ScaleAndRotateModes";

import ScaleBar from './Controls/ScaleBar';
import { computeScaleBarSnapshot, type ScaleBarHostOptions } from './Controls/scaleBarCompute';
import {
  createMiniMapHost,
  destroyMiniMapHost,
  resizeMiniMapHost,
  type MiniMapHostOptions,
  type MiniMapHostState,
} from './Controls/miniMapHost';
import {
  MapModelsManager,
  createPlacedModelId,
  type MapPlacedModel,
  type MapModelsDocument,
} from './Models3D';
import {
  applyWgs84VertexToDrawFeature,
  removeWgs84VertexFromDrawFeature,
  buildEditGeometryPayload,
  isDrawSketchMode,
  isEditSelectMode,
  payloadFromFeature,
  positionsFromSelectionEvent,
  verticesFromFeature,
  type EditGeometryOptions,
  type EditGeometryCursorPayload,
} from './Controls/editGeometryCompute';
import { geometryWebMercatorToLngLat } from './Controls/featureCreateGeometry';

import {
  geoboxServer,
  geoboxMap,
  layerSource,
  layerObj,
  maplibreSource,
  baseMapTypes,
  customBasemap,
  mapSettings,
  layerType,
  property, 
  expressionType,
  Source,
  GeoboxDraw,
  MeasurementOptions,
  SelectByLocationOptions,
  SelectByLocationGeometryOptions,
  SelectByLocationDrawMode,
  MeasurementGeometryPayload,
  MeasurementGeometryVertex,
  MeasurementLengthUnit,
  MeasurementAreaUnit,
} from './Types';
import {
  formatAreaSquareMeters,
  formatLengthMeters,
} from './measurementUnits';

import { FeatureLayer } from './FeatureLayer';
import { RasterLayer } from './RasterLayer';

import layout from './const/layout.json';
import defaults from './const/default.json';

const MAP_MIN_ZOOM: number = 0;
const MAP_MAX_ZOOM: number = 28;
const BUILTIN_BASEMAP_ID = 'geobox-gl-base-map';
const CUSTOM_BASEMAP_ID = 'geobox-gl-custom-base-map';
/** Prefixed runtime stack for vector basemaps loaded from another app map's style. */
const VECTOR_BASEMAP_PREFIX = 'geobox-gl-vector-basemap--';
const VECTOR_MAP_BASEMAP_REF_PREFIX = 'map:';

function isVectorMapBasemapRef(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith(VECTOR_MAP_BASEMAP_REF_PREFIX) && value.length > VECTOR_MAP_BASEMAP_REF_PREFIX.length;
}

function parseVectorMapBasemapUuid(value: unknown): string | null {
  if (!isVectorMapBasemapRef(value)) return null;
  return value.slice(VECTOR_MAP_BASEMAP_REF_PREFIX.length).trim() || null;
}

function isVectorBasemapStyleId(id: string): boolean {
  return String(id ?? '').startsWith(VECTOR_BASEMAP_PREFIX);
}

function isSketchLikeDrawMode(mode: string): boolean {
  return isDrawSketchMode(mode);
}

export class Map extends maplibregl.Map {
  private _setCanvasCursor(cursor: string): void {
    for (const el of [this.getCanvasContainer?.(), this.getCanvas?.()]) {
      if (!el) continue;
      // Use !important to override draw-mode css classes and map hover handlers.
      el.style.setProperty('cursor', cursor, 'important');
    }
  }

  private _clearCanvasCursor(): void {
    for (const el of [this.getCanvasContainer?.(), this.getCanvas?.()]) {
      if (!el) continue;
      el.style.removeProperty('cursor');
    }
  }

  private _syncCursorFromDrawMode(): void {
    if (!this.drawTools) {
      this._setCanvasCursor('default');
      return;
    }
    // Measurement needs a crosshair even while gl-draw is in simple_select between sketches.
    if (this.measurementMode) {
      this._setCanvasCursor('crosshair');
      return;
    }
    const mode = String(this.drawTools.getMode?.() ?? '');
    if (isSketchLikeDrawMode(mode) && (this.drawMode || this.selectByLocationMode)) {
      this._setCanvasCursor('crosshair');
      return;
    }
    if (mode === 'direct_select' || mode === 'simple_select') {
      this._setCanvasCursor('grab');
      return;
    }
    this._setCanvasCursor('default');
  }


  // private mapElemID: string;

  private serverMap?: geoboxMap;
  private userID?: number;


  public originalStyle: StyleSpecification;

  public symbolNames: Array<string>;

  public server: geoboxServer;

  public models3d: MapModelsManager;

  public drawTools: GeoboxDraw;
  public measurementMode: boolean;
  public selectByLocationMode: boolean;
  public drawMode: boolean;

  private _measurementOptions?: MeasurementOptions;

  private _selectByLocationOnDrawCreate = (e?: { features?: Feature[] }) => {
    const drawnFeature = this._resolveSelectByDrawFeature(e);
    if (!drawnFeature) return;

    const filterPolygon = this._toSelectByLocationFilterPolygon(drawnFeature);
    if (!filterPolygon) return;

    const featureId =
      drawnFeature.id != null ? String(drawnFeature.id) : null;
    this._scheduleSelectByLocationPostDraw(filterPolygon, featureId);
  };

  private _selectByLocationLayerIds: string[] = [];

  private _selectByLocationOptions?: SelectByLocationOptions;

  private _selectByLocationPostDrawRaf = 0;

  private _editGeometryHostOptions?: EditGeometryOptions;

  private _editGeometryHostUiBound = false;

  private _lastEditGeometrySelectionPoints: Position[] = [];

  private _snapLayersUpdateTimer: ReturnType<typeof setTimeout> | null = null;

  private _snapLayersRequestId = 0;

  private _snapTolerancePixels = 5;

  private _snapMarkerLayerAdded = false;

  private _snapMapListenersBound = false;

  private _measurementPanelUpdateTimer: ReturnType<typeof setTimeout> | null = null;

  private _measurementDraftPopupsCleared = false;

  private _onSnapMoveEnd = (): void => {
    this.scheduleUpdateSnapLayers();
    this._refreshSnapToleranceFromPixels();
  };

  private _onSnapZoom = (): void => {
    this._refreshSnapToleranceFromPixels();
  };

  private _onSnapZoomEnd = (): void => {
    this.scheduleUpdateSnapLayers();
  };

  public selectedFeatures: MapGeoJSONFeature[] = [];

  private _currentPopup: maplibregl.Popup | undefined;
  private _currentSubPopups: Array<maplibregl.Popup> | [];

  public _popups: Array<{ id: any; popup: maplibregl.Popup | undefined; subPopups: Array<maplibregl.Popup> | []; }>;

  private _basemapsServerUrl: Array<string> | null;

  public libreMapRasterLayers: { [index: string]: RasterLayer } = {};

  public libreMapFeatureLayers: { [index: string]: FeatureLayer } = {};

  public mapSettings: mapSettings;

  public pulsingDot: any;

  private scaleBar: any;

  private _hostScaleBarMove?: () => void;

  private _hostMiniMap?: MiniMapHostState;

  public dualMap: Map | null = null;

  private _dualMapSyncing = false;

  private _dualMapSyncEnabled = false;

  private _dualMapSyncPrimary?: () => void;

  private _dualMapSyncSecondary?: () => void;

  private static readonly DUAL_MAP_CONTAINER_ID = 'geo-map-dual';

  private static readonly DUAL_MAP_SHELL_CLASS = 'geo-map-shell';

  constructor(mapEl: string, server: geoboxServer) {
    if (!mapEl) {
      throw `Invalid type: 'container' must be a String.`;
    }

    if (!server) {
      throw `Server is undefined.`;
    }

    // Safe even if called for every Map — setRTLTextPlugin is patched at module load.
    void Promise.resolve(
      maplibregl.setRTLTextPlugin(
        `${server._origin}/static/libs/third-party/mapbox-gl-rtl-text.js`,
        true // Lazy load the plugin
      )
    ).catch(() => {});

    
    const emptyPointCollection: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: { type: 'Point', coordinates: [] },
        },
      ],
    };

    const emptyLineCollection: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: [] },
        },
      ],
    };

    const emptyPolygonCollection: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: { type: 'Polygon', coordinates: [] },
        },
      ],
    };

    // Bake runtime overlays into the bootstrap style so openMap/setStyle races
    // cannot hit Map.addSource while a style swap is mid-load.
    const originalStyle: StyleSpecification = {
      version: 8,
      name: 'Geobox',
      sources: {
        'geobox-gl-splash-circle': {
          type: 'geojson',
          data: emptyPointCollection,
        },
        'geobox-gl-snap-vertex': {
          type: 'geojson',
          data: emptyPointCollection,
        },
        'geobox-gl-snap-point': {
          type: 'geojson',
          maxzoom: 24,
          tolerance: 0,
          data: emptyPointCollection,
        },
        'geobox-gl-snap-line': {
          type: 'geojson',
          maxzoom: 24,
          tolerance: 0,
          data: emptyLineCollection,
        },
        'geobox-gl-snap-polygon': {
          type: 'geojson',
          maxzoom: 24,
          tolerance: 0,
          data: emptyPolygonCollection,
        },
        'geobox-gl-snap-marker': {
          type: 'geojson',
          maxzoom: 24,
          tolerance: 0,
          data: emptyPointCollection,
        },
      },
      layers: [],
      glyphs: `${server._origin}/fonts/{fontstack}/{range}.pbf`,
      sprite: `${server._origin}/sprites/basic`,
    };

    
    super({
      container: mapEl,
      maplibreLogo: false,
      attributionControl: false,
      style: originalStyle,
      center: [1, 15],
      zoom: 3,
      maxPitch: 75,
      minZoom: MAP_MIN_ZOOM,
      maxZoom: MAP_MAX_ZOOM,
      // Needed so Three.js custom layers (3D models) antialias correctly.
      canvasContextAttributes: { antialias: true },
      transformRequest: (url) => {
        // A URL that already carries apikey is authorized by that key.
        // Adding the session bearer on top makes public catalog tilesets and layers 401.
        if (url.indexOf('apikey=') !== -1) {
          return { url };
        }
        if (
          server._apikey &&
          // (url.indexOf('geobox.ir') > -1 || url.indexOf('app.geo-mind.ai') > -1)  &&
          url.indexOf('apikey') == -1
        ) {
          if (url.indexOf('?') > -1) {
            return {
              url: `${url}&apikey=${server._apikey}`,
            };
          }
          return {
            url: `${url}?apikey=${server._apikey}`,
          };
        }
        if (server._access_token 
          // && (url.indexOf('geobox.ir') > -1 || url.indexOf('app.geo-mind.ai') > -1)
        ) {
          return {
            url,
            headers: { Authorization: `Bearer ${server._access_token}` },
          };
        }
      },
    });

    this.symbolNames = [];

    this.mapSettings = {
      general_settings: {
        map_unit: 'lnglat',
        base_map: 'blank',
        flash_color: '#ff0000',
        highlight_color: '#ff7300',
        selection_color: '#06f4cc',
        selectable_layers: 'ALL',
        show_maptip_on: 'None',
        calendar_type: 'default',
        custom_basemaps: [],
      },
      edit_settings: {
        target_layer: undefined,
        editable_layers: 'ALL',
      },
      snap_settings: {
        snap_tolerance: 5,
        snap_unit: 'pixels',
        snap_mode: 'both',
        snap_cache: 500,
        snappable_layers: 'ALL',
      },
      search_settings: {
        search_mode: 'both',
        search_layers: 'ALL',
      },
      marker_settings: {
        remvoe_unused_tags: true,
      },
      controls: [
        'overview',
        'data_analytics',
        'manage_3d_model',
        'compare_maps',
        'show_all_attachments',
        'scalebar',
        'searchbar',
        'legend',
        'basemaps',
        'coordinates',
        'measurement',
        'print',
        'gotoxy',
        'location',
        'edit',
      ],
      terrain_settings: { terrain_layer: null, exaggeration: 1 },
      grid_settings: {
        enable_grid: false,
        grid_width: 1,
        grid_height: 1,
        grid_unit: 'degrees',
        grid_minzoom: 0,
        grid_maxzoom: 22,
      },
      view_settings: {
        center: [59, 30],
        zoom: 5,
        bearing: 0,
        pitch: 0,
      },
      toc_settings: [],
    };

    // this.drawTools = new MapboxDraw({
    //   styles: SRStyle
    //     // displayControlsDefault: false,
    //     // controls: {
    //     //     polygon: true,
    //     //     trash: true
    //     // }
    // });

    this.drawTools = <GeoboxDraw> new MapboxDraw(<any>{
      modes: {
        ...MapboxDraw.modes,
        simple_select: <any>SimpleSelect,
        direct_select: <any>DirectSelect,
        draw_point: <any>SnapPointMode,
        draw_polygon: <any>SnapPolygonMode,
        draw_polygon_freehand:<any>SnapFreehandPolygonMode,
        draw_line_string: <any>SnapLineMode,
        scaleRotateMode: <any>SRMode,
        splitLineMode: <any>SplitLineMode,
        draw_paint: <any>PaintMode,
        draw_circle: <any>CircleMode,
        // drag_circle  : DragCircleMode,
        draw_rectangle: <any>DrawAssistedRectangle,
        draw_bezier: <any>BezierMode,
        partial_update: <any>PartialUpdateMode,
        create_hole: <any>CreateHoleMode,

        passing_mode_point: PassingMode(MapboxDraw.modes.draw_point),
        splitPolygonMode: <any>SplitPolygonMode,
        passing_mode_line_string: PassingMode(
          MapboxDraw.modes.draw_line_string
        ),
        cutPolygonMode: <any>CutPolygonMode,
        passing_mode_polygon: PassingMode(MapboxDraw.modes.draw_polygon),
      },
      styles: SRStyle,

      snap: false,
      snapTolerancePixels:
        this.mapSettings["snap_settings"]["snap_tolerance"] ?? 5,
      snapTolerance:
        this.mapSettings["snap_settings"]["snap_tolerance"] *
          ((Math.cos((this.getCenter().lat * Math.PI) / 180.0) *
            2 *
            Math.PI *
            6378137) /
            (256 * 2 ** this.getZoom())) <
        99999
          ? this.mapSettings["snap_settings"]["snap_tolerance"] *
            ((Math.cos((this.getCenter().lat * Math.PI) / 180.0) *
              2 *
              Math.PI *
              6378137) /
              (256 * 2 ** this.getZoom()))
          : 99999,
      snapCache: this.mapSettings["snap_settings"]["snap_cache"],
      snapMode: this.mapSettings["snap_settings"]["snap_mode"],
      measurementUnit: "meters",
      // measuringMode: this.measuringMode,
    });

    this._snapTolerancePixels =
      this.mapSettings.snap_settings.snap_tolerance ?? 5;

    

    this.measurementMode = false;
    this._currentSubPopups = [];

    this._popups = [];
    this.drawMode = false;

    this.selectByLocationMode = false;
    this.selectedFeatures = [];

    this.scaleBar = undefined;

    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        try {
          // Measurement panel is closed by the Vue host (Measurement.vue).
          if (this.measurementMode) return;

          this._currentPopup?.remove();
          for (let popup of this._currentSubPopups){
            popup.remove();
          }
          this._currentSubPopups = [];
          this._measurementOptions = undefined;
          this.disableSelectByLocation();
          this.drawMode = false;
          this._clearCanvasCursor();
        } catch (error) {
        }
      }
    });
    
    this._basemapsServerUrl = null;

    

    this.originalStyle = originalStyle;

    this.server = server;
    this.models3d = new MapModelsManager(this);

    this.on('error', (event) => {
      const status = (event as { error?: { status?: number } })?.error?.status;
      if (status === 429) {
        window.alert(
          'There are too many tile requests. Upgrade your plan or contact your administrator.'
        );
        return;
      }

      // Drop inaccessible TileJSON/tile sources so style mutations (basemap, etc.) keep working.
      if (status === 404 || status === 403 || status === 401) {
        const sourceId =
          (event as { sourceId?: string }).sourceId
          ?? (event as { source?: { id?: string } }).source?.id;
        if (sourceId) {
          this._removeInaccessibleMapSource(sourceId);
        }
      }
    });

    this.on('remove', () => {
      this.disableHostScaleBar();
      this.disableHostMiniMap();
      this._unbindSnapMapListeners();
      this.models3d?.destroy();
    });

    this.on('load', async (e) => {
      const _this = this;

      // Never await before mutating the style — openMap() may call setStyle while
      // sprite.json is in flight and Map.addSource then throws "Style is not done loading".
      this._ensureRuntimeOverlayLayers();

      try {
        const sprite = await fetch(`${this.originalStyle.sprite}.json`);
        const sprites = await sprite.json();
        this.symbolNames = Object.keys(sprites);
      } catch {
        this.symbolNames = this.symbolNames ?? [];
      }

      // If openMap swapped the style during the sprite fetch, re-assert overlays once ready.
      if (!this.isStyleLoaded() || !this.getSource('geobox-gl-splash-circle')) {
        this.once('idle', () => this._ensureRuntimeOverlayLayers());
      }

      const size = 200;
   
      this.pulsingDot = {
        // _this: this,
        width: size,
        height: size,
        data: new Uint8Array(size * size * 4),

        onAdd: function () {
          const canvas = document.createElement("canvas");
          canvas.width = this.width;
          canvas.height = this.height;
          this.context = canvas.getContext("2d");
          this.startTime = 0;
        },

        start: function () {
          this.startTime = performance.now();
          this.startRender = true;
        },

        stop: function () {
          this.startRender = false;
        },

        render: function () {
          const context = this.context;
          const color = Utils.hexToRgba(
            _this.mapSettings["general_settings"]["flash_color"]
          );

          if (!this.startRender) {
            context.clearRect(0, 0, this.width, this.height);

            this.data = context.getImageData(
              0,
              0,
              this.width,
              this.height
            ).data;

            _this.triggerRepaint();

            return true;
          } else {
            const duration = 600;
            const t =
              ((performance.now() - this.startTime) % duration) / duration;

            const radius = (size / 2) * 0.1 * t; // (size / 2) * 0.3;
            const outerRadius = (size / 2) * 0.7 * t;

            context.clearRect(0, 0, this.width, this.height);
            context.beginPath();
            context.arc(
              this.width / 2,
              this.height / 2,
              outerRadius,
              0,
              Math.PI * 2
            );

            context.strokeStyle = `rgba(${color.slice(5, color.length - 3)}, ${
              1 - t
            })`;

            context.lineWidth = 3;
            context.stroke();

            this.data = context.getImageData(
              0,
              0,
              this.width,
              this.height
            ).data;

            _this.triggerRepaint();

            return true;
          }
        },
      };

      if (!this.hasImage("pulsing-dot")) {
        this.addImage("pulsing-dot", this.pulsingDot, {
          pixelRatio: 1,
        });
      }

      ensureDrawTransformIcons(this);


      this.on('mousemove', (e) => {
        if (this.measurementMode) {
          // Measurement always uses crosshair — handle first and skip map hover cursors.
          this._setCanvasCursor('crosshair');

          if (!this._isDrawStoreReady()) {
            return;
          }
          const measureMode = this._safeDrawGetMode();
          const isMeasureDrawTool = ['draw_line_string','draw_polygon', 'draw_point'].includes(measureMode);
          // Always track the mouse for the coordinate popup — even while gl-draw is
          // briefly in simple_select after sketch→measurement handoff / mode settle.
          if (e) {
            try {
              const cursor = this._resolveEditGeometryCursor(e);
              const cursorMode = isMeasureDrawTool
                ? measureMode
                : (this._measurementOptions?.initialMode || measureMode || 'draw_line_string');
              const payload = {
                ...cursor,
                drawMode: isMeasureDrawTool ? measureMode : cursorMode,
              };
              if (this._editGeometryHostOptions?.onCursorMove) {
                this._editGeometryHostOptions.onCursorMove(payload);
              }
              if (this._measurementOptions?.onCursorMove) {
                this._measurementOptions.onCursorMove({
                  lng: payload.lng,
                  lat: payload.lat,
                  drawMode: payload.drawMode,
                });
              }
            } catch {
              // Fall back to raw lng/lat if snap/cursor resolve throws.
              try {
                const drawMode =
                  isMeasureDrawTool
                    ? measureMode
                    : (this._measurementOptions?.initialMode || 'draw_line_string');
                const raw = {
                  lng: e.lngLat.lng,
                  lat: e.lngLat.lat,
                  drawMode,
                };
                this._editGeometryHostOptions?.onCursorMove?.(raw);
                this._measurementOptions?.onCursorMove?.(raw);
              } catch {
                /* ignore host cursor errors */
              }
            }
          }

          if (isMeasureDrawTool) {
          // Panel length/area updates are throttled; draw.create/update emit immediately.
          this._scheduleMeasurementPanelUpdate();

          const showDraftPopup = this._measurementOptions?.showDrawCursorPopup !== false;

          if (!showDraftPopup) {
            if (!this._measurementDraftPopupsCleared) {
              this._clearMeasurementDraftPopups();
              this._measurementDraftPopupsCleared = true;
            }
          } else if (measureMode === 'draw_polygon'){
            this._measurementDraftPopupsCleared = false;
           
            let innerHTML = 
            `<div style="margin: 5px;" ><span style="position: relative;bottom: 4px;">Total area: <strong class="geobox-gl-draw-value-span">${this._formatMeasurementArea(0)}</strong></span>
             <span style="display:block">Left click to add a point and double-click to end</span></div>`;

            if (!this._currentPopup){
              this._currentPopup = new Popup({ closeOnClick: false, closeButton: false })
              .setLngLat({lng: e.lngLat.lng, lat: e.lngLat.lat})
              .setHTML(<string>innerHTML)
              .addTo(this);
            }
            
            if (this._currentPopup){
              const data = <any> this._safeDrawGetAll();
              if (data.features.length > 0 ) {
                
                const areaM2 = Utils.polygonAreaPostGIS(data.features[data.features.length - 1].geometry.coordinates);
                const measurementText = this._formatMeasurementArea(areaM2);

                innerHTML = 
                  `<div style="margin: 5px;" ><span>Total area: <strong class="geobox-gl-draw-value-span">${measurementText}</strong></span>
                  <span style="display:block">Left click to add a point and double-click to end</span></div>`;
          
              }

              this._currentPopup.setLngLat([e.lngLat.lng, e.lngLat.lat]);
              this._currentPopup.setHTML(innerHTML);

            }
            
          }else if (measureMode === 'draw_line_string'){
            this._measurementDraftPopupsCleared = false;
            let innerHTML = 
            `<div style="margin: 5px;" ><span style="position: relative;bottom: 4px;">Total length: <strong class="geobox-gl-draw-value-span">${this._formatMeasurementLength(0)}</strong></span>
             <span style="display:block">Left click to add a point and double-click to end</span></div>`;

            if (!this._currentPopup){
              this._currentPopup = new Popup({ closeOnClick: false, closeButton: false })
              .setLngLat({lng: e.lngLat.lng, lat: e.lngLat.lat})
              .setHTML(<string>innerHTML)
              .addTo(this);
            }
            
            if (this._currentPopup){
              const data: any = this._safeDrawGetAll();
              if (data.features.length > 0) {

                const lengthM = Utils.lineLengthPostGIS(data.features[data.features.length - 1].geometry.coordinates);
                const measurementText = this._formatMeasurementLength(lengthM);
                
                innerHTML = 
                    `<div style="margin: 5px;" ><span>Total length: <strong class="geobox-gl-draw-value-span">${measurementText}</strong></span>
                    <span style="display:block">Left click to add a point and double-click to end</span></div>`;
              }

              this._currentPopup.setLngLat([e.lngLat.lng, e.lngLat.lat]);
              this._currentPopup.setHTML(innerHTML);

              if (data.features.length > 0) {
                let subPopups = []

                for (let popup of this._currentSubPopups){
                  popup.remove();
                }
                this._currentSubPopups = [];

                const coords = data.features[data.features.length - 1].geometry.coordinates;
          
                for (let i = 0; i < coords.length - 2; i++) {

                  if (i === 0){
                    let segmentLengthHTML = 
                      `<div style="margin: 5px;" ><span>Start point</span></div>`;

                    let subPopup = new Popup({ closeOnClick: false, closeButton: false })
                    .setLngLat({lng: coords[i][0], lat: coords[i][1]})
                    .setHTML(<string>segmentLengthHTML)
                    .addTo(_this);
                    
                    subPopups.push(subPopup)
                  }

                  try {
                    const segment = {
                      type: "Feature",
                      geometry: {
                        type: "LineString",
                        coordinates: [coords[i], coords[i + 1]]
                      }
                    };
                    const segmentLengthM = Utils.lineLengthPostGIS(segment.geometry.coordinates);
                    const segmentLengthText = this._formatMeasurementLength(segmentLengthM);

                    let segmentLengthHTML = 
                      `<div style="margin: 5px;" ><span><strong class="geobox-gl-draw-value-span">${segmentLengthText}</strong></span></div>`;
                    

                    let subPopup = new Popup({ closeOnClick: false, closeButton: false })
                    .setLngLat({lng: coords[i + 1][0], lat: coords[i + 1][1]})
                    .setHTML(<string>segmentLengthHTML)
                    .addTo(_this);
                    
                    subPopups.push(subPopup)
                    
                  } catch (error) {
                    
                  }
                  this._currentSubPopups = subPopups
                }
              }
              // if (data.features.length > 0) {
                
              //   const coords = data.features[data.features.length - 1].geometry.coordinates;
              //   for (let i = 0; i < coords.length - 1; i++) {
              //     try {
              //       const segment = {
              //         type: "Feature",
              //         geometry: {
              //           type: "LineString",
              //           coordinates: [coords[i], coords[i + 1]]
              //         }
              //       };
              //       const segmentLength = tufrLength(segment, { units: 'kilometers' });
              //       console.log(`بخش ${i + 1}: ${segmentLength.toFixed(3)} کیلومتر`);
  
              //       let segmentLengthHTML = 
              //         `<span>Total length: <strong class="geobox-gl-draw-value-span">${segmentLength}</strong> km</span>
              //         <span style="display:block">Left click to add a point and double-click to end</span>`;
              //         console.log(coords[i], coords[i + 1])
  
              //       new Popup({ closeOnClick: false, closeButton: false })
              //       .setLngLat({lng: coords[i + 1][0], lat: coords[i + 1][1]})
              //       .setHTML(<string>segmentLengthHTML)
              //       .addTo(_this);
  
              //     } catch (error) {
                    
              //     }
                  
              //   }
              // }

            }
            
          }

          }

          return;
        }

        this._setCanvasCursor('default');

        if (
          (this.selectByLocationMode || this.drawMode) &&
          this.drawTools &&
          isSketchLikeDrawMode(String(this.drawTools.getMode()))
        ){
          // Keep the normal crosshair — no custom snap cursor icon.
          this._setCanvasCursor('crosshair');
          if (this.drawMode && this._editGeometryHostOptions) {
            this._emitEditGeometryHostUi(e);
          }

        } else if (
          this.drawMode &&
          this.drawTools &&
          ['direct_select', 'simple_select'].includes(this.drawTools.getMode()) &&
          this._editGeometryHostOptions
        ) {
          this._emitEditGeometryHostUi(e);

        }else {

          const bbox: [PointLike, PointLike] = [
            [e.point.x - 2, e.point.y - 2],
            [e.point.x + 2, e.point.y + 2],
          ];

          this._setCanvasCursor('grab');

          let selectedFeatures = this.queryRenderedFeatures(bbox);

          selectedFeatures = selectedFeatures.filter(
            (data) =>
              data.layer.id != "geobox-gl-splash-circle"
              && !isVectorBasemapStyleId(String(data.layer.id ?? '')),
          );

          const clickedPoint = point(
            Utils.lngLatToWebMercator(e.lngLat.lng, e.lngLat.lat)
          );
          const rasterLayers = this.getRasterLayers();
          for (const key in rasterLayers) {
            try {
              if (
                this.getLayer(key) &&
                this.getLayoutProperty(key, 'visibility') !== 'none'
              ) {
                const layer = rasterLayers[key].source;
                let layerExtent;
                if (layer.extent) {
                  layerExtent = layer.extent;
                } else {
                  const source: maplibreSource = <maplibreSource>(
                    (<unknown>this.getSource(layer.source))
                  );
                  if (source) {
                    layerExtent = [
                      ...Utils.lngLatToWebMercator(
                        source.bounds[0],
                        source.bounds[1]
                      ),
                      ...Utils.lngLatToWebMercator(
                        source.bounds[2],
                        source.bounds[3]
                      ),
                    ];
                  } else {
                    continue;
                  }
                }

                const extentPolygon = polygon([
                  [
                    [layerExtent[0], layerExtent[1]],
                    [layerExtent[2], layerExtent[1]],
                    [layerExtent[2], layerExtent[3]],
                    [layerExtent[0], layerExtent[3]],
                    [layerExtent[0], layerExtent[1]],
                  ],
                ]);

                if (booleanPointInPolygon(clickedPoint, extentPolygon)) {
                  this._setCanvasCursor('pointer');
                }
              }
            } catch (error) {}
          }

          const featureLayers = this.getFeatureLayers();
          for (const key in featureLayers) {
            featureLayers[key].clearHighlight();
          }

          const feature = selectedFeatures[0];
          if (!feature) {
            return;
          }

          this._setCanvasCursor('pointer');

          try {
            featureLayers[feature.layer.id].clearHighlight();
            featureLayers[feature.layer.id].highlight(<number>feature.id);
          } catch (error) {}

          try {
            featureLayers[
              feature.layer.id.replace('$extrusion', '')
            ].clearHighlight();
            featureLayers[feature.layer.id.replace('$extrusion', '')].highlight(
              <number>feature.id
            );
          } catch (error) {}

          try {
            featureLayers[
              feature.layer.id.replace('$outline', '')
            ].clearHighlight();
            featureLayers[feature.layer.id.replace('$outline', '')].highlight(
              <number>feature.id
            );
          } catch (error) {}

          try {
            featureLayers[
              feature.layer.id.replace('$symbol', '')
            ].clearHighlight();
            featureLayers[feature.layer.id.replace('$symbol', '')].highlight(
              <number>feature.id
            );
          } catch (error) {}    
        }
      });

      this.on('draw.modechange', (e: { mode?: string }) => {
        const mode = String(e?.mode ?? this.drawTools?.getMode?.() ?? '');
        const snapMarkerModes = new Set([
          'draw_line_string',
          'draw_polygon',
          'draw_point',
          'draw_polygon_freehand',
          'draw_rectangle',
          'draw_circle',
          'passing_mode_point',
          'passing_mode_line_string',
          'passing_mode_polygon',
          'direct_select',
        ]);
        if (!snapMarkerModes.has(mode)) {
          this._clearSnapMarker();
        }
        if (mode === 'direct_select' && (this.drawTools as any)?.options?.snap) {
          this.scheduleUpdateSnapLayers();
        }
        this._syncCursorFromDrawMode();
      });

    });
  }

  addVectorLayer = async (
    vectorLayer: layerSource,
    option?: LayerSpecification
  ): Promise<FeatureLayer> => {
    if (this.loaded()) {
      option = option || <LayerSpecification>{};
      const prefix = vectorLayer.is_view ? 'view' : 'vector';
      vectorLayer.layer_source_type = prefix;
  
      let featureLayer: FeatureLayer;
      switch (vectorLayer.layer_type) {
        case 'Point':
        case 'MultiPoint':
          featureLayer = new PointVectorLayer(vectorLayer, this);
          if (option.paint) {
            if (!(<any>option.paint)['circle-color']) {
              (<any>option.paint)['circle-color'] = `rgb(${Math.floor(
                Math.random() * 256
              )},${Math.floor(Math.random() * 256)},${Math.floor(
                Math.random() * 256
              )})`;
            }
          } else {
            option['paint'] = {};
            (<any>option.paint)['circle-color'] = `rgb(${Math.floor(
              Math.random() * 256
            )},${Math.floor(Math.random() * 256)},${Math.floor(
              Math.random() * 256
            )})`;
          }
          break;
  
        case 'Polyline':
          featureLayer = new LineVectorLayer(vectorLayer, this);
          if (option.paint) {
            if (!(<any>option.paint)['line-color']) {
              (<any>option.paint)['line-color'] = `rgb(${Math.floor(
                Math.random() * 256
              )},${Math.floor(Math.random() * 256)},${Math.floor(
                Math.random() * 256
              )})`;
            }
          } else {
            option['paint'] = {};
            (<any>option.paint)['line-color'] = `rgb(${Math.floor(
              Math.random() * 256
            )},${Math.floor(Math.random() * 256)},${Math.floor(
              Math.random() * 256
            )})`;
          }
          break;
  
        case 'Polygon':
          featureLayer = new PolygonVectorLayer(vectorLayer, this);
          if (option.paint) {
            if (
              !(<any>option.paint)['fill-opacity'] &&
              (<any>option.paint)['fill-opacity'] !== 0
            ) {
              (<any>option.paint)['fill-opacity'] = 0.5;
            }
          } else {
            option['paint'] = {};
            (<any>option.paint)['fill-opacity'] = 0.5;
          }
  
          if (option.paint) {
            if (!(<any>option.paint)['fill-color']) {
              (<any>option.paint)['fill-color'] = `rgb(${Math.floor(
                Math.random() * 256
              )},${Math.floor(Math.random() * 256)},${Math.floor(
                Math.random() * 256
              )})`;
            }
          } else {
            option['paint'] = {};
            (<any>option.paint)['fill-color'] = `rgb(${Math.floor(
              Math.random() * 256
            )},${Math.floor(Math.random() * 256)},${Math.floor(
              Math.random() * 256
            )})`;
          }
          break;
  
        default:
          featureLayer = new FeatureLayer(vectorLayer, this);
          break;
      }
  
      const layerObj: LayerSpecification = <LayerSpecification>(
        featureLayer?.toMaplibreLayer(option)
      );
      const sourceObj: SourceSpecification = <SourceSpecification>(
        await featureLayer?.toMaplibreSource()
      );
      const sourceID = `geobox://${prefix}/${vectorLayer.id}`;
      if (!this.getSource(sourceID)) {
        this.addSource(sourceID, <SourceSpecification>sourceObj);
      }
  
     
      this.originalStyle.layers.push(layerObj)
      this.addLayer(layerObj);
  
      let layerExtent: LngLatBoundsLike = [1, 1, 130, 60];
  
      if (vectorLayer.extent) {
        layerExtent = <LngLatBoundsLike>[
          ...Utils.webMercatorToLngLat(
            vectorLayer.extent[0],
            vectorLayer.extent[1]
          ),
          ...Utils.webMercatorToLngLat(
            vectorLayer.extent[2],
            vectorLayer.extent[3]
          ),
        ];
      }
  
      this.fitBounds(layerExtent);
  
      this.libreMapFeatureLayers[layerObj.id] = <FeatureLayer>featureLayer;

  

      try {
        this.moveLayerOrder('gl-draw-polygon-fill-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-active.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-midpoint.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-active.cold.cold');
        this.moveLayerOrder('gl-draw-line-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-line-active.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-stroke-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-point-point-stroke-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-point-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-point-stroke-active.cold.cold');
        this.moveLayerOrder('gl-draw-point-active.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-static.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-static.cold.cold');
        this.moveLayerOrder('gl-draw-line-static.cold.cold');
        this.moveLayerOrder('gl-draw-point-static.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-active.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-midpoint.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-active.hot.cold');
        this.moveLayerOrder('gl-draw-line-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-line-active.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-stroke-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-point-point-stroke-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-point-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-point-stroke-active.hot.cold');
        this.moveLayerOrder('gl-draw-point-active.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-static.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-static.hot.cold');
        this.moveLayerOrder('gl-draw-line-static.hot.cold');
        this.moveLayerOrder('gl-draw-point-static.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-scale-icon.cold');
        this.moveLayerOrder('gl-draw-bbox-frame-stroke.cold');
        this.moveLayerOrder('gl-draw-bbox-corner-halo.cold');
        this.moveLayerOrder('gl-draw-bbox-corner.cold');
        this.moveLayerOrder('gl-draw-line-rotate-point.cold');
        this.moveLayerOrder('gl-draw-polygon-rotate-point-stroke.cold');
        this.moveLayerOrder('gl-draw-polygon-rotate-point.cold');
        this.moveLayerOrder('gl-draw-polygon-rotate-point-icon.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-fill-active.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-midpoint.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-active.cold.hot');
        this.moveLayerOrder('gl-draw-line-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-line-active.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-stroke-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-point-point-stroke-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-point-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-point-stroke-active.cold.hot');
        this.moveLayerOrder('gl-draw-point-active.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-fill-static.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-static.cold.hot');
        this.moveLayerOrder('gl-draw-line-static.cold.hot');
        this.moveLayerOrder('gl-draw-point-static.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-fill-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-fill-active.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-midpoint.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-active.hot.hot');
        this.moveLayerOrder('gl-draw-line-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-line-active.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-stroke-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-point-point-stroke-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-point-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-point-stroke-active.hot.hot');
        this.moveLayerOrder('gl-draw-point-active.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-fill-static.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-static.hot.hot');
        this.moveLayerOrder('gl-draw-line-static.hot.hot');
        this.moveLayerOrder('gl-draw-point-static.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-scale-icon.hot');
        this.moveLayerOrder('gl-draw-bbox-frame-stroke.hot');
        this.moveLayerOrder('gl-draw-bbox-corner-halo.hot');
        this.moveLayerOrder('gl-draw-bbox-corner.hot');
        this.moveLayerOrder('gl-draw-line-rotate-point.hot');
        this.moveLayerOrder('gl-draw-polygon-rotate-point-stroke.hot');
        this.moveLayerOrder('gl-draw-polygon-rotate-point.hot');
        this.moveLayerOrder('gl-draw-polygon-rotate-point-icon.hot');
      } catch (error) {
        
      }

      this.moveLayerOrder('geobox-gl-splash-circle');
      
      return featureLayer;
    }
    
    return new Promise((resolve) => {
      setTimeout(async () => {
        const result = await this.addVectorLayer(vectorLayer, option);
        resolve(result);
      }, 100);
    });
    
  
  };
  
  
  addRasterLayer = async (
    rasterLayer: layerSource,
    option?: LayerSpecification
  ): Promise<RasterLayer> => {
    if (this.loaded()) {
      option = option || <LayerSpecification>{};

      const prefix = option.type || 'raster';
      rasterLayer.layer_source_type = 'raster';
      rasterLayer.layer_type = prefix;
     
      const featureLayer = new RasterLayer(rasterLayer, this);
      const layerObj = featureLayer.toMaplibreLayer(option);
      
      const sourceObj = await featureLayer.toMaplibreSource();
   
      const sourceID = `geobox://${prefix}/${rasterLayer.id}`;
      if (!this.getSource(sourceID)) {
        this.addSource(sourceID, sourceObj);
      }

      this.addLayer(layerObj);

      let layerExtent: LngLatBoundsLike = [1, 1, 130, 60];

      if (rasterLayer.extent) {
        layerExtent = <LngLatBoundsLike>[
          ...Utils.webMercatorToLngLat(
            rasterLayer.extent[0],
            rasterLayer.extent[1]
          ),
          ...Utils.webMercatorToLngLat(
            rasterLayer.extent[2],
            rasterLayer.extent[3]
          ),
        ];
      }

      this.fitBounds(layerExtent);

      this.libreMapRasterLayers[layerObj.id] = featureLayer;

      // JsLoadingOverlay.hide();
      try {
        this.moveLayerOrder('gl-draw-polygon-fill-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-active.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-midpoint.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-active.cold.cold');
        this.moveLayerOrder('gl-draw-line-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-line-active.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-stroke-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-point-point-stroke-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-point-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-point-stroke-active.cold.cold');
        this.moveLayerOrder('gl-draw-point-active.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-static.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-static.cold.cold');
        this.moveLayerOrder('gl-draw-line-static.cold.cold');
        this.moveLayerOrder('gl-draw-point-static.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-active.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-midpoint.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-active.hot.cold');
        this.moveLayerOrder('gl-draw-line-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-line-active.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-stroke-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-point-point-stroke-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-point-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-point-stroke-active.hot.cold');
        this.moveLayerOrder('gl-draw-point-active.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-static.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-static.hot.cold');
        this.moveLayerOrder('gl-draw-line-static.hot.cold');
        this.moveLayerOrder('gl-draw-point-static.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-scale-icon.cold');
        this.moveLayerOrder('gl-draw-bbox-frame-stroke.cold');
        this.moveLayerOrder('gl-draw-bbox-corner-halo.cold');
        this.moveLayerOrder('gl-draw-bbox-corner.cold');
        this.moveLayerOrder('gl-draw-line-rotate-point.cold');
        this.moveLayerOrder('gl-draw-polygon-rotate-point-stroke.cold');
        this.moveLayerOrder('gl-draw-polygon-rotate-point.cold');
        this.moveLayerOrder('gl-draw-polygon-rotate-point-icon.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-fill-active.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-midpoint.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-active.cold.hot');
        this.moveLayerOrder('gl-draw-line-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-line-active.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-stroke-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-point-point-stroke-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-point-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-point-stroke-active.cold.hot');
        this.moveLayerOrder('gl-draw-point-active.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-fill-static.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-static.cold.hot');
        this.moveLayerOrder('gl-draw-line-static.cold.hot');
        this.moveLayerOrder('gl-draw-point-static.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-fill-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-fill-active.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-midpoint.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-active.hot.hot');
        this.moveLayerOrder('gl-draw-line-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-line-active.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-stroke-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-point-point-stroke-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-point-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-point-stroke-active.hot.hot');
        this.moveLayerOrder('gl-draw-point-active.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-fill-static.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-static.hot.hot');
        this.moveLayerOrder('gl-draw-line-static.hot.hot');
        this.moveLayerOrder('gl-draw-point-static.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-scale-icon.hot');
        this.moveLayerOrder('gl-draw-bbox-frame-stroke.hot');
        this.moveLayerOrder('gl-draw-bbox-corner-halo.hot');
        this.moveLayerOrder('gl-draw-bbox-corner.hot');
        this.moveLayerOrder('gl-draw-line-rotate-point.hot');
        this.moveLayerOrder('gl-draw-polygon-rotate-point-stroke.hot');
        this.moveLayerOrder('gl-draw-polygon-rotate-point.hot');
        this.moveLayerOrder('gl-draw-polygon-rotate-point-icon.hot');
      } catch (error) {
        
      }
      this.moveLayerOrder('geobox-gl-splash-circle');
      return featureLayer;
    }
    return new Promise((resolve) => {
      setTimeout(async () => {
        const result = await  this.addRasterLayer(rasterLayer, option);
        resolve(result);
      }, 100);
    });
    
  };

  addMosaicLayer = async (
    mosaicLayer: layerSource,
    option?: LayerSpecification
  ): Promise<RasterLayer> => {
    if (this.loaded()) {
      option = option || <LayerSpecification>{};
      const prefix = option.type || 'mosaic';
      mosaicLayer.layer_source_type = 'mosaic';
      mosaicLayer.layer_type = prefix;
      const featureLayer = new RasterLayer(mosaicLayer, this);
      const layerObj = featureLayer.toMaplibreLayer(option);
      const sourceObj = await featureLayer.toMaplibreSource();
      const sourceID = `geobox://${prefix}/${mosaicLayer.id}`;
 
      if (!this.getSource(sourceID)) {
        this.addSource(sourceID, sourceObj);
      }
  
      this.addLayer(layerObj);

      let layerExtent: LngLatBoundsLike = [1, 1, 130, 60];

      if (mosaicLayer.extent) {
        layerExtent = <LngLatBoundsLike>[
          ...Utils.webMercatorToLngLat(
            mosaicLayer.extent[0],
            mosaicLayer.extent[1]
          ),
          ...Utils.webMercatorToLngLat(
            mosaicLayer.extent[2],
            mosaicLayer.extent[3]
          ),
        ];
      }

      this.fitBounds(layerExtent);

      let visibility = 'visible';
      if (layerObj.layout && layerObj.layout.visibility) {
        visibility = layerObj.layout.visibility || 'visible';
      }

      this.libreMapRasterLayers[layerObj.id] = featureLayer;

      // JsLoadingOverlay.hide();
      try {
        this.moveLayerOrder('gl-draw-polygon-fill-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-active.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-midpoint.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-active.cold.cold');
        this.moveLayerOrder('gl-draw-line-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-line-active.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-stroke-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-point-point-stroke-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-point-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-point-stroke-active.cold.cold');
        this.moveLayerOrder('gl-draw-point-active.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-static.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-static.cold.cold');
        this.moveLayerOrder('gl-draw-line-static.cold.cold');
        this.moveLayerOrder('gl-draw-point-static.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-active.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-midpoint.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-active.hot.cold');
        this.moveLayerOrder('gl-draw-line-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-line-active.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-stroke-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-point-point-stroke-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-point-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-point-stroke-active.hot.cold');
        this.moveLayerOrder('gl-draw-point-active.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-static.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-static.hot.cold');
        this.moveLayerOrder('gl-draw-line-static.hot.cold');
        this.moveLayerOrder('gl-draw-point-static.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-scale-icon.cold');
        this.moveLayerOrder('gl-draw-bbox-frame-stroke.cold');
        this.moveLayerOrder('gl-draw-bbox-corner-halo.cold');
        this.moveLayerOrder('gl-draw-bbox-corner.cold');
        this.moveLayerOrder('gl-draw-line-rotate-point.cold');
        this.moveLayerOrder('gl-draw-polygon-rotate-point-stroke.cold');
        this.moveLayerOrder('gl-draw-polygon-rotate-point.cold');
        this.moveLayerOrder('gl-draw-polygon-rotate-point-icon.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-fill-active.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-midpoint.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-active.cold.hot');
        this.moveLayerOrder('gl-draw-line-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-line-active.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-stroke-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-point-point-stroke-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-point-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-point-stroke-active.cold.hot');
        this.moveLayerOrder('gl-draw-point-active.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-fill-static.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-static.cold.hot');
        this.moveLayerOrder('gl-draw-line-static.cold.hot');
        this.moveLayerOrder('gl-draw-point-static.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-fill-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-fill-active.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-midpoint.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-active.hot.hot');
        this.moveLayerOrder('gl-draw-line-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-line-active.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-stroke-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-point-point-stroke-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-point-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-point-stroke-active.hot.hot');
        this.moveLayerOrder('gl-draw-point-active.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-fill-static.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-static.hot.hot');
        this.moveLayerOrder('gl-draw-line-static.hot.hot');
        this.moveLayerOrder('gl-draw-point-static.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-scale-icon.hot');
        this.moveLayerOrder('gl-draw-bbox-frame-stroke.hot');
        this.moveLayerOrder('gl-draw-bbox-corner-halo.hot');
        this.moveLayerOrder('gl-draw-bbox-corner.hot');
        this.moveLayerOrder('gl-draw-line-rotate-point.hot');
        this.moveLayerOrder('gl-draw-polygon-rotate-point-stroke.hot');
        this.moveLayerOrder('gl-draw-polygon-rotate-point.hot');
        this.moveLayerOrder('gl-draw-polygon-rotate-point-icon.hot');
      } catch (error) {
        
      }
      this.moveLayerOrder('geobox-gl-splash-circle');
      return featureLayer;
    }

    return new Promise((resolve) => {
      setTimeout(async () => {
        const result = await this.addMosaicLayer(mosaicLayer, option);
        resolve(result);
      }, 100);
    });

  };

  addForeignLayer = async (options: {
    name: string;
    source: SourceSpecification;
    layerType: 'raster' | 'fill' | 'line' | 'circle' | 'symbol' | 'hillshade';
    sourceLayer?: string;
    id?: string;
    paint?: LayerSpecification['paint'];
    layout?: LayerSpecification['layout'];
  }): Promise<FeatureLayer | RasterLayer> => {
    const run = async (): Promise<FeatureLayer | RasterLayer> => {
      const id = options.id || createPlacedModelId();
      const title = (options.name || 'foreign').trim() || 'foreign';
      const sourceId = `foreign://${id}`;

      let layerId = `foreign/${title}`;
      const styleLayers = this.getStyle()?.layers ?? [];
      const existingIds = styleLayers.map((layer) => layer.id);
      let repeat = 0;
      while (
        existingIds.includes(repeat === 0 ? layerId : `${layerId}#${repeat}`) ||
        this.getLayer(repeat === 0 ? layerId : `${layerId}#${repeat}`)
      ) {
        repeat += 1;
      }
      if (repeat > 0) layerId = `${layerId}#${repeat}`;

      const stub = {
        id,
        uuid: id,
        name: title,
        display_name: title,
        layer_type: 'foreign',
        layer_source_type: 'foreign',
        extent: undefined,
        is_view: false,
        _endpoint: '',
        has_z: false,
        source: sourceId,
        getFields: async () => [],
        getSettings: async () => ({}),
        getTilejson: async () => ({}),
        getFeatures: async () => ({ type: 'FeatureCollection', features: [] }),
        getLayers: async () => [],
        updateFeature: async () => undefined,
        updateFeatureGeometry: async () => undefined,
        createFeature: async () => undefined,
      } as unknown as layerSource;

      if (!this.getSource(sourceId)) {
        this.addSource(sourceId, options.source);
      }

      const isRasterLike =
        options.layerType === 'raster' || options.layerType === 'hillshade';
      const needsSourceLayer =
        !isRasterLike &&
        options.source.type === 'vector' &&
        Boolean(options.sourceLayer);

      const randomRgb = () =>
        `rgb(${Math.floor(Math.random() * 256)},${Math.floor(
          Math.random() * 256
        )},${Math.floor(Math.random() * 256)})`;

      let paint: LayerSpecification['paint'] = options.paint || {};
      if (!options.paint) {
        switch (options.layerType) {
          case 'circle':
            paint = { 'circle-color': randomRgb(), 'circle-radius': 5 };
            break;
          case 'line':
            paint = { 'line-color': randomRgb(), 'line-width': 2 };
            break;
          case 'fill':
            paint = { 'fill-color': randomRgb(), 'fill-opacity': 0.5 };
            break;
          default:
            paint = {};
        }
      }

      const layerObj = {
        id: layerId,
        type: options.layerType,
        source: sourceId,
        ...(needsSourceLayer ? { 'source-layer': options.sourceLayer } : {}),
        layout: options.layout || {},
        paint,
      } as LayerSpecification;

      if (!this.originalStyle) {
        this.originalStyle = { version: 8, sources: {}, layers: [] };
      }
      if (!this.originalStyle.sources) this.originalStyle.sources = {};
      if (!this.originalStyle.layers) this.originalStyle.layers = [];
      this.originalStyle.sources[sourceId] = options.source;
      this.originalStyle.layers.push(layerObj);

      this.addLayer(layerObj);

      if (isRasterLike) {
        const rasterLayer = new RasterLayer(
          stub,
          this,
          undefined,
          layerId,
          sourceId
        );
        this.libreMapRasterLayers[layerId] = rasterLayer;
        try {
          this.moveLayerOrder('geobox-gl-splash-circle');
        } catch {
          /* ignore */
        }
        return rasterLayer;
      }

      const featureLayer = new FeatureLayer(
        stub,
        this,
        options.sourceLayer,
        layerId,
        sourceId
      );
      // GeoJSON / foreign sources have no server fields.
      featureLayer.allFields = [];
      featureLayer.publishedFields = [];
      this.libreMapFeatureLayers[layerId] = featureLayer;
      try {
        this.moveLayerOrder('geobox-gl-splash-circle');
      } catch {
        /* ignore */
      }
      return featureLayer;
    };

    if (this.loaded()) {
      return run();
    }

    return new Promise((resolve) => {
      setTimeout(async () => {
        resolve(await this.addForeignLayer(options));
      }, 100);
    });
  };

  addTileset = async (
    tileset: layerSource,
    sourceLayers?: Array<string>,
    option?: LayerSpecification // option works if only sourceLayers.length === 1
  ): Promise<FeatureLayer[]> => {
    if (this.loaded()) {
      sourceLayers = sourceLayers || [];
      option = option || <LayerSpecification>{};

      let layerOpt = <LayerSpecification>{}

      let prefix = 'tileset';

      tileset.layer_type = prefix;

      let featureLayer, tilesetLayersList= [] ;

      switch (tileset.layer_type) {
        case 'Point':
        case 'MultiPoint':
          featureLayer = new PointVectorLayer(tileset, this);

          break;
        case 'Polyline':
          featureLayer = new LineVectorLayer(tileset, this);

          break;
        case 'Polygon':
          featureLayer = new PolygonVectorLayer(tileset, this);
          break;
        default:
          featureLayer = new FeatureLayer(tileset, this);
          break;
      }

      const sourceObj = <SourceSpecification>(
        await featureLayer?.toMaplibreSource()
      );

      const sourceID = `geobox://${prefix}/${tileset.id}`;

      if (!this.getSource(sourceID)) {
        this.addSource(sourceID, sourceObj);
      }
      const tilesetLayers = await tileset.getLayers();
      
      let lastTilesetLayer;
      for (const tilesetLayer of tilesetLayers) {

        // let geoboxLayer = tilesetLayer['geobox-layer'];
            
        // const geoboxSource = tilesetLayer['geobox-source'];

        // if (!geoboxSource && layer.source.slice(0, 9) === 'geobox://') {
          
        //   if (layer.type !== 'raster' || layer.type !== 'hillshade') {
        //     geoboxLayer = {};
        //     geoboxLayer.layer_source_type = layer.type;
        //     geoboxLayer.layer_type = 'foreign';
        //   }
        // }

        
        if (
          sourceLayers.length > 0 &&
          sourceLayers.includes(tilesetLayer.name)
        ) {
          if (sourceLayers.length === 1){
            layerOpt = option;
          }else {
            layerOpt = <LayerSpecification>{}
          }

          prefix = tilesetLayer.is_view ? 'view' : 'vector';
          const sourceLayerID = `${prefix}/${tilesetLayer.name}`;
          tilesetLayer.layer_source_type = prefix;

          switch (tilesetLayer.layer_type) {
            case 'Point':
            case 'MultiPoint':
              featureLayer = new PointVectorLayer(
                tilesetLayer,
                this,
                sourceLayerID,
                sourceLayerID,
                sourceID
              );
              if (layerOpt.paint) {
                if (!(<any>layerOpt.paint)['circle-color']) {
                  (<any>layerOpt.paint)['circle-color'] = `rgb(${Math.floor(
                    Math.random() * 256
                  )},${Math.floor(Math.random() * 256)},${Math.floor(
                    Math.random() * 256
                  )})`;
                }
              } else {
                layerOpt['paint'] = {};
                (<any>layerOpt.paint)['circle-color'] = `rgb(${Math.floor(
                  Math.random() * 256
                )},${Math.floor(Math.random() * 256)},${Math.floor(
                  Math.random() * 256
                )})`;
              }
              break;
            case 'Polyline':
              featureLayer = new LineVectorLayer(
                tilesetLayer,
                this,
                sourceLayerID,
                sourceLayerID,
                sourceID
              );

              if (layerOpt.paint) {
                if (!(<any>layerOpt.paint)['line-color']) {
                  (<any>layerOpt.paint)['line-color'] = `rgb(${Math.floor(
                    Math.random() * 256
                  )},${Math.floor(Math.random() * 256)},${Math.floor(
                    Math.random() * 256
                  )})`;
                }
              } else {
                layerOpt['paint'] = {};
                (<any>layerOpt.paint)['line-color'] = `rgb(${Math.floor(
                  Math.random() * 256
                )},${Math.floor(Math.random() * 256)},${Math.floor(
                  Math.random() * 256
                )})`;
              }
              break;
            case 'Polygon':
              featureLayer = new PolygonVectorLayer(
                tilesetLayer,
                this,
                sourceLayerID,
                sourceLayerID,
                sourceID
              );

              if (layerOpt.paint) {
                if (
                  !(<any>layerOpt.paint)['fill-opacity'] &&
                  (<any>layerOpt.paint)['fill-opacity'] !== 0
                ) {
                  (<any>layerOpt.paint)['fill-opacity'] = 0.5;
                }
              } else {
                layerOpt['paint'] = {};
                (<any>layerOpt.paint)['fill-opacity'] = 0.5;
              }
      
              if (layerOpt.paint) {
                if (!(<any>layerOpt.paint)['fill-color']) {
                  (<any>layerOpt.paint)['fill-color'] = `rgb(${Math.floor(
                    Math.random() * 256
                  )},${Math.floor(Math.random() * 256)},${Math.floor(
                    Math.random() * 256
                  )})`;
                }
              } else {
                layerOpt['paint'] = {};
                (<any>layerOpt.paint)['fill-color'] = `rgb(${Math.floor(
                  Math.random() * 256
                )},${Math.floor(Math.random() * 256)},${Math.floor(
                  Math.random() * 256
                )})`;
              }
              break;
            default:
              featureLayer = new FeatureLayer(tilesetLayer, this);
              break;
          }

          const layerObj = <layerObj>featureLayer?.toMaplibreLayer(layerOpt);
          this.addLayer(layerObj);
          this.originalStyle.layers.push(layerObj)
          let visibility = 'visible';
          if (layerObj.layout && layerObj.layout.visibility) {
            visibility = layerObj.layout.visibility || 'visible';
          }

          this.libreMapFeatureLayers[layerObj.id] = <FeatureLayer>featureLayer;
          tilesetLayersList.push(featureLayer);
          lastTilesetLayer = tilesetLayer;
   
        } else if (sourceLayers.length === 0) {
        
          layerOpt = <LayerSpecification>{}
          prefix = tilesetLayer.is_view ? 'view' : 'vector';
          const sourceLayerID = `${prefix}/${tilesetLayer.name}`;
          tilesetLayer.layer_source_type = prefix;

          switch (tilesetLayer.layer_type) {
            case 'Point':
            case 'MultiPoint':
              featureLayer = new PointVectorLayer(
                tilesetLayer,
                this,
                sourceLayerID,
                sourceLayerID,
                sourceID
              );

              if (layerOpt.paint) {
                if (!(<any>layerOpt.paint)['circle-color']) {
                  (<any>layerOpt.paint)['circle-color'] = `rgb(${Math.floor(
                    Math.random() * 256
                  )},${Math.floor(Math.random() * 256)},${Math.floor(
                    Math.random() * 256
                  )})`;
                }
              } else {
                layerOpt['paint'] = {};
                (<any>layerOpt.paint)['circle-color'] = `rgb(${Math.floor(
                  Math.random() * 256
                )},${Math.floor(Math.random() * 256)},${Math.floor(
                  Math.random() * 256
                )})`;
              }
              break;
            case 'Polyline':
              featureLayer = new LineVectorLayer(
                tilesetLayer,
                this,
                sourceLayerID,
                sourceLayerID,
                sourceID
              );

              if (layerOpt.paint) {
                if (!(<any>layerOpt.paint)['line-color']) {
                  (<any>layerOpt.paint)['line-color'] = `rgb(${Math.floor(
                    Math.random() * 256
                  )},${Math.floor(Math.random() * 256)},${Math.floor(
                    Math.random() * 256
                  )})`;
                }
              } else {
                layerOpt['paint'] = {};
                (<any>layerOpt.paint)['line-color'] = `rgb(${Math.floor(
                  Math.random() * 256
                )},${Math.floor(Math.random() * 256)},${Math.floor(
                  Math.random() * 256
                )})`;
              }
              break;
            case 'Polygon':
              featureLayer = new PolygonVectorLayer(
                tilesetLayer,
                this,
                sourceLayerID,
                sourceLayerID,
                sourceID
              );

              if (layerOpt.paint) {
                if (
                  !(<any>layerOpt.paint)['fill-opacity'] &&
                  (<any>layerOpt.paint)['fill-opacity'] !== 0
                ) {
                  (<any>layerOpt.paint)['fill-opacity'] = 0.5;
                }
              } else {
                layerOpt['paint'] = {};
                (<any>layerOpt.paint)['fill-opacity'] = 0.5;
              }
      
              if (layerOpt.paint) {
                if (!(<any>layerOpt.paint)['fill-color']) {
                  (<any>layerOpt.paint)['fill-color'] = `rgb(${Math.floor(
                    Math.random() * 256
                  )},${Math.floor(Math.random() * 256)},${Math.floor(
                    Math.random() * 256
                  )})`;
                }
              } else {
                layerOpt['paint'] = {};
                (<any>layerOpt.paint)['fill-color'] = `rgb(${Math.floor(
                  Math.random() * 256
                )},${Math.floor(Math.random() * 256)},${Math.floor(
                  Math.random() * 256
                )})`;
              }
              break;
            default:
              featureLayer = new FeatureLayer(tilesetLayer, this);
              break;
          }
      
          const layerObj = <layerObj>featureLayer?.toMaplibreLayer(layerOpt);
          
          this.addLayer(layerObj);
          this.originalStyle.layers.push(layerObj);

          this.libreMapFeatureLayers[layerObj.id] = featureLayer;
          tilesetLayersList.push(featureLayer);
          lastTilesetLayer = tilesetLayer;

        }
      }

      let layerExtent: LngLatBoundsLike = [1, 1, 130, 60];

      if (lastTilesetLayer && lastTilesetLayer.extent) {
        layerExtent = <LngLatBoundsLike>[
          ...Utils.webMercatorToLngLat(
            lastTilesetLayer.extent[0],
            lastTilesetLayer.extent[1]
          ),
          ...Utils.webMercatorToLngLat(
            lastTilesetLayer.extent[2],
            lastTilesetLayer.extent[3]
          ),
        ];
      }

      this.fitBounds(layerExtent);

      // JsLoadingOverlay.hide();
      try {
        this.moveLayerOrder('gl-draw-polygon-fill-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-active.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-midpoint.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-active.cold.cold');
        this.moveLayerOrder('gl-draw-line-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-line-active.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-stroke-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-point-point-stroke-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-point-inactive.cold.cold');
        this.moveLayerOrder('gl-draw-point-stroke-active.cold.cold');
        this.moveLayerOrder('gl-draw-point-active.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-static.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-static.cold.cold');
        this.moveLayerOrder('gl-draw-line-static.cold.cold');
        this.moveLayerOrder('gl-draw-point-static.cold.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-active.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-midpoint.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-active.hot.cold');
        this.moveLayerOrder('gl-draw-line-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-line-active.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-stroke-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-point-point-stroke-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-point-inactive.hot.cold');
        this.moveLayerOrder('gl-draw-point-stroke-active.hot.cold');
        this.moveLayerOrder('gl-draw-point-active.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-static.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-stroke-static.hot.cold');
        this.moveLayerOrder('gl-draw-line-static.hot.cold');
        this.moveLayerOrder('gl-draw-point-static.hot.cold');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-scale-icon.cold');
        this.moveLayerOrder('gl-draw-bbox-frame-stroke.cold');
        this.moveLayerOrder('gl-draw-bbox-corner-halo.cold');
        this.moveLayerOrder('gl-draw-bbox-corner.cold');
        this.moveLayerOrder('gl-draw-line-rotate-point.cold');
        this.moveLayerOrder('gl-draw-polygon-rotate-point-stroke.cold');
        this.moveLayerOrder('gl-draw-polygon-rotate-point.cold');
        this.moveLayerOrder('gl-draw-polygon-rotate-point-icon.cold');
        this.moveLayerOrder('gl-draw-polygon-fill-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-fill-active.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-midpoint.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-active.cold.hot');
        this.moveLayerOrder('gl-draw-line-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-line-active.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-stroke-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-point-point-stroke-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-point-inactive.cold.hot');
        this.moveLayerOrder('gl-draw-point-stroke-active.cold.hot');
        this.moveLayerOrder('gl-draw-point-active.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-fill-static.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-static.cold.hot');
        this.moveLayerOrder('gl-draw-line-static.cold.hot');
        this.moveLayerOrder('gl-draw-point-static.cold.hot');
        this.moveLayerOrder('gl-draw-polygon-fill-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-fill-active.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-midpoint.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-active.hot.hot');
        this.moveLayerOrder('gl-draw-line-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-line-active.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-stroke-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-point-point-stroke-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-point-inactive.hot.hot');
        this.moveLayerOrder('gl-draw-point-stroke-active.hot.hot');
        this.moveLayerOrder('gl-draw-point-active.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-fill-static.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-stroke-static.hot.hot');
        this.moveLayerOrder('gl-draw-line-static.hot.hot');
        this.moveLayerOrder('gl-draw-point-static.hot.hot');
        this.moveLayerOrder('gl-draw-polygon-and-line-vertex-scale-icon.hot');
        this.moveLayerOrder('gl-draw-bbox-frame-stroke.hot');
        this.moveLayerOrder('gl-draw-bbox-corner-halo.hot');
        this.moveLayerOrder('gl-draw-bbox-corner.hot');
        this.moveLayerOrder('gl-draw-line-rotate-point.hot');
        this.moveLayerOrder('gl-draw-polygon-rotate-point-stroke.hot');
        this.moveLayerOrder('gl-draw-polygon-rotate-point.hot');
        this.moveLayerOrder('gl-draw-polygon-rotate-point-icon.hot');
      } catch (error) {
        
      }
      this.moveLayerOrder('geobox-gl-splash-circle');
      return tilesetLayersList;
    }
    return new Promise((resolve) => {
      setTimeout(async () => {
        const result = await this.addTileset(tileset, sourceLayers, option);
        resolve(result);
      }, 100);
    });

  };

  addBackground = (color?: string) => {
    if (this.loaded()) {
      color = color || 'black';
      let style = this.getStyle();
      if (style.layers){
        for (const layer of style.layers) {
          if (layer.type === 'background'){
            this.removeLayer(layer.id);
          }
        }
      }
      const layerObj: LayerSpecification = {
        id: 'geobox-gl-background',
        type: 'background',
        paint: {
          'background-color': color,
        },
      };

      const sourceObj: SourceSpecification = {
        type: 'vector',
        tiles: [''],
        minzoom: 0,
        maxzoom: 14,
      };

      const sourceID = 'geobox://geobox-gl-background';

      this._clearVectorBaseMapLayers();
      this._removeLayerAndSource(BUILTIN_BASEMAP_ID);
      this._removeLayerAndSource(CUSTOM_BASEMAP_ID);
      this._stripCustomBaseMapFromOriginalStyle();

      if (!this.getSource(sourceID)) {
        this.addSource(sourceID, sourceObj);
      }

      if (this.getLayer('geobox-gl-background')) {
        this.removeLayer('geobox-gl-background');
      }

      this.addLayer(layerObj);

      this.moveLayer('geobox-gl-background', this.getStyle().layers[0].id);
    }else {
      setTimeout(async () => {
        this.addBackground(color);
      }, 100);
    }
    
  };

  changeBackgroundColor = (color: string) => {
    if (this.loaded()) {
      if (this.getLayer('geobox-gl-background')) {
        this.setPaintProperty(
          'geobox-gl-background',
          'background-color',
          color
        );
      }
    } else {
      setTimeout(() => {
        this.changeBackgroundColor(color);
      }, 100);
    }
  };

  _normalizeCustomBasemaps = (value: unknown): customBasemap[] => {
    let parsed = value;
    if (typeof parsed === 'string' && parsed.trim()) {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return [];
      }
    }
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
      .map((item) => ({
        url: typeof item.url === 'string' ? item.url : '',
        image: typeof item.image === 'string' ? item.image : '',
        title: typeof item.title === 'string' ? item.title : '',
      }))
      .filter((item) => item.url && item.title);
  };

  getCustomBasemaps = (): customBasemap[] => {
    return this._normalizeCustomBasemaps(this.mapSettings?.general_settings?.custom_basemaps);
  };

  findCustomBasemap = (name: string): customBasemap | undefined => {
    return this.getCustomBasemaps().find(
      (item) => item.title === name || item.url === name,
    );
  };

  setCustomBasemaps = (list: unknown) => {
    const custom_basemaps = this._normalizeCustomBasemaps(list);
    this.mapSettings = {
      ...this.mapSettings,
      general_settings: {
        ...this.mapSettings.general_settings,
        custom_basemaps,
      },
    };
  };

  addCustomBasemap = (item: customBasemap) => {
    this.setCustomBasemaps([...this.getCustomBasemaps(), item]);
  };

  removeCustomBasemap = (titleOrIndex: string | number) => {
    const list = this.getCustomBasemaps();
    const next =
      typeof titleOrIndex === 'number'
        ? list.filter((_, index) => index !== titleOrIndex)
        : list.filter((item) => item.title !== titleOrIndex && item.url !== titleOrIndex);
    this.setCustomBasemaps(next);
  };

  private _authorizeMapRequest = (url: string): { url: string; headers?: Record<string, string> } => {
    const server = this.server;
    if (url.indexOf('apikey=') !== -1) {
      return { url };
    }
    if (server?._apikey && url.indexOf('apikey') === -1) {
      if (url.indexOf('?') > -1) {
        return { url: `${url}&apikey=${server._apikey}` };
      }
      return { url: `${url}?apikey=${server._apikey}` };
    }
    if (server?._access_token) {
      return {
        url,
        headers: { Authorization: `Bearer ${server._access_token}` },
      };
    }
    return { url };
  };

  private _removeInaccessibleMapSource = (sourceId: string) => {
    if (!sourceId) return;
    try {
      const layers = [...(this.getStyle()?.layers ?? [])];
      for (const layer of layers) {
        if ((layer as { source?: string }).source !== sourceId) continue;
        const layerId = layer.id;
        try {
          if (this.getLayer(layerId)) this.removeLayer(layerId);
        } catch {
          /* ignore */
        }
        delete this.libreMapFeatureLayers?.[layerId];
        delete this.libreMapRasterLayers?.[layerId];
      }
      if (this.getSource(sourceId)) {
        this.removeSource(sourceId);
      }
      console.warn(`[geobox-gl] Ignored inaccessible map source: ${sourceId}`);
    } catch (error) {
      console.warn(`[geobox-gl] Failed to remove inaccessible source ${sourceId}`, error);
    }
  };

  _removeLayerAndSource = (id: string) => {
    try {
      if (this.getLayer(id)) this.removeLayer(id);
      if (this.getSource(id)) this.removeSource(id);
    } catch (error) {}
  };

  _removeBackgroundLayers = () => {
    const ids = (this.getStyle()?.layers ?? [])
      .filter((layer) => layer.type === 'background')
      .map((layer) => layer.id);
    for (const id of ids) {
      try {
        this.removeLayer(id);
      } catch (error) {}
    }
  };

  _finalizeBasemapLayers = () => {
    this._pinRasterBaseMap();
    this._pinVectorBaseMap();
    this.models3d.repositionAboveBasemap();
  };

  _applyBasemapFromSettings = async (): Promise<void> => {
    const baseMapName = this.mapSettings?.general_settings?.base_map;
    if (!baseMapName || baseMapName === 'blank') return;

    // Wait until the current style can accept sources/layers.
    for (let attempt = 0; attempt < 40; attempt++) {
      const styleReady =
        this.loaded()
        && (typeof this.isStyleLoaded !== 'function' || this.isStyleLoaded());
      if (styleReady) break;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    const vectorUuid = parseVectorMapBasemapUuid(baseMapName);
    if (vectorUuid) {
      try {
        await this.addVectorBaseMap(vectorUuid);
      } catch {
        /* ignore unavailable vector basemap maps */
      }
      return;
    }

    const custom = this.findCustomBasemap(String(baseMapName));
    if (custom?.url) {
      this.addCustomBaseMap(custom.url);
      return;
    }

    try {
      await this.addBaseMap(baseMapName as baseMapTypes);
    } catch {
      /* ignore unavailable basemap ids */
    }
  };

  _waitForStyleReady = (timeoutMs = 4000): Promise<void> => {
    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };

      if (
        this.loaded()
        && (typeof this.isStyleLoaded !== 'function' || this.isStyleLoaded())
      ) {
        finish();
        return;
      }

      this.once('style.load', finish);
      this.once('idle', finish);
      setTimeout(finish, timeoutMs);
    });
  };

  _applySavedMapCamera = (serverMap: geoboxMap): void => {
    const view = this.mapSettings?.['view_settings'];
    if (view?.center && view?.zoom != null) {
      this.jumpTo({
        center: <LngLatLike>(view.center || [59, 30]),
        zoom: Number(view.zoom) || 5,
        pitch: Number(view.pitch) || 0,
        bearing: Number(view.bearing) || 0,
      });
      return;
    }

    if (Utils.isArray(serverMap.extent) && serverMap.extent.length > 0) {
      this.fitBounds(<LngLatBoundsLike>serverMap.extent, { animate: false });
    }
  };

  _pinRasterBaseMap = () => {
    const rasterId = this.getLayer(CUSTOM_BASEMAP_ID)
      ? CUSTOM_BASEMAP_ID
      : this.getLayer(BUILTIN_BASEMAP_ID)
        ? BUILTIN_BASEMAP_ID
        : null;
    if (!rasterId) return;
    const firstData = this.getStyle()?.layers?.find(
      (layer) =>
        layer.id !== rasterId
        && layer.type !== 'background'
        && !isVectorBasemapStyleId(String(layer.id ?? ''))
        && !Utils.isInternalRuntimeMapLayerId(String(layer.id ?? '')),
    );
    if (firstData) {
      try {
        this.moveLayer(rasterId, firstData.id);
      } catch (error) {}
    }
  };

  _pinVectorBaseMap = () => {
    const layers = this.getStyle()?.layers ?? [];
    const vectorIds = layers
      .filter((layer) => isVectorBasemapStyleId(String(layer.id ?? '')))
      .map((layer) => String(layer.id));
    if (vectorIds.length === 0) return;

    const firstData = layers.find(
      (layer) =>
        !isVectorBasemapStyleId(String(layer.id ?? ''))
        && layer.type !== 'background'
        && layer.id !== BUILTIN_BASEMAP_ID
        && layer.id !== CUSTOM_BASEMAP_ID
        && !Utils.isInternalRuntimeMapLayerId(String(layer.id ?? '')),
    );
    if (!firstData) return;

    for (const id of vectorIds) {
      try {
        this.moveLayer(id, firstData.id);
      } catch (error) {}
    }
  };

  _clearVectorBaseMapLayers = () => {
    const style = this.getStyle();
    if (!style) return;

    const layerIds = (style.layers ?? [])
      .map((layer) => String(layer.id ?? ''))
      .filter((id) => isVectorBasemapStyleId(id));
    for (const id of [...layerIds].reverse()) {
      try {
        if (this.getLayer(id)) this.removeLayer(id);
      } catch (error) {}
    }

    const sourceIds = Object.keys(style.sources ?? {}).filter((id) =>
      isVectorBasemapStyleId(id),
    );
    for (const id of sourceIds) {
      try {
        if (this.getSource(id)) this.removeSource(id);
      } catch (error) {}
    }
  };

  _clearBaseMapLayers = () => {
    this._clearVectorBaseMapLayers();
    this._removeLayerAndSource(BUILTIN_BASEMAP_ID);
    this._removeLayerAndSource(CUSTOM_BASEMAP_ID);
    this._removeBackgroundLayers();
    try {
      if (!this.getSource('geobox://geobox-gl-background')) {
        this.removeSource('geobox://geobox-gl-background');
      }
    } catch (error) {}
  };

  _applyRasterBaseMap = (id: string, tiles: string[]) => {
    this._removeBackgroundLayers();
    this.addSource(id, {
      type: 'raster',
      tiles,
      tileSize: 256,
      scheme: 'xyz',
    });
    const beforeId = this.getStyle()?.layers?.find((layer) => layer.id !== id)?.id;
    this.addLayer(
      {
        id,
        type: 'raster',
        source: id,
        minzoom: 0,
        maxzoom: 24,
        paint: {
          'raster-opacity': 1,
        },
      },
      beforeId,
    );
  };

  _stripCustomBaseMapFromOriginalStyle = () => {
    if (!this.originalStyle) return;
    if (!this.originalStyle.sources) this.originalStyle.sources = {};
    if (!this.originalStyle.layers) this.originalStyle.layers = [];

    this.originalStyle.layers = this.originalStyle.layers.filter(
      (layer) => layer.id !== CUSTOM_BASEMAP_ID,
    );
    delete this.originalStyle.sources[CUSTOM_BASEMAP_ID];
  };

  addCustomBaseMap(url: string, retryCount = 0) {
    const tileUrl = String(url ?? '').trim();
    if (!tileUrl) return;

    if (!this.loaded() || (typeof this.isStyleLoaded === 'function' && !this.isStyleLoaded())) {
      if (retryCount >= 50) {
        console.warn('[geobox-gl] addCustomBaseMap: style not ready, giving up');
        return;
      }
      setTimeout(() => {
        this.addCustomBaseMap(tileUrl, retryCount + 1);
      }, 100);
      return;
    }

    try {
      this._clearBaseMapLayers();
      this._applyRasterBaseMap(CUSTOM_BASEMAP_ID, [tileUrl]);
      this._finalizeBasemapLayers();
      this._stripCustomBaseMapFromOriginalStyle();
    } catch (error) {
      console.warn('[geobox-gl] addCustomBaseMap failed', error);
    }
  }

  addBaseMap = async (name: baseMapTypes, retryCount = 0) => {
    const styleReady =
      this.loaded()
      && (typeof this.isStyleLoaded !== 'function' || this.isStyleLoaded());

    if (!styleReady) {
      if (retryCount >= 50) {
        console.warn('[geobox-gl] addBaseMap: style not ready, giving up');
        return;
      }
      setTimeout(() => {
        void this.addBaseMap(name, retryCount + 1);
      }, 100);
      return;
    }

    let basemapsServerUrl = await this.server.getBasemapsServerUrl();

    let offlineBaseMaps;

    if (basemapsServerUrl && basemapsServerUrl.length > 0) {
      offlineBaseMaps = await this.server.getBasemaps();
    }

    if (basemapsServerUrl && offlineBaseMaps && offlineBaseMaps[name]) {
      this._clearBaseMapLayers();

      try {
        this._applyRasterBaseMap(BUILTIN_BASEMAP_ID, [
          offlineBaseMaps[name].url
            || `${this.server._origin}/v1/basemaps/${name}/tiles/{z}/{x}/{y}.png`,
        ]);
        this._stripCustomBaseMapFromOriginalStyle();
        this._finalizeBasemapLayers();
      } catch (error) {
        console.warn('[geobox-gl] addBaseMap failed', error);
      }
      return;
    }

    if (name === 'blank') {
      this._clearBaseMapLayers();
      this._stripCustomBaseMapFromOriginalStyle();
      return;
    }

    if (!baseMaps[name]) return;

    this._clearBaseMapLayers();
    try {
      this._applyRasterBaseMap(BUILTIN_BASEMAP_ID, [...baseMaps[name].url]);
      this._stripCustomBaseMapFromOriginalStyle();
      this._finalizeBasemapLayers();
    } catch (error) {
      console.warn('[geobox-gl] addBaseMap failed', error);
    }
  };

  /**
   * Load a catalog (or saved) map style as a display-only vector basemap stack.
   * Layers are namespaced, never wrapped as FeatureLayer/RasterLayer, and stripped on save.
   */
  addVectorBaseMap = async (mapUuid: string, retryCount = 0): Promise<void> => {
    const uuid = String(mapUuid ?? '').trim();
    if (!uuid) return;

    const styleReady =
      this.loaded()
      && (typeof this.isStyleLoaded !== 'function' || this.isStyleLoaded());

    if (!styleReady) {
      if (retryCount >= 50) {
        console.warn('[geobox-gl] addVectorBaseMap: style not ready, giving up');
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
      return this.addVectorBaseMap(uuid, retryCount + 1);
    }

    try {
      let style: StyleSpecification | null = null;
      let fromCatalog = false;

      const catalog = catalogStyleAuthForMapUuid(uuid);
      const origin = String(this.server?._origin || catalog?.origin || '').replace(/\/$/, '');
      const apikey = catalog?.apikey || this.server?._apikey || '';
      const styleUrls = [
        catalog?.styleUrl,
        origin && apikey ? appendApikey(`${origin}/v1/maps/${uuid}/style/`, apikey) : '',
      ].filter((item, index, list): item is string => Boolean(item) && list.indexOf(item) === index);

      for (const url of styleUrls) {
        try {
          const response = await fetch(url);
          if (!response.ok) continue;
          const json = await response.json();
          if (json && typeof json === 'object') {
            style = await prepareVectorBasemapStyle(json as StyleSpecification, apikey, origin || catalog?.origin || '');
            fromCatalog = true;
            break;
          }
        } catch {
          /* try the next style URL */
        }
      }

      if (!style) {
        try {
          const serverMap = await this.server.getMap(uuid);
          if (serverMap) {
            style = await serverMap.getStyle(true, this.userID);
            if (style && apikey) {
              style = await prepareVectorBasemapStyle(style, apikey, origin);
              fromCatalog = true;
            }
          }
        } catch {
          /* Catalog maps are public by apikey; the current user often cannot getMap them. */
        }
      }

      if (!style) {
        throw new Error(`Vector basemap style missing: ${uuid}`);
      }

      style = Utils.stripEphemeralGraphicsFromStyle(style, {
        preserveLiveMetadata: true,
      });

      if (!fromCatalog) {
        try {
          const filtered = await Utils.filterInaccessibleMapSources(style, {
            authorizeRequest: this._authorizeMapRequest,
          });
          style = filtered.style;
        } catch (error) {
          console.warn('[geobox-gl] addVectorBaseMap filterInaccessibleMapSources failed', error);
        }
      }

      const plain = Utils.toPlainMapStyle(style);
      // Use a plain record — class name `Map` shadows the built-in Map constructor here.
      const sourceIdMap: Record<string, string> = {};

      for (const sourceId of Object.keys(plain.sources ?? {})) {
        if (Utils.isInternalRuntimeMapSourceId(sourceId) || isVectorBasemapStyleId(sourceId)) {
          continue;
        }
        sourceIdMap[sourceId] = `${VECTOR_BASEMAP_PREFIX}${sourceId}`;
      }

      // Only clear the previous basemap after the new style is ready to apply.
      this._clearBaseMapLayers();

      for (const [oldId, newId] of Object.entries(sourceIdMap)) {
        if (this.getSource(newId)) continue;
        try {
          this.addSource(newId, (plain.sources as Record<string, SourceSpecification>)[oldId]);
        } catch (error) {
          console.warn(`[geobox-gl] addVectorBaseMap source failed: ${oldId}`, error);
        }
      }

      const beforeId = this.getStyle()?.layers?.find(
        (layer) =>
          layer.type !== 'background'
          && !isVectorBasemapStyleId(String(layer.id ?? ''))
          && !Utils.isInternalRuntimeMapLayerId(String(layer.id ?? '')),
      )?.id;

      for (const layer of plain.layers ?? []) {
        if (layer.type === 'background') continue;
        if (Utils.isInternalRuntimeMapLayerId(String(layer.id ?? ''))) continue;
        if (isVectorBasemapStyleId(String(layer.id ?? ''))) continue;

        const nextLayer = { ...layer } as LayerSpecification & { source?: string };
        nextLayer.id = `${VECTOR_BASEMAP_PREFIX}${layer.id}`;

        if (typeof nextLayer.source === 'string') {
          const remapped = sourceIdMap[nextLayer.source];
          if (!remapped) continue;
          nextLayer.source = remapped;
        }

        if (this.getLayer(nextLayer.id)) continue;

        try {
          if (beforeId && this.getLayer(beforeId)) {
            this.addLayer(nextLayer, beforeId);
          } else {
            this.addLayer(nextLayer);
          }
        } catch (error) {
          console.warn(`[geobox-gl] addVectorBaseMap layer failed: ${nextLayer.id}`, error);
        }
      }

      this._stripCustomBaseMapFromOriginalStyle();
      this._finalizeBasemapLayers();
    } catch (error) {
      console.warn('[geobox-gl] addVectorBaseMap failed', error);
      throw error;
    }
  };

  refreshMap = async (): Promise<void> => {
    const _this = this;
    if (this.loaded()) {

      const emptyStyle: StyleSpecification = {
        version: 8,
        name: 'Geobox',
        sources: {},
        layers: [],
        glyphs: `${this.server._origin}/fonts/{fontstack}/{range}.pbf`,
        sprite: `${this.server._origin}/sprites/basic`,
      };

      this.setStyle(emptyStyle);

      let newMap = await this.server.getMap(this.serverMap?.uuid)
      
      this.openMap(newMap, this.userID)
    }else {
      return new Promise((resolve) => {
        setTimeout(async () => {
          const result = await this.refreshMap();
          resolve(result);
        }, 100);
      });
    }
  }


  private hydrateGeoboxLayerSource = async (
    mapSourceId: string,
    geoboxLayer: layerSource,
  ): Promise<layerSource> => {
    if (Utils.isLiveGeoboxLayerSource(geoboxLayer)) {
      return geoboxLayer;
    }

    const parsed = Utils.parseGeoboxSourceRef(mapSourceId);
    if (!parsed) return geoboxLayer;

    const server = this.server as geoboxServer & {
      getVectorLayer?: (uuid: string) => Promise<layerSource>;
      getVectorLayerView?: (uuid: string) => Promise<layerSource>;
      getTileset?: (uuid: string) => Promise<layerSource>;
      getMosaic?: (uuid: string) => Promise<layerSource>;
      getRaster?: (uuid: string) => Promise<layerSource>;
    };

    try {
      switch (parsed.type) {
        case 'view':
          if (server.getVectorLayerView) {
            return await server.getVectorLayerView(parsed.uuid);
          }
          break;
        case 'vector':
          if (server.getVectorLayer) {
            return await server.getVectorLayer(parsed.uuid);
          }
          break;
        case 'tileset':
          if (server.getTileset) {
            return await server.getTileset(parsed.uuid);
          }
          break;
        case 'mosaic':
          if (server.getMosaic) {
            return await server.getMosaic(parsed.uuid);
          }
          break;
        case 'raster':
        case 'hillshade':
          if (server.getRaster) {
            return await server.getRaster(parsed.uuid);
          }
          break;
      }
    } catch {
      /* keep deserialized metadata if fetch fails */
    }

    return geoboxLayer;
  };


  openMap = async (serverMap: geoboxMap, userID?: number): Promise<void> => {
    const _this = this;

    this.userID = userID;

    if (this.loaded()) {
      this.serverMap = serverMap;

      try {
        
        this.libreMapFeatureLayers = {};
       
        this.libreMapRasterLayers = {};

        let style = await serverMap.getStyle(true, userID);
        let legacyStyleModels: ReturnType<typeof Utils.extractMapModelsFromStyle> = [];

        if (style) {
          // Recover models that were incorrectly saved into style before stripping.
          legacyStyleModels = Utils.extractMapModelsFromStyle(style);
          style = Utils.stripEphemeralGraphicsFromStyle(style, {
            preserveLiveMetadata: true,
          });

          // Skip deleted/unauthorized TileJSON sources before setStyle so MapLibre
          // does not stall mid-load (which breaks basemap and other mutations).
          try {
            const filtered = await Utils.filterInaccessibleMapSources(style, {
              authorizeRequest: this._authorizeMapRequest,
            });
            style = filtered.style;
            if (filtered.removedSourceIds.length > 0) {
              console.warn(
                '[geobox-gl] Skipped inaccessible map sources:',
                filtered.removedSourceIds,
              );
            }
          } catch (error) {
            console.warn('[geobox-gl] filterInaccessibleMapSources failed', error);
          }

          this.originalStyle = JSON.parse(JSON.stringify(style));

          // for (let index = 0; index <  this.originalStyle.layers.length; index++) {
          //   const layer =  this.originalStyle.layers[index];  
          //   delete layer['geobox-layer'];
          //   delete layer['geobox-source'];
          // }
        }

        if (serverMap.settings) {
          this.mapSettings = <mapSettings>serverMap.settings;
        }

        // Prefer an intentional camera permalink (#zoom/lat/lng/pitch/bearing).
        // Ignore the Map constructor default (#3/15/1/…) — hosts often stamp that
        // into the URL before openMap applies saved view_settings/extent.
        // Dual-map secondary pane shares the page hash with the primary map, so it
        // uses the secondary map's own view_settings / extent instead.
        const isDualPane =
          this.getContainer()?.id === Map.DUAL_MAP_CONTAINER_ID;
        if (!isDualPane) {
          const hashCamera = Utils.parseMapCameraHash(window.location.hash);
          if (hashCamera && !Utils.isConstructorDefaultCamera(hashCamera)) {
            this.flyTo({
              center: [hashCamera.lng, hashCamera.lat],
              zoom: hashCamera.zoom,
              pitch: hashCamera.pitch,
              bearing: hashCamera.bearing,
              duration: 2000,
              essential: true,
            });
          } else if (Utils.isArray(serverMap.extent) && serverMap.extent.length > 0) {
            if (
              this.mapSettings["view_settings"] &&
              this.mapSettings["view_settings"]["center"] &&
              this.mapSettings["view_settings"]["zoom"]
            ) {
              this.flyTo({
                center: <LngLatLike>(this.mapSettings["view_settings"]["center"] || [59, 30]),
                zoom: Number(this.mapSettings["view_settings"]["zoom"]) || 5,
                pitch: Number(this.mapSettings["view_settings"]["pitch"]) || 0,
                bearing: Number(this.mapSettings["view_settings"]["bearing"]) || 0,
                duration: 2000,
                essential: true
              });
            } else {
              this.fitBounds(<LngLatBoundsLike>serverMap.extent);
            }
          }
        } else {
          this._applySavedMapCamera(serverMap);
        }

        // Dual pane is view-only: skip FeatureLayer wrapping (it keeps live Geobox SDK
        // class instances on the style and breaks MapLibre worker serialization).
        if (style && isDualPane) {
          const plainStyle = Utils.toPlainMapStyle(style);
          this.originalStyle = plainStyle;

          let dualBasemapApplied = false;
          const afterDualStyleReady = () => {
            if (dualBasemapApplied) return;
            dualBasemapApplied = true;
            _this._ensureRuntimeOverlayLayers();
            void _this._applyBasemapFromSettings();
            _this.off('idle', afterDualStyleReady);
            _this.off('style.load', afterDualStyleReady);
          };
          // Register before setStyle so we cannot miss a fast style.load/idle.
          this.once('style.load', afterDualStyleReady);
          this.once('idle', afterDualStyleReady);
          this.setStyle(plainStyle, { diff: false });

          try {
            if (plainStyle.sprite) {
              const sprite = await fetch(`${plainStyle.sprite}.json`);
              const sprites = await sprite.json();
              this.symbolNames = Object.keys(sprites);
            }
          } catch {
            this.symbolNames = this.symbolNames ?? [];
          }

          // Fallback if style.load/idle already passed or never fire.
          setTimeout(() => {
            if (!dualBasemapApplied) afterDualStyleReady();
          }, 500);

          return;
        }

        if (style) {
          
          for (let index = 0; index < style.layers.length; index++) {
            const layer = style.layers[index];

            if (Utils.isInternalRuntimeMapLayerId(layer.id)) continue;

            let visibility = 'visible';
            if (layer.layout && layer.layout.visibility) {
              visibility = layer.layout.visibility || 'visible';
            }

            if (layer.type === 'background') continue;
            let featureLayer: FeatureLayer;
            let rasterLayer: RasterLayer;
            let geoboxLayer: any = Utils.resolveGeoboxLayerMetadata(
              layer,
              style.sources,
            );
            const geoboxSource =
              layer['geobox-source'] ??
              (style.sources?.[layer.source] as { 'geobox-source'?: unknown } | undefined)?.[
                'geobox-source'
              ];

            if (
              !geoboxLayer &&
              !geoboxSource &&
              layer.source.slice(0, 9) === 'geobox://'
            ) {
              
              if (layer.type !== 'raster' || layer.type !== 'hillshade') {
                geoboxLayer = {};
                geoboxLayer.layer_source_type = layer.type;
                geoboxLayer.layer_type = 'foreign';
                geoboxLayer.name = layer.id;
                // geoboxLayer.show_in_legend = true;
                geoboxLayer.is_terrain = false;

                switch (geoboxLayer.layer_type) {
                  case 'Point':
                  case 'MultiPoint':
                    featureLayer = new PointVectorLayer(
                      geoboxLayer,
                      this,
                      layer['source-layer'],
                      layer.id,
                      layer.source
                    );

                    break;
                  case 'Polyline':
                    featureLayer = new LineVectorLayer(
                      geoboxLayer,
                      this,
                      layer['source-layer'],
                      layer.id,
                      layer.source
                    );

                    break;
                  case 'Polygon':
                    featureLayer = new PolygonVectorLayer(
                      geoboxLayer,
                      this,
                      layer['source-layer'],
                      layer.id,
                      layer.source
                    );

                    break;
                  default:
                    featureLayer = new FeatureLayer(geoboxLayer, 
                      this,
                      layer['source-layer'],
                      layer.id,
                      layer.source);
                    break;
                }
                const newLayer = featureLayer.changeFeatureStyle(layer);

                style.layers[index] = newLayer;
                // this.libreMapFeatureLayers[layer.id] = featureLayer;
              } else {
                geoboxLayer = {};
                geoboxLayer.layer_source_type = layer.type;
                geoboxLayer.layer_type = 'foreign';
                geoboxLayer.name = layer.id;
                geoboxLayer.source = layer.source;
                // geoboxLayer.show_in_legend = true;
                geoboxLayer.is_terrain = false;
              }
              continue;
            }
            if (!geoboxLayer) {
              if (layer.type !== 'raster' || layer.type !== 'hillshade') {
                geoboxLayer = {};
                geoboxLayer.layer_source_type = layer.type;
                geoboxLayer.layer_type = 'foreign';
                geoboxLayer.name = layer.id;
                // geoboxLayer.show_in_legend = true;
                geoboxLayer.is_terrain = false;

                switch (geoboxLayer.layer_type) {
                  case 'Point':
                  case 'MultiPoint':
                    featureLayer = new PointVectorLayer(
                      geoboxLayer,
                      this,
                      layer['source-layer'],
                      layer.id,
                      layer.source
                    );

                    break;
                  case 'Polyline':
                    featureLayer = new LineVectorLayer(
                      geoboxLayer,
                      this,
                      layer['source-layer'],
                      layer.id,
                      layer.source
                    );

                    break;
                  case 'Polygon':
                    featureLayer = new PolygonVectorLayer(
                      geoboxLayer,
                      this,
                      layer['source-layer'],
                      layer.id,
                      layer.source
                    );

                    break;
                  default:
                    featureLayer = new FeatureLayer(geoboxLayer, this);
                    break;
                }
                const newLayer = featureLayer.changeFeatureStyle(layer);
                

                style.layers[index] = newLayer;
                if (!(layer.id.endsWith("$outline") || layer.id.endsWith("$symbol") || layer.id.endsWith("$label") || layer.id.endsWith("$heatmap") || layer.id.endsWith("$extrusion") || layer.id.endsWith("$cluster")
                  || layer.id.endsWith("$cluster-count") || layer.id.endsWith("$unclustered"))) 
                  this.libreMapFeatureLayers[layer.id] = featureLayer;
              } else {
                geoboxLayer = {};
                geoboxLayer.layer_source_type = layer.type;
                geoboxLayer.layer_type = 'foreign';
                geoboxLayer.name = layer.id;
                geoboxLayer.source = layer.source;
                // geoboxLayer.show_in_legend = true;
                geoboxLayer.is_terrain = false;
              }
              continue;
            }

            if (layer.type === 'raster' || layer.type === 'hillshade') {
              let prefix;
            
              if (layer.source.includes('geobox://mosaic/')) {
                prefix = 'mosaic';
                geoboxLayer.layer_source_type = 'raster';
                geoboxLayer.layer_type = prefix;
              }else  if (layer.source.includes('geobox://hillshade/')) {
                prefix = 'hillshade';
                geoboxLayer.layer_source_type = 'raster';
                geoboxLayer.layer_type = prefix;
              } else {
                prefix = 'raster';
                geoboxLayer.layer_source_type = prefix;
                geoboxLayer.layer_type = prefix;
              }
              // geoboxLayer.show_in_legend = true;
              geoboxLayer.is_terrain = layer.is_terrain;

              rasterLayer = new RasterLayer(
                geoboxLayer,
                this,
                layer['source-layer'],
                layer.id,
                layer.source
              );

              if (!(layer.id.endsWith("$hillshade") || layer.id.endsWith("$raster"))) this.libreMapRasterLayers[layer.id] = rasterLayer;

            } else {
              geoboxLayer.layer_source_type = geoboxLayer.is_view ? 'view' : 'vector';

              // geoboxLayer.show_in_legend = true;
              geoboxLayer.is_terrain = layer.is_terrain;

              geoboxLayer = await this.hydrateGeoboxLayerSource(
                layer.source,
                geoboxLayer,
              );


              switch (geoboxLayer.layer_type) {
                case 'Point':
                case 'MultiPoint':
                  featureLayer = new PointVectorLayer(
                    geoboxLayer,
                    this,
                    layer['source-layer'],
                    layer.id,
                    layer.source
                  );
  
                  break;
                case 'Polyline':
                  featureLayer = new LineVectorLayer(
                    geoboxLayer,
                    this,
                    layer['source-layer'],
                    layer.id,
                    layer.source
                  );

                  break;
                case 'Polygon':
                  featureLayer = new PolygonVectorLayer(
                    geoboxLayer,
                    this,
                    layer['source-layer'],
                    layer.id,
                    layer.source
                  );

                  break;
                default:
                  featureLayer = new FeatureLayer(geoboxLayer, this);
                  break;
              }
              let newLayer: LayerSpecification;
              
              newLayer = featureLayer.changeFeatureStyle(layer);
  
              style.layers[index] = newLayer;
            
              if (!(layer.id.endsWith("$outline") || layer.id.endsWith("$symbol") || layer.id.endsWith("$label") || layer.id.endsWith("$heatmap") || layer.id.endsWith("$extrusion") || layer.id.endsWith("$cluster")
                || layer.id.endsWith("$cluster-count") || layer.id.endsWith("$unclustered"))) 
                this.libreMapFeatureLayers[layer.id] = featureLayer;
            }
          }
        }

        if (!style) style = this.originalStyle;

        style.sources['geobox-gl-snap-vertex'] = {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'Point',
                  coordinates: [],
                },
              },
            ],
          },
        };

        style.sources['geobox-gl-snap-point'] = {
          type: 'geojson',
          maxzoom: 24,
          tolerance: 0,
          data: {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'Point',
                  coordinates: [],
                },
              },
            ],
          },
        };

        style.sources['geobox-gl-snap-line'] = {
          type: 'geojson',
          maxzoom: 24,
          tolerance: 0,
          data: {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates: [],
                },
              },
            ],
          },
        };

        style.sources['geobox-gl-snap-polygon'] = {
          type: 'geojson',
          maxzoom: 24,
          tolerance: 0,
          data: {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'Polygon',
                  coordinates: [],
                },
              },
            ],
          },
        };

        style.sources['geobox-gl-snap-marker'] = {
          type: 'geojson',
          maxzoom: 24,
          tolerance: 0,
          data: {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'Point',
                  coordinates: [],
                },
              },
            ],
          },
        };

        style.sources['geobox-gl-splash-circle'] = {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: [],
                },
              },
            ],
          },
        };

        if (this.getSource('geobox-gl-splash-circle')) {
          style.layers[style.layers.length] = {
            id: 'geobox-gl-splash-circle',
            type: 'symbol',
            source: 'geobox-gl-splash-circle',
            layout: {
              'icon-image': 'pulsing-dot',
              'icon-allow-overlap': true,
            },
          };
        }

        style.sources["geobox-gl-splash-circle"] = {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                geometry: {
                  type: "Point",
                  coordinates: [],
                },
              },
            ],
          },
        };

        // if (this.getSource("geobox-gl-splash-circle")) {
        //   style.layers[style.layers.length] = {
        //     id: "geobox-gl-splash-circle",
        //     type: "symbol",
        //     source: "geobox-gl-splash-circle",
        //     layout: {
        //       "icon-image": "pulsing-dot",
        //       "icon-allow-overlap": true
        //     },
        //   };
        // }

        // getStyle(true) keeps live Geobox SDK class instances on layers/sources
        // (minified names like "c"). MapLibre workers can only serialize plain
        // Object / registered classes — clone before setStyle.
        // diff:false avoids the setLayerProperty diff path that re-walks mixed objects.
        this.setStyle(Utils.toPlainMapStyle(style), { diff: false });

        function afterStyleReady(){
          _this._ensureRuntimeOverlayLayers();
          _this.setTerrainInMap('on');

          // Legacy maps stored pick GeoJSON in style; rehydrate after style is ready
          // so MapModelsManager.addSource cannot race setStyle.
          if (legacyStyleModels.length > 0) {
            _this.models3d.setFromDocument({ objects: legacyStyleModels });
          }

          // _this.addBaseMap(<baseMapTypes>_this.mapSettings.general_settings.base_map)

          _this.off('idle', afterStyleReady);
        }

        this.on('idle', afterStyleReady);

        const sprite = await fetch(`${this.originalStyle.sprite}.json`);
        const sprites = await sprite.json();
        this.symbolNames = Object.keys(sprites);

        // for (let index = 0; index <  this.originalStyle.layers.length; index++) {
        //   const layer =  this.originalStyle.layers[index];
        //   if (layer.type === 'symbol' && layer.layout && layer.layout["text-field"]){
        //     this.libreMapFeatureLayers[layer.id].addLabel(this.symbolNames[0], layer);
        //   }else if (layer.type === 'fill-extrusion'){
        //     if (layer.paint){
        //       (<PolygonVectorLayer>this.libreMapFeatureLayers[layer.id]).addExtrusion(<number>layer.paint["fill-extrusion-height"], <number>layer.paint["fill-extrusion-base"],layer);
        //     } else {
        //       (<PolygonVectorLayer>this.libreMapFeatureLayers[layer.id]).addExtrusion(100,0);
        //     }
        //   }
        // }

        if (!this.hasImage("pulsing-dot")) {
          try {
            this.addImage("pulsing-dot", this.pulsingDot, {
              pixelRatio: 1,
            });
          } catch (error) {}
        }

        return;
      } catch (error) {
        throw new Error(String(error));
      }
    }else {
      return new Promise((resolve) => {
        setTimeout(async () => {
          const result = await this.openMap(serverMap, userID);
          resolve(result);
        }, 100);
      });
    }
  };

  zoomToExtent = (extent: LngLatBoundsLike) => {
    if (this.loaded()) {
      if (!extent) return;
      if (!Utils.isArray(extent)) return;
      if ((<any>extent).length === 0) return;
      extent = extent || [-70, -150, 70, 150];
      const hashCamera = Utils.parseMapCameraHash(window.location.hash);
      if (hashCamera && !Utils.isConstructorDefaultCamera(hashCamera)) {
        try {
          this.flyTo({
            center: [hashCamera.lng, hashCamera.lat],
            zoom: hashCamera.zoom,
            pitch: hashCamera.pitch,
            bearing: hashCamera.bearing,
            duration: 2000,
            essential: true,
          });
        } catch (error) {
          this.fitBounds(extent);
        }
      } else {
        this.fitBounds(extent);
      }
    } else {
      setTimeout(() => {
        this.zoomToExtent(extent);
      }, 100);
    }
  };

  setTerrainInMap( status: 'on' | 'off', layerID?:string, exaggeration?:number) {
    if (!status) return;

    let style: StyleSpecification = this.getStyle();

    layerID = layerID || <string>this.mapSettings.terrain_settings?.terrain_layer;

    exaggeration = exaggeration || this.mapSettings.terrain_settings?.exaggeration || 1;

    if (status === 'on' && !layerID) return;

    if (status === 'off' || !layerID) {
      // if (this._loaded) {
      this.setTerrain(null);
      
      try {
        this.removeSource('geobox_gl_terrain_for_3d');
      } catch (error) {
      }

      return;
      // } else {
      //   setTimeout(() => {
      //     console.log(12)
      //     this.setTerrainInMap(style);
      //   }, 100);
      //   return;
      // }
    }

    for (const layer of style.layers) {
      let layerObject = this.getLayerObject(layer.id);
      
      if (layerObject && layerObject.source.is_terrain) {
        if (layer.id !== layerID) continue;
        
        const source: RasterDEMSourceSpecification = <
          RasterDEMSourceSpecification
        >style.sources[(<layerObj>layer).source];
        const jsonUrl = source.url;

        const pngUrl = <string>(
          jsonUrl?.replace('/tilejson.json', '/tiles/{z}/{x}/{y}.png')
        );

        try {
          this.removeSource('geobox_gl_terrain_for_3d');
        } catch (error) {
        }

        try {
          this.addSource('geobox_gl_terrain_for_3d', {
            url: jsonUrl,
            type: 'raster-dem',
            tiles: [pngUrl],
            encoding: 'mapbox',
            tileSize: 512,
          });
  
        } catch (error) {
          
        }
        
        this.setTerrain({
          source: 'geobox_gl_terrain_for_3d',
          exaggeration: exaggeration,
        });
      
        break;
      }
    }
  }

  getTerrainHeightAt(lngLat: LngLatLike): number | null {
    const terrain = (this as any).terrain;
    if (!terrain) return null;

    const lnglat = maplibregl.LngLat.convert(lngLat);
    const zoom = (this as any).transform?.tileZoom ?? this.getZoom();
    const { tileID, mercatorX, mercatorY } = terrain._getOverscaledTileIDFromLngLatZoom(lnglat, zoom);
    const terrainData = terrain.getTerrainData(tileID);
    if (!terrainData?.tile?.dem) return null;

    const EXTENT = 8192;
    const elevation = terrain.getDEMElevation(
      tileID,
      mercatorX % EXTENT,
      mercatorY % EXTENT,
      EXTENT,
    );

    return typeof elevation === 'number' && Number.isFinite(elevation) ? elevation : null;
  }

  getFeatureLayers = () => {
    return this.libreMapFeatureLayers;
  };

  getRasterLayers = () => {
    return this.libreMapRasterLayers;
  };

  getLayerObject = (id: string): any => {
    if (Object.keys(this.libreMapFeatureLayers).includes(id)) {
      return this.libreMapFeatureLayers[id];
    }
    if (Object.keys(this.libreMapRasterLayers).includes(id)) {
      return this.libreMapRasterLayers[id];
    }
    return undefined;
  };

  // setTOC = (toc: tocLayer) => {
  //   this.mapSettings.toc_settings = toc;
  //   this.serverMap?.updateSettings(this.mapSettings);
  // };

  // renameLayer = (oldID: string, newID: string) => {
  //   const newStyle = JSON.parse(JSON.stringify(this.getStyle()));

  //   for (const layer of newStyle.layers) {
  //     if (oldID === layer.id) {
  //       layer.id = newID;

  //       if (Object.keys(this.libreMapFeatureLayers).includes(oldID)) {
  //         delete Object.assign(this.libreMapFeatureLayers, {
  //           [newID]: this.libreMapFeatureLayers[oldID],
  //         })[oldID];

  //         this.libreMapFeatureLayers[newID].layerId = newID;
  //       } else if (Object.keys(this.libreMapRasterLayers).includes(oldID)) {
  //         delete Object.assign(this.libreMapRasterLayers, {
  //           [newID]: this.libreMapRasterLayers[oldID],
  //         })[oldID];

  //         this.libreMapRasterLayers[newID].layerId = newID;
  //       } else {
  //         console.log('Layer was not founded');
  //       }
  //     }
  //   }

  //   this.setStyle(newStyle, { diff: true });
  // };

  moveLayerOrder = (id: string, targetId?: string ) => {
 
    if (!targetId || !this.getLayer(targetId)) {
      this.moveLayer(id);

      try {
        this.moveLayer(`${id}$hillshade`);
      } catch (error) {}

      try {
        this.moveLayer(`${id}$raster`);
      } catch (error) {}
      
      try {
        this.moveLayer(`${id}$extrusion`);
      } catch (error) {}

      try {
        this.moveLayer(`${id}$outline`);
      } catch (error) {} 


      try {
        this.moveLayer(`${id}$heatmap`);
      } catch (error) {}


      try {
        this.moveLayer(`${id}$cluster`);
      } catch (error) {}

      try {
        this.moveLayer(`${id}$unclustered`);
      } catch (error) {}

      try {
        this.moveLayer(`${id}$cluster-count`);
      } catch (error) {}
  
    } else {
      this.moveLayer(id, targetId);

      try {
        this.moveLayer(`${id}$hillshade`,targetId);
      } catch (error) {}

      try {
        this.moveLayer(`${id}$raster`,targetId);
      } catch (error) {}

      try {
        this.moveLayer(`${id}$extrusion`,targetId);
      } catch (error) {}

      try {
        this.moveLayer(`${id}$outline`,targetId);
      } catch (error) {} 

      try {
        this.moveLayer(`${id}$heatmap`,targetId);
      } catch (error) {}
  
      // try {
      //   this.moveLayer(`${id}$symbol`,targetId);
      // } catch (error) {}

      try {
        this.moveLayer(`${id}$cluster`,targetId);
      } catch (error) {}

      try {
        this.moveLayer(`${id}$unclustered`,targetId);
      } catch (error) {}

      try {
        this.moveLayer(`${id}$cluster-count`,targetId);
      } catch (error) {}

    }

    let style = this.getStyle();

    style.layers.forEach(layer => {
      try {
        this.moveLayer(`${layer.id}$label`);
        this.moveLayer(`${layer.id}$symbol`);
      } catch (error) {
      }
    });

    this.moveLayer('geobox-gl-splash-circle');

    this._finalizeBasemapLayers();

  };

  override removeLayer(id: string): any {
    try {
      this.style.removeLayer(id);
      this._update(true);
     
      if (Object.keys(this.libreMapFeatureLayers).includes(id)) {
        delete this.libreMapFeatureLayers[id];
      } else if (Object.keys(this.libreMapRasterLayers).includes(id)) {
        delete this.libreMapRasterLayers[id];
      } else {
        // console.log('Layer was not founded');
      }

      try {
        this.style.removeLayer(`${id}$symbol`);
      } catch (error) {
      }

      try {
        this.style.removeLayer(`${id}$label`);
      } catch (error) {
      }

      try {
        this.style.removeLayer(`${id}$outline`);
      } catch (error) {
      } 

      try {
        this.style.removeLayer(`${id}$extrusion`);
      } catch (error) {
      } 

      try {
        this.style.removeLayer(`${id}$heatmap`);
      } catch (error) {
      } 

      try {
        this.style.removeLayer(`${id}$cluster`);
      } catch (error) {
      } 

      try {
        this.style.removeLayer(`${id}$cluster-count`);
      } catch (error) {
      } 

      try {
        this.style.removeLayer(`${id}$unclustered`);
      } catch (error) {
      } 

      try {
        this.style.removeLayer(`${id}$hillshade`);
        if (this.getSource(`${id}$hillshade`)){
          this.removeSource(`${id}$hillshade`)
        }
      } catch (error) {
      } 

      try {
        this.style.removeLayer(`${id}$raster`);
      } catch (error) {
      } 
  
    } catch (error) {}
  }

  // removeGroup = (groupName: string) => {
  //   this._removeGroup(groupName);
  // };

  // private _removeGroup = (
  //   groupName: string,
  //   groups: tocLayer = this.mapSettings.toc_settings,
  //   remove: boolean = false
  // ) => {
  //   for (const group of groups) {
  //     if (remove) {
  //       this.removeLayer(group.name);
  //     }
  //     if (group.name === groupName) {
  //       if (group.type === 'layer') {
  //         this.removeLayer(groupName);
  //       } else {
  //         for (const group1 of group.layers) {
  //           this._removeGroup(groupName, [group1], true);
  //         }
  //       }
  //     } else {
  //       for (const group1 of group.layers) {
  //         this._removeGroup(groupName, [group1], remove);
  //       }
  //     }
  //   }
  // };

  // changeGroupVisibility = (groupName: string, status: visibilityStatus) => {
  //   this._changeGroupVisibility(groupName, status);
  // };

  // private _changeGroupVisibility = (
  //   groupName: string,
  //   status: visibilityStatus,
  //   groups: tocLayer = this.mapSettings.toc_settings,
  //   change: boolean = false
  // ) => {
  //   for (const group of groups) {
  //     if (change) {
  //       if (Object.keys(this.libreMapFeatureLayers).includes(group.name)) {
  //         this.libreMapFeatureLayers[group.name].setProperty(
  //           'visibility',
  //           status
  //         );
  //       } else if (
  //         Object.keys(this.libreMapRasterLayers).includes(group.name)
  //       ) {
  //         this.libreMapRasterLayers[group.name].setProperty(
  //           'visibility',
  //           status
  //         );
  //       } else {
  //         // console.log('Layer was not founded');
  //       }
  //     }
  //     if (group.name === groupName) {
  //       if (group.type === 'layer') {
  //         if (Object.keys(this.libreMapFeatureLayers).includes(groupName)) {
  //           this.libreMapFeatureLayers[groupName].setProperty(
  //             'visibility',
  //             status
  //           );
  //         } else if (
  //           Object.keys(this.libreMapRasterLayers).includes(groupName)
  //         ) {
  //           this.libreMapRasterLayers[groupName].setProperty(
  //             'visibility',
  //             status
  //           );
  //         } else {
  //           // console.log('Layer was not founded');
  //         }
  //       } else {
  //         for (const group1 of group.layers) {
  //           this._changeGroupVisibility(groupName, status, [group1], true);
  //         }
  //       }
  //     } else {
  //       for (const group1 of group.layers) {
  //         this._changeGroupVisibility(groupName, status, [group1], change);
  //       }
  //     }
  //   }
  // };

  // zoomToGroup = (groupName: string) => {
  //   this._zoomToGroup(groupName);
  // };

  // private _zoomToGroup = async (
  //   groupName: string,
  //   extent: number[] = [99999999, 99999999, -99999999, -99999999],
  //   groups: tocLayer = this.mapSettings.toc_settings,
  //   change: boolean = false
  // ) => {
  //   let layerExtent;

  //   for (const group of groups) {
  //     if (change) {
  //       if (Object.keys(this.libreMapFeatureLayers).includes(group.name)) {
  //         layerExtent = this.libreMapFeatureLayers[group.name].source.extent;

  //         if (layerExtent[0] < extent[0]) extent[0] = layerExtent[0];
  //         if (layerExtent[1] < extent[1]) extent[1] = layerExtent[1];
  //         if (layerExtent[2] > extent[2]) extent[2] = layerExtent[2];
  //         if (layerExtent[3] > extent[3]) extent[3] = layerExtent[3];
  //       } else if (
  //         Object.keys(this.libreMapRasterLayers).includes(group.name)
  //       ) {
  //         layerExtent = this.libreMapRasterLayers[group.name].source.extent;
  //         if (layerExtent[0] < extent[0]) extent[0] = layerExtent[0];
  //         if (layerExtent[1] < extent[1]) extent[1] = layerExtent[1];
  //         if (layerExtent[2] > extent[2]) extent[2] = layerExtent[2];
  //         if (layerExtent[3] > extent[3]) extent[3] = layerExtent[3];
  //       }
  //       if (
  //         JSON.stringify(extent) !==
  //         JSON.stringify([99999999, 99999999, -99999999, -99999999])
  //       ) {
  //         await this.fitBounds(<LngLatBoundsLike>[
  //           Utils.webMercatorToLngLat(extent[0], extent[1]),
  //           Utils.webMercatorToLngLat(extent[2], extent[3]),
  //         ]);
  //       }
  //     }

  //     if (group.name === groupName) {
  //       if (group.type === 'layer') {
  //         if (Object.keys(this.libreMapFeatureLayers).includes(groupName)) {
  //           layerExtent = this.libreMapFeatureLayers[group.name].source.extent;
  //           if (layerExtent[0] < extent[0]) extent[0] = layerExtent[0];
  //           if (layerExtent[1] < extent[1]) extent[1] = layerExtent[1];
  //           if (layerExtent[2] > extent[2]) extent[2] = layerExtent[2];
  //           if (layerExtent[3] > extent[3]) extent[3] = layerExtent[3];
  //         } else if (
  //           Object.keys(this.libreMapRasterLayers).includes(groupName)
  //         ) {
  //           layerExtent = this.libreMapRasterLayers[group.name].source.extent;
  //           if (layerExtent[0] < extent[0]) extent[0] = layerExtent[0];
  //           if (layerExtent[1] < extent[1]) extent[1] = layerExtent[1];
  //           if (layerExtent[2] > extent[2]) extent[2] = layerExtent[2];
  //           if (layerExtent[3] > extent[3]) extent[3] = layerExtent[3];
  //         }
  //         if (
  //           JSON.stringify(extent) !==
  //           JSON.stringify([99999999, 99999999, -99999999, -99999999])
  //         ) {
  //           await this.fitBounds(<LngLatBoundsLike>[
  //             Utils.webMercatorToLngLat(extent[0], extent[1]),
  //             Utils.webMercatorToLngLat(extent[2], extent[3]),
  //           ]);
  //         }
  //       } else {
  //         for (const group1 of group.layers) {
  //           this._zoomToGroup(groupName, extent, [group1], true);
  //         }
  //       }
  //     } else {
  //       for (const group1 of group.layers) {
  //         this._zoomToGroup(groupName, extent, [group1], change);
  //       }
  //     }
  //   }
  // };

  getLegendImage = (imageName: string, backgroundColor: string, imageColor: string) => {
    if (!this.style.getImage(imageName)){
      throw new Error("This image is not existed!");
    } 

    backgroundColor = backgroundColor || 'rgb(255,255,255)';
    imageColor = imageColor || 'rgb(0,0,0)';
    let backgroundColorArray = backgroundColor.match(/\d+/g);
    let imageColorArray = imageColor.match(/\d+/g);

    const { height, width, data } = this.style.getImage(imageName)?.data || {};

    const sdf = this.style.getImage(imageName).sdf || false;
    if (!height || !width || !data) return undefined;

    let imageData = new ImageData(
      Uint8ClampedArray.from(data),
      width,
      height
    );

    let data2 = []
    const { length } = data;
    if (sdf) {
      for (let i = 0; i < length; i += 4) {
        const a = data[i + 3];
        data2[i + 3] = data[i + 3];
        if (a > 180 ) {
          data2[i] = Number((<any>imageColorArray)[0]);
          data2[i + 1] =  Number((<any>imageColorArray)[1]);
          data2[i + 2] =  Number((<any>imageColorArray)[2]);
        } else {
          data2[i] =  Number((<any>backgroundColorArray)[0]);
          data2[i + 1] =  Number((<any>backgroundColorArray)[1]);
          data2[i + 2] =  Number((<any>backgroundColorArray)[2]);
        }
      }
      imageData = new ImageData(
        Uint8ClampedArray.from(data2),
        width,
        height
      );
    }
    
    return imageData;

  }

  // remove this method----------------------------------------------------------
  getLegendStyle = (layerId: string, property:property) =>{
    const featureLayer = this.libreMapFeatureLayers[layerId];

    let value = featureLayer.getProperty(property);

    // let value = this.getPaintProperty(layerId, property);

    // if (value[5] && value[5].length > 1) {
    //   value = value[5];
    // }

    return expression.parse(value);
  }

  getScreenshot = async () => {
    return await this.takeScreenshot();
  }


  private takeScreenshot = () => {
    const _this = this;
    const mapThumbnail = new Utils.GetMapThumbnail();
    return new Promise(function (resolve, reject) {
      _this.once("render", function () {
        const canvas = mapThumbnail.saveClipped(_this);
        resolve(canvas.toDataURL());
      });
      _this.setBearing(_this.getBearing());
    });
  };

  resetStyle = () =>{
    let style = this.getStyle();
    style.layers = [];
    style.sources = {};
    this.setStyle(style);
  }

  getOriginalStyle = () =>{
    let mapstyle = Utils.stripEphemeralGraphicsFromStyle(
      <StyleSpecification>JSON.parse(JSON.stringify(this.getStyle()))
    );
    mapstyle.layers = (mapstyle.layers ?? []).filter(
      (layer) => !Utils.isInternalRuntimeMapLayerId(String(layer.id ?? '')),
    );
    let index = mapstyle.layers.findIndex(layer => layer.id === 'geobox-gl-splash-circle');
    if (index > -1) {
      mapstyle.layers.splice(index, 1);
    }
    index = mapstyle.layers.findIndex(layer => layer.id === 'geobox-gl-base-map');
    if (index > -1) {
      mapstyle.layers.splice(index, 1);
    }
    index = mapstyle.layers.findIndex(layer => layer.id === 'geobox-gl-custom-base-map');
    if (index > -1) {
      mapstyle.layers.splice(index, 1);
    }

    for (const layer of mapstyle.layers) {
      let layerObject = this.getLayerObject(layer.id);
      
      if (layerObject && layerObject.source.is_terrain) {
        (<any>layer)["is_terrain"] = true
      }
    }
    
    try {
      delete mapstyle['terrain']
    } catch (error) {
      
    }
    
    delete mapstyle.sources['geobox-gl-base-map'];
    delete mapstyle.sources['geobox-gl-custom-base-map'];
    delete mapstyle.sources['geobox-gl-snap-line'];
    delete mapstyle.sources['geobox-gl-snap-marker'];
    delete mapstyle.sources['geobox-gl-snap-point'];
    delete mapstyle.sources['geobox-gl-snap-polygon'];
    delete mapstyle.sources['geobox-gl-snap-vertex'];
    delete mapstyle.sources['geobox-gl-splash-circle'];
    delete mapstyle.sources['geobox-gl-map-models'];
    // Defense in depth: drop any remaining snap / map-models / vector-basemap layers/sources by prefix.
    mapstyle.layers = (mapstyle.layers ?? []).filter(
      (layer) =>
        !String(layer.id ?? '').startsWith('geobox-gl-snap')
        && !String(layer.id ?? '').startsWith('geobox-gl-map-models')
        && !isVectorBasemapStyleId(String(layer.id ?? '')),
    );
    if (mapstyle.sources) {
      for (const sourceId of Object.keys(mapstyle.sources)) {
        if (
          sourceId.startsWith('geobox-gl-snap')
          || sourceId.startsWith('geobox-gl-map-models')
          || isVectorBasemapStyleId(sourceId)
        ) {
          delete mapstyle.sources[sourceId];
        }
      }
    }
    mapstyle.layers.forEach(layer => {
      if (layer.paint){

        let index = this.originalStyle.layers.findIndex(originalLayer => originalLayer.id === layer.id);
        if (index > -1) {
          if ((<any>this.originalStyle.layers[index])['source-layer-id']){
            (<any>layer)['source-layer-id'] = (<any>this.originalStyle.layers[index])['source-layer-id']
          }
          
        }

        for (const key in layer.paint) {
          if (key === 'fill-opacity' || key === 'fill-outline-color') {
            let value;
            const paintProperty = (<any>layer.paint)[key];
            if (typeof paintProperty !== "boolean" && !paintProperty){
              value = defaults[key];
            }else if (paintProperty && (typeof paintProperty == "string" || typeof paintProperty == "number")){
              value = paintProperty;
            } else if (paintProperty && ['case', 'match'].includes(paintProperty[0]) ) {
              value = paintProperty[3];
            } else if (paintProperty && paintProperty[0] === 'interpolate') {
              let propertyObject: expressionType = <expressionType>expression.parse(paintProperty);
              if (propertyObject?.inputs.length === 1){
                value = paintProperty[4][3];
              }else {
                value = ["interpolate",["linear"]];
                if (propertyObject.isZoom){
                  value.push(["zoom"]);
                }else {
                  value.push(["get", propertyObject.fieldName]);
                }
                for (let index = 0; index < <number>propertyObject?.outputs.length; index++) {
                  propertyObject.outputs[index] = propertyObject?.outputs[index][3];
                  propertyObject.stops[index][1] = propertyObject.stops[index][1][3];
                  value.push(propertyObject.stops[index][0])
                  value.push(propertyObject.stops[index][1])
                }
              }
            }
            (<any>layer.paint)[key] = value;
          }else if (!layer.id.endsWith("$cluster") && ['line-color', 'fill-color', 'fill-extrusion-color', 'circle-color','icon-color'].includes(key)){
            let value;
            const paintProperty = (<any>layer.paint)[key];
            if (typeof paintProperty === "string"){
              value = paintProperty;
            }else if (paintProperty && paintProperty[0] === 'case') {
              // if (typeof paintProperty[5] == "string") {
              //   value =paintProperty[5];
              // }else {
                value = paintProperty[5];
              // }
            } else if (paintProperty[0] === 'interpolate') {
              let colorObject: expressionType = <expressionType>expression.parse(paintProperty);
              if (colorObject?.inputs.length === 1){
                // if (typeof paintProperty[5] == "string") {
                //   value = paintProperty[4][5];
                // }else {
                  value = paintProperty[4][5];
                // }
              }else {
                value = ["interpolate",["linear"]];
                if (colorObject.isZoom){
                  value.push(["zoom"]);
                }else {
                  value.push(["get", colorObject.fieldName]);
                }
                for (let index = 0; index < <number>colorObject?.outputs.length; index++) {
                  colorObject.outputs[index] = colorObject?.outputs[index][5];
                  colorObject.stops[index][1] = colorObject.stops[index][1][5];
                  value.push(colorObject.stops[index][0])
                  value.push(colorObject.stops[index][1])
                }
                // value = colorObject;
              }
            }
            (<any>layer.paint)[key] = value;
          }else if (key === "fill-opacity"){
            let value;
            const paintProperty = (<any>layer.paint)[key];
            if (typeof paintProperty === "string"){
              value = paintProperty;
            }else if (paintProperty[0] === 'case') {
                value = paintProperty[3];
            } else if (paintProperty[0] === 'interpolate') {
              let colorObject: expressionType = <expressionType>expression.parse(paintProperty);
              if (colorObject?.inputs.length === 1){
                value = paintProperty[4][3];
              }else {
                value = ["interpolate",["linear"]];
                if (colorObject.isZoom){
                  value.push(["zoom"]);
                }else {
                  value.push(["get", colorObject.fieldName]);
                }
                for (let index = 0; index < <number>colorObject?.outputs.length; index++) {
                  colorObject.outputs[index] = colorObject?.outputs[index][3];
                  colorObject.stops[index][1] = colorObject.stops[index][1][3];
                  value.push(colorObject.stops[index][0])
                  value.push(colorObject.stops[index][1])
                }
              }
            }
            (<any>layer.paint)[key] = value;
          }
        }
      }
    });
    return mapstyle;
  }

  getLayerStyle = (layerId: string) => {  
    const layer = this.getLayer(layerId);
    if (!layer) {
      return;
      // throw new Error("Layer not found.");
    }
    let type: string = "main";

    if (layer.id.endsWith("$outline")) {type = "outline"; layerId = layerId.slice(0,-8)};
    if (layer.id.endsWith("$label")) {type = "label"; layerId = layerId.slice(0,-6)};
    if (layer.id.endsWith("$symbol")) {type = "symbol"; layerId = layerId.slice(0,-7)};
    if (layer.id.endsWith("$heatmap")) {type = "heatmap"; layerId = layerId.slice(0,-8)};
    if (layer.id.endsWith("$extrusion")) {type = "extrusion"; layerId = layerId.slice(0,-10)};
    if (layer.id.endsWith("$cluster")) {type = "cluster"; layerId = layerId.slice(0,-8)};
    if (layer.id.endsWith("$cluster-count")) {type = "cluster-count"; layerId = layerId.slice(0,-14)};

    const layerStyle:{[key: string]: any} = {
        id: layer.id,
        type: layer.type,
        source: layer.source
    };

    if (layer.sourceLayer) layerStyle['source-Layer'] = layer.sourceLayer
    if (layer.filter)  layerStyle['filter'] = layer.filter
    if (layer.minzoom) layerStyle['minzoom'] = layer.minzoom
    if (layer.maxzoom) layerStyle['maxzoom'] = layer.maxzoom

    let layerObject = this.getLayerObject(layerId);

    const layoutProperties = layout[<layerType>layer.type].layout || [];
    let value, keys;

    layoutProperties.forEach(property => {
      // if (type === "main"){
      //   value = layerObject.getProperty(property);
      //   if (value) {
      //     keys = Object.keys(value);
      //     if (value[keys[0]] !== defaults[<property>property]) {
      //       if (!layerStyle.layout) layerStyle['layout'] = {};
      //       layerStyle.layout[property] = value[keys[0]];
      //     }
      //   }
      // }else {
        value = layerObject._getLayerProperty(property,type);
        if (value) {
          keys = Object.keys(value);
          if (value[keys[0]] !== defaults[<property>property]) {
            if (!layerStyle.layout) layerStyle['layout'] = {};
            // layerStyle.layout[property] = value[keys[0]];

            if (property != "icon-image" && keys.includes('interpolate')){
              layerStyle.layout[property] = [
                "interpolate",
                ["linear"],
                [value[keys[1]]],
                ...value[keys[0]].flat()
              ]
            }else if (property === "icon-image" && keys.includes('interpolate')){
              layerStyle.layout[property] = {
                "stops":[
                ...value[keys[0]]
              ]}
            }else {
              layerStyle.layout[property] = value[keys[0]];
            }
          }
        }
      // }
        
    });

    const paintProperties = layout[<layerType>layer.type].paint || [];
    paintProperties.forEach(property => {
      // if (type === "main"){
      //   value = layerObject.getProperty(property);
      //   if (value) {
      //     keys = Object.keys(value);
      //     if (value[keys[0]] !== defaults[<property>property]) {
      //       if (!layerStyle.paint) layerStyle['paint'] = {};
      //       layerStyle.paint[property] = value[keys[0]];
      //     }
      //   }
      // }else {
        value = layerObject._getLayerProperty(property,type);

        if (value) {
          keys = Object.keys(value);
          if (value[keys[0]] !== defaults[<property>property]) {
            if (!layerStyle.paint) layerStyle['paint'] = {};
            if (keys.includes('interpolate')){
              layerStyle.paint[property] = [
                "interpolate",
                ["linear"],
                [value[keys[1]]],
                ...value[keys[0]].flat()
              ]
            }else {
              layerStyle.paint[property] = value[keys[0]];
            }
            
          }
        }
      // }
    });

    return <LayerSpecification>layerStyle;
  }

  setLayerStyle = (layerId: string, style:layerObj) => {
    const layer = this.getLayer(layerId);
    if (!layer) {
      return;
      // throw new Error("Layer not found.");
    }

    let type: string = "main";

    if (layer.id.endsWith("$outline")) {type = "outline"; layerId = layerId.slice(0,-8)};
    if (layer.id.endsWith("$label")) {type = "label"; layerId = layerId.slice(0,-6)};
    if (layer.id.endsWith("$symbol")) {type = "symbol"; layerId = layerId.slice(0,-7)};
    if (layer.id.endsWith("$heatmap")) {type = "heatmap"; layerId = layerId.slice(0,-8)};
    if (layer.id.endsWith("$extrusion")) {type = "extrusion"; layerId = layerId.slice(0,-10)};
    if (layer.id.endsWith("$cluster")) {type = "cluster"; layerId = layerId.slice(0,-8)};
    if (layer.id.endsWith("$cluster-count")) {type = "cluster-count"; layerId = layerId.slice(0,-14)};

    const layerObject = this.getLayerObject(layerId);

    layerObject.setProperty('minzoom', style.minzoom);
    layerObject.setProperty('maxzoom', style.maxzoom);
    layerObject.setFilter(style.filter);

    for (let key in style.paint) {
      // if (type === "main"){
      //   layerObject.setProperty(key, style.paint[key])
      // }else {
        layerObject._setLayerProperty(key, style.paint[key], type)
      // }
    }

    for (const key in style.layout) {
      // if (type === "main"){
      //   layerObject.setProperty(key, style.layout[key])
      // }else {
        layerObject._setLayerProperty(key, style.layout[key], type)
      // }
    }
  }


  zoomToLayers = async (layerNames?:string[] , options?: FitBoundsOptions) => {
    layerNames = layerNames || []
    options = options || { maxZoom: 22 }

    let minX = 20037509;
    let maxX = -20037509;
    let minY = 20037509;
    let maxY = -20037509;

    if(layerNames.length === 0){
      layerNames = [...Object.keys(this.libreMapFeatureLayers),...Object.keys(this.libreMapRasterLayers)]
    }

 
    for (const layerName of layerNames) {
      let layer = this.getLayerObject(layerName);
      if (layer && layer.source.extent){
        minX = Math.min(minX, Number(layer.source.extent[0]));
        minY = Math.min(minY, Number(layer.source.extent[1]));
        maxX = Math.max(maxX, Number(layer.source.extent[2]));
        maxY = Math.max(maxY, Number(layer.source.extent[3]));
      }

    }

    await this.fitBounds(
      <LngLatBoundsLike>[
        Utils.webMercatorToLngLat(minX, minY),
        Utils.webMercatorToLngLat(maxX, maxY),
      ],
      options
    );

  };

  override loaded() {
    if (!this.style._loaded)
      return false;

      
    if (Object.keys(this.style._updatedSources).length)
        return false;

  
    for (const id in this.libreMapFeatureLayers){
      if (this.getLayer(id) && !this.style.sourceCaches[(<any>this.getLayer(id)).source].loaded())
        return false;
    }

    for (const id in this.libreMapRasterLayers){
      if (this.getLayer(id) && !this.style.sourceCaches[(<any>this.getLayer(id)).source].loaded())
          return false;
    }

    if (!this.style.imageManager.isLoaded())
        return false;
    
    if (this._moving)
      return false

    return true;
  }

  resetBearingAndPitch = () => {
    this.flyTo({
      ...{
        pitch: 0,
        bearing: 0,
      },
      duration: 1000,
      essential: true,
    });
  };


  private _measurementVerticesFromFeature(feature: Feature): MeasurementGeometryVertex[] {
    return verticesFromFeature(feature);
  }

  private _measurementPayloadFromFeature(feature: Feature | null | undefined): MeasurementGeometryPayload {
    if (!feature || !feature.geometry) {
      return { featureId: undefined, geometryType: 'Empty', vertices: [], feature: null };
    }
    const base = payloadFromFeature(feature, '', 'editing');
    return {
      featureId: base.featureId,
      geometryType: base.geometryType,
      vertices: base.vertices,
      feature: base.feature,
    };
  }

  private _getMeasurementLengthUnit(): MeasurementLengthUnit {
    return this._measurementOptions?.lengthUnit ?? 'm';
  }

  private _getMeasurementAreaUnit(): MeasurementAreaUnit {
    return this._measurementOptions?.areaUnit ?? 'm2';
  }

  private _formatMeasurementLength(meters: number): string {
    return formatLengthMeters(meters, this._getMeasurementLengthUnit());
  }

  private _formatMeasurementArea(m2: number): string {
    return formatAreaSquareMeters(m2, this._getMeasurementAreaUnit());
  }

  setMeasurementUnits(units: {
    lengthUnit?: MeasurementLengthUnit;
    areaUnit?: MeasurementAreaUnit;
  }) {
    if (!this._measurementOptions) return;
    if (units.lengthUnit !== undefined) {
      this._measurementOptions.lengthUnit = units.lengthUnit;
    }
    if (units.areaUnit !== undefined) {
      this._measurementOptions.areaUnit = units.areaUnit;
    }
    if (!this._isDrawStoreReady()) return;

    const data = this._safeDrawGetAll();
    for (const feature of data.features) {
      if (feature.geometry?.type === 'LineString' || feature.geometry?.type === 'Polygon') {
        this._updateMeasurements({ features: [feature] });
      }
    }
  }

  /** True when the shared MapboxDraw control is currently on this map. */
  private _hasDrawControl(): boolean {
    try {
      return Boolean(this.drawTools && this.hasControl?.(this.drawTools));
    } catch {
      return false;
    }
  }

  /**
   * Attach drawTools once. MapLibre addControl does not dedupe — a second add
   * double-registers mousemove/mouseout handlers and leaves stale closures with
   * currentMode === null after the next remove/re-add cycle.
   */
  private _attachDrawControl(): void {
    if (!this.drawTools || this._hasDrawControl()) return;
    this.addControl(this.drawTools);
  }

  /**
   * Detach drawTools only when present. MapLibre removeControl still calls
   * onRemove even when the control is not registered; mapbox-gl-draw then throws
   * on null ctx.map and can skip removeEventListeners.
   */
  private _detachDrawControl(): void {
    if (!this.drawTools || !this._hasDrawControl()) return;
    try {
      this.removeControl(this.drawTools);
    } catch {
      /* ignore tear-down races */
    }
  }

  private _isDrawStoreReady(): boolean {
    try {
      if (!this.drawTools || !this._hasDrawControl()) return false;
      // MapboxDraw does not expose `_ctx` on the public API. Probe the store via
      // getAll — after onRemove, store is nulled and this throws.
      this.drawTools.getAll();
      return true;
    } catch {
      return false;
    }
  }

  private _safeDrawGetMode(): string {
    if (!this._isDrawStoreReady()) return '';
    try {
      return String(this.drawTools.getMode?.() ?? '');
    } catch {
      return '';
    }
  }

  private _safeDrawGetAll(): GeoJSON.FeatureCollection {
    if (!this._isDrawStoreReady()) {
      return { type: 'FeatureCollection', features: [] };
    }
    try {
      return (this.drawTools.getAll() as GeoJSON.FeatureCollection) ?? {
        type: 'FeatureCollection',
        features: [],
      };
    } catch {
      return { type: 'FeatureCollection', features: [] };
    }
  }

  private _buildMeasurementGeometryPayload(): MeasurementGeometryPayload {
    if (!this._isDrawStoreReady()) {
      return { featureId: undefined, geometryType: 'Empty', vertices: [], feature: null };
    }
    const mode = this._safeDrawGetMode();
    if (!['draw_line_string', 'draw_polygon', 'draw_point'].includes(mode)) {
      return { featureId: undefined, geometryType: 'Empty', vertices: [], feature: null };
    }
    const data = this._safeDrawGetAll();
    if (!data.features?.length) {
      return { featureId: undefined, geometryType: 'Empty', vertices: [], feature: null };
    }
    const last = data.features[data.features.length - 1] as Feature;
    return this._measurementPayloadFromFeature(last);
  }

  private _clearMeasurementDraftPopups(): void {
    try {
      this._currentPopup?.remove();
      this._currentPopup = undefined;
      for (const popup of this._currentSubPopups) {
        popup.remove();
      }
      this._currentSubPopups = [];
    } catch {
      /* ignore */
    }
  }

  private _scheduleMeasurementPanelUpdate(): void {
    if (this._measurementPanelUpdateTimer != null) return;
    this._measurementPanelUpdateTimer = setTimeout(() => {
      this._measurementPanelUpdateTimer = null;
      if (!this.measurementMode || !this._isDrawStoreReady()) return;

      const measureOpts = this._measurementOptions;
      if (measureOpts?.onGeometryChange) {
        measureOpts.onGeometryChange(this._buildMeasurementGeometryPayload());
      }

      // Refresh coordinate-form vertex list at the same throttled cadence.
      if (this._editGeometryHostOptions?.onGeometryChange && this._isDrawStoreReady()) {
        try {
          this._editGeometryHostOptions.onGeometryChange(
            buildEditGeometryPayload(this.drawTools, this._extractSelectionPointsFromDraw())
          );
        } catch {
          /* ignore */
        }
      }
    }, 50);
  }

  private _emitMeasurementHostUi(e?: MapMouseEvent): void {
    const opts = this._measurementOptions;
    if (!this.measurementMode || !opts || !this._isDrawStoreReady()) return;
    const mode = this._safeDrawGetMode();
    if (!['draw_line_string', 'draw_polygon', 'draw_point'].includes(mode)) return;

    if (e && opts.onCursorMove) {
      opts.onCursorMove({ lng: e.lngLat.lng, lat: e.lngLat.lat, drawMode: mode });
    }

    if (opts.onGeometryChange) {
      opts.onGeometryChange(this._buildMeasurementGeometryPayload());
    }
  }

  setMeasurementVertexLngLat(
    vertexIndex: number,
    lng: number,
    lat: number,
    opts?: { featureId?: string; polygonRingIndex?: number }
  ): boolean {
    return this.setEditGeometryVertexLngLat(vertexIndex, lng, lat, opts);
  }

  enableEditGeometryHostUi(options?: EditGeometryOptions): void {
    this._editGeometryHostOptions = options;
    if (!this._editGeometryHostUiBound) {
      this._editGeometryHostUiBound = true;
      this.on('draw.create', this._onEditGeometryHostDrawEvent);
      this.on('draw.update', this._onEditGeometryHostDrawEvent);
      this.on('draw.selectionchange', this._onEditGeometryHostSelectionChange);
      this.on('draw.modechange', this._onEditGeometryHostDrawEvent);
    }
    this._emitEditGeometryHostUi();
  }

  disableEditGeometryHostUi(): void {
    this._editGeometryHostOptions = undefined;
    this._lastEditGeometrySelectionPoints = [];
  }

  refreshEditGeometryHostUi(): void {
    this._emitEditGeometryHostUi();
  }

  private _computeSnapToleranceMeters(pixels: number): number {
    if (!Number.isFinite(pixels) || pixels <= 0) return 0;
    const lat = this.getCenter().lat;
    const metersPerPixel =
      (Math.cos((lat * Math.PI) / 180.0) * 2 * Math.PI * 6378137) /
      (256 * 2 ** this.getZoom());
    const meters = pixels * metersPerPixel;
    return meters < 99999 ? meters : 99999;
  }

  private _refreshSnapToleranceFromPixels(): void {
    const drawTools: any = this.drawTools;
    if (!drawTools?.options || !Number.isFinite(this._snapTolerancePixels)) return;
    drawTools.options.snapTolerance = this._computeSnapToleranceMeters(
      this._snapTolerancePixels
    );
    drawTools.options.snapTolerancePixels = this._snapTolerancePixels;
    try {
      this.fire('draw.snap.options_changed', {
        snap: drawTools.options.snap,
        snapMode: drawTools.options.snapMode,
        snapTolerance: drawTools.options.snapTolerance,
        snapTolerancePixels: drawTools.options.snapTolerancePixels,
        currentVertexPosition: 0,
        rectangle: null,
      });
    } catch {
      /* ignore */
    }
  }

  private _ensureRuntimeOverlayLayers(): void {
    if (!this.isStyleLoaded()) return;

    const emptyPoint = (): GeoJSON.FeatureCollection => ({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: { type: 'Point', coordinates: [] },
        },
      ],
    });
    const emptyLine = (): GeoJSON.FeatureCollection => ({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: [] },
        },
      ],
    });
    const emptyPolygon = (): GeoJSON.FeatureCollection => ({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: { type: 'Polygon', coordinates: [] },
        },
      ],
    });

    try {
      if (!this.getSource('geobox-gl-splash-circle')) {
        this.addSource('geobox-gl-splash-circle', {
          type: 'geojson',
          data: emptyPoint(),
        });
      }
      if (!this.getLayer('geobox-gl-splash-circle')) {
        this.addLayer({
          id: 'geobox-gl-splash-circle',
          type: 'symbol',
          source: 'geobox-gl-splash-circle',
          layout: {
            'icon-image': 'pulsing-dot',
            'icon-allow-overlap': true,
          },
        });
      }

      if (!this.getSource('geobox-gl-snap-vertex')) {
        this.addSource('geobox-gl-snap-vertex', {
          type: 'geojson',
          data: emptyPoint(),
        });
      }
      if (!this.getSource('geobox-gl-snap-point')) {
        this.addSource('geobox-gl-snap-point', {
          type: 'geojson',
          maxzoom: 24,
          tolerance: 0,
          data: emptyPoint(),
        });
      }
      if (!this.getSource('geobox-gl-snap-line')) {
        this.addSource('geobox-gl-snap-line', {
          type: 'geojson',
          maxzoom: 24,
          tolerance: 0,
          data: emptyLine(),
        });
      }
      if (!this.getSource('geobox-gl-snap-polygon')) {
        this.addSource('geobox-gl-snap-polygon', {
          type: 'geojson',
          maxzoom: 24,
          tolerance: 0,
          data: emptyPolygon(),
        });
      }
      if (!this.getSource('geobox-gl-snap-marker')) {
        this.addSource('geobox-gl-snap-marker', {
          type: 'geojson',
          maxzoom: 24,
          tolerance: 0,
          data: emptyPoint(),
        });
      }

      this._snapTolerancePixels =
        this.mapSettings.snap_settings.snap_tolerance ?? 5;
      this._ensureSnapMarkerLayer();
    } catch {
      /* style may be mid-swap — caller can retry on idle */
    }
  }

  private _ensureSnapMarkerLayer(): void {
    if (!this.getSource('geobox-gl-snap-marker')) return;
    try {
      if (!this.getLayer('geobox-gl-snap-marker')) {
        this.addLayer({
          id: 'geobox-gl-snap-marker',
          type: 'circle',
          source: 'geobox-gl-snap-marker',
          paint: {
            // Only vertex locks are shown; always solid red.
            'circle-color': 'rgba(255, 0, 0, 1)',
            'circle-stroke-color': 'rgba(180, 0, 0, 1)',
            'circle-stroke-width': 2,
            'circle-radius': 6,
          },
        });
      } else {
        // Keep paint in sync if an older style left expressions behind.
        this.setPaintProperty(
          'geobox-gl-snap-marker',
          'circle-color',
          'rgba(255, 0, 0, 1)'
        );
        this.setPaintProperty(
          'geobox-gl-snap-marker',
          'circle-stroke-color',
          'rgba(180, 0, 0, 1)'
        );
        this.setPaintProperty('geobox-gl-snap-marker', 'circle-radius', 6);
      }
      this._snapMarkerLayerAdded = true;
    } catch {
      /* style may not be ready yet */
    }
  }

  private _readSnapMarkerLngLat(): { lng: number; lat: number } | null {
    try {
      const src = this.getSource('geobox-gl-snap-marker') as any;
      const coords = src?._data?.features?.[0]?.geometry?.coordinates;
      if (!Array.isArray(coords) || coords.length < 2) return null;
      const lng = Number(coords[0]);
      const lat = Number(coords[1]);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
      return { lng, lat };
    } catch {
      return null;
    }
  }

  private _clearSnapMarker(): void {
    try {
      const src = this.getSource('geobox-gl-snap-marker') as any;
      const data = src?._data;
      if (!data) return;
      data.features = [
        {
          type: 'Feature',
          properties: {},
          geometry: { type: 'Point', coordinates: [] },
        },
      ];
      src.setData(data);
    } catch {
      /* ignore */
    }
  }

  private _isSnapCursorLocked(e?: MapMouseEvent): boolean {
    const drawOpts: any = this.drawTools?.options;
    if (!drawOpts?.snap || !e) return false;
    try {
      const src = this.getSource('geobox-gl-snap-marker') as any;
      const props = src?._data?.features?.[0]?.properties;
      if (props?.locked === true) return true;
    } catch {
      /* fall through */
    }
    const marker = this._readSnapMarkerLngLat();
    if (!marker) return false;
    try {
      const cursorPx = this.project([e.lngLat.lng, e.lngLat.lat]);
      const markerPx = this.project([marker.lng, marker.lat]);
      const dx = cursorPx.x - markerPx.x;
      const dy = cursorPx.y - markerPx.y;
      // Locked when marker is pulled away from the raw cursor by > ~0.75 px.
      return Math.sqrt(dx * dx + dy * dy) > 0.75;
    } catch {
      return false;
    }
  }

  private _resolveEditGeometryCursor(
    e: MapMouseEvent
  ): EditGeometryCursorPayload {
    const mode = this._safeDrawGetMode() || String(this.drawTools?.getMode?.() ?? '');
    const rawLng = e.lngLat.lng;
    const rawLat = e.lngLat.lat;
    const drawOpts: any = this.drawTools?.options;
    const marker = drawOpts?.snap ? this._readSnapMarkerLngLat() : null;
    const snapLocked = this._isSnapCursorLocked(e);
    if (snapLocked && marker) {
      return {
        lng: marker.lng,
        lat: marker.lat,
        drawMode: mode,
        rawLng,
        rawLat,
        snapLocked: true,
      };
    }
    return { lng: rawLng, lat: rawLat, drawMode: mode, snapLocked: false };
  }

  private _isSnapDrawSessionActive(): boolean {
    return Boolean(this.drawMode || this.measurementMode);
  }

  scheduleUpdateSnapLayers(): void {
    const drawOpts: any = this.drawTools?.options;
    if (!this._isSnapDrawSessionActive() || !drawOpts?.snap) return;
    if (this._snapLayersUpdateTimer != null) {
      clearTimeout(this._snapLayersUpdateTimer);
    }
    this._snapLayersUpdateTimer = setTimeout(() => {
      this._snapLayersUpdateTimer = null;
      const liveOpts: any = this.drawTools?.options;
      if (!this._isSnapDrawSessionActive() || !liveOpts?.snap) return;
      void this._updateSnapLayers();
    }, 300);
  }

  setSnapOptions(opts: {
    enabled?: boolean;
    mode?: 'both' | 'edge' | 'vertex';
    tolerance?: number;
    tolerancePixels?: number;
    snappableLayers?: 'ALL' | string[];
  }): void {
    const drawTools: any = this.drawTools;
    if (!drawTools?.options) return;

    if (typeof opts.enabled === 'boolean') {
      drawTools.options.snap = opts.enabled;
      if (!opts.enabled) {
        this.clearSnapRuntimeState();
      }
    }
    if (typeof opts.mode === 'string') {
      drawTools.options.snapMode = opts.mode;
    }
    if (typeof opts.tolerancePixels === 'number' && Number.isFinite(opts.tolerancePixels)) {
      this._snapTolerancePixels = opts.tolerancePixels;
      this.mapSettings.snap_settings.snap_tolerance = opts.tolerancePixels;
      drawTools.options.snapTolerancePixels = opts.tolerancePixels;
      drawTools.options.snapTolerance = this._computeSnapToleranceMeters(
        opts.tolerancePixels
      );
    } else if (typeof opts.tolerance === 'number' && Number.isFinite(opts.tolerance)) {
      drawTools.options.snapTolerance = opts.tolerance;
    }
    if (opts.snappableLayers !== undefined) {
      this.mapSettings.snap_settings.snappable_layers = opts.snappableLayers;
    }

    try {
      this.fire('draw.snap.options_changed', {
        snap: drawTools.options.snap,
        snapMode: drawTools.options.snapMode,
        snapTolerance: drawTools.options.snapTolerance,
        snapTolerancePixels: drawTools.options.snapTolerancePixels,
        currentVertexPosition: 0,
        rectangle: null,
      });
    } catch {
      /* ignore: not in draw context */
    }

    this._ensureSnapMarkerLayer();

    if (drawTools.options.snap) {
      this._bindSnapMapListeners();
      this.scheduleUpdateSnapLayers();
    } else {
      this._unbindSnapMapListeners();
    }
  }

  private _bindSnapMapListeners(): void {
    if (this._snapMapListenersBound) return;
    this.on('moveend', this._onSnapMoveEnd);
    this.on('zoom', this._onSnapZoom);
    this.on('zoomend', this._onSnapZoomEnd);
    this._snapMapListenersBound = true;
  }

  private _unbindSnapMapListeners(): void {
    if (!this._snapMapListenersBound) return;
    try {
      this.off('moveend', this._onSnapMoveEnd);
      this.off('zoom', this._onSnapZoom);
      this.off('zoomend', this._onSnapZoomEnd);
    } catch {
      /* ignore */
    }
    this._snapMapListenersBound = false;
  }

  clearSnapRuntimeState(): void {
    this._unbindSnapMapListeners();
    if (this._snapLayersUpdateTimer != null) {
      clearTimeout(this._snapLayersUpdateTimer);
      this._snapLayersUpdateTimer = null;
    }
    this._snapLayersRequestId += 1;
    try {
      this._clearSnapMarker();
    } catch {
      /* ignore */
    }
    this._applySnapLayerData({
      pointList: [],
      lineList: [],
      polygonList: [],
      vertexList: [],
    });
  }

  getSnapOptions(): {
    enabled: boolean;
    mode: 'both' | 'edge' | 'vertex';
    tolerance: number;
    tolerancePixels: number;
    snappableLayers: 'ALL' | string[];
  } {
    const o: any = this.drawTools?.options ?? {};
    const snappable = this.mapSettings?.snap_settings?.snappable_layers;
    return {
      enabled: Boolean(o.snap),
      mode: (o.snapMode as 'both' | 'edge' | 'vertex') ?? 'both',
      tolerance: typeof o.snapTolerance === 'number' ? o.snapTolerance : 0,
      tolerancePixels: this._snapTolerancePixels,
      snappableLayers:
        snappable === 'ALL' || Array.isArray(snappable) ? snappable : 'ALL',
    };
  }

  private static readonly RECT_FORM_HALF_SIZE_DEG = 0.0005;

  private _simulateRectangleDraw(
    lng1: number,
    lat1: number,
    lng2: number,
    lat2: number
  ): boolean {
    const fire = (type: string, lng: number, lat: number) => {
      const lngLat = { lng, lat };
      const point = this.project([lng, lat]);
      this.fire(type, {
        lngLat,
        point,
        originalEvent: new MouseEvent(type),
      } as MapMouseEvent);
    };

    fire('mousemove', lng1, lat1);
    fire('mousedown', lng1, lat1);
    fire('mousemove', lng2, lat2);
    fire('mouseup', lng2, lat2);
    return true;
  }

  /**
   * Move the in-progress sketch to lng/lat without committing.
   * Point draw: writes coordinates and renders the placed point, but does not
   * fire draw.create — Finish / پایان still ends the session.
   */
  previewDrawVertexAt(lng: number, lat: number): boolean {
    if (!this.drawTools) return false;
    const mode = String(this.drawTools.getMode());
    if (!isSketchLikeDrawMode(mode)) return false;
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return false;

    try {
      if (mode === 'draw_point' || mode === 'passing_mode_point') {
        const tools = this.drawTools as any;
        const currentMode = tools._ctx?.events?.currentMode;
        const state = currentMode?.state ?? currentMode;
        const live = state?.point ?? currentMode?.point;
        if (live) {
          if (typeof live.incomingCoords === 'function') {
            live.incomingCoords([lng, lat]);
          } else if (typeof live.updateCoordinate === 'function') {
            live.updateCoordinate('', lng, lat);
          }
        }
        if (state) {
          state.snappedLng = lng;
          state.snappedLat = lat;
        }

        const store = tools._ctx?.store;
        const id = live?.id;
        const stored = id != null ? store?.get?.(id) : undefined;
        if (stored && typeof stored.incomingCoords === 'function') {
          stored.incomingCoords([lng, lat]);
        } else if (id != null) {
          tools.add?.({
            type: 'Feature',
            id,
            properties: { ...(live?.properties ?? {}), active: 'true' },
            geometry: { type: 'Point', coordinates: [lng, lat] },
          });
        } else {
          const all = tools.getAll?.()?.features ?? [];
          const existing = [...all]
            .reverse()
            .find((f: { geometry?: { type?: string } }) => f?.geometry?.type === 'Point');
          if (existing?.id != null) {
            tools.add?.({
              ...existing,
              geometry: { type: 'Point', coordinates: [lng, lat] },
            });
          }
        }

        store?.featureChanged?.(id);
        store?.render?.();
        this.triggerRepaint?.();
        this._emitEditGeometryHostUi();
        setTimeout(() => this._emitEditGeometryHostUi(), 0);
        return true;
      }

      const lngLat = { lng, lat };
      const point = this.project([lng, lat]);
      this.fire('mousemove', {
        lngLat,
        point,
        originalEvent: new MouseEvent('mousemove'),
      } as MapMouseEvent);
      this._emitEditGeometryHostUi();
      return true;
    } catch {
      return false;
    }
  }

  placeDrawVertexAt(lng: number, lat: number): boolean {
    if (!this.drawTools) {
      return false;
    }
    const mode = String(this.drawTools.getMode());
    const supportedMode = isSketchLikeDrawMode(mode);
    if (!supportedMode) return false;
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      return false;
    }

    try {
      if (mode === 'draw_rectangle') {
        const d = Map.RECT_FORM_HALF_SIZE_DEG;
        this._simulateRectangleDraw(lng - d, lat - d, lng + d, lat + d);
      } else {
        const lngLat = { lng, lat };
        const point = this.project([lng, lat]);
        const moveEvent = {
          lngLat,
          point,
          originalEvent: new MouseEvent('mousemove'),
        } as MapMouseEvent;
        const clickEvent = {
          lngLat,
          point,
          originalEvent: new MouseEvent('click'),
        } as MapMouseEvent;

        // Keep snap state in sync before click commit.
        this.fire('mousemove', moveEvent);
        this.fire('mousedown', {
          lngLat,
          point,
          originalEvent: new MouseEvent('mousedown'),
        } as MapMouseEvent);
        this.fire('mouseup', {
          lngLat,
          point,
          originalEvent: new MouseEvent('mouseup'),
        } as MapMouseEvent);
        this.fire('click', clickEvent);
      }
    } catch {
      return false;
    }
    this._emitEditGeometryHostUi();
    // Some draw state updates are emitted async (e.g. draw.create/modechange).
    // Emit again on next tick so host UIs read the committed sketch.
    setTimeout(() => {
      this._emitEditGeometryHostUi();
    }, 0);
    return true;
  }

  finishDrawSketch(lng?: number, lat?: number): boolean {
    if (!this.drawTools) return false;
    const mode = String(this.drawTools.getMode?.() ?? '');
    if (!isSketchLikeDrawMode(mode)) return false;

    const center = this.getCenter();
    const useLng = Number.isFinite(lng) ? (lng as number) : center.lng;
    const useLat = Number.isFinite(lat) ? (lat as number) : center.lat;

    try {
      if (mode === 'draw_point' || mode === 'passing_mode_point') {
        const placed = this.placeDrawVertexAt(useLng, useLat);
        this._setCanvasCursor('grab');
        return placed;
      }

      if (mode === 'draw_circle') {
        for (let i = 0; i < 2; i++) {
          if (String(this.drawTools.getMode?.() ?? '') !== mode) break;
          this.placeDrawVertexAt(useLng, useLat);
        }
        this._setCanvasCursor('grab');
        this._emitEditGeometryHostUi();
        setTimeout(() => this._emitEditGeometryHostUi(), 0);
        return true;
      }

      if (mode === 'draw_rectangle') {
        const d = Map.RECT_FORM_HALF_SIZE_DEG;
        this._simulateRectangleDraw(
          useLng - d,
          useLat - d,
          useLng + d,
          useLat + d
        );
        this._setCanvasCursor('grab');
        this._emitEditGeometryHostUi();
        setTimeout(() => this._emitEditGeometryHostUi(), 0);
        return true;
      }

      // Line / polygon / freehand / bezier: Enter commits the sketch (onStop → draw.create).
      this._simulateDrawEnterKey();

      const all = this.drawTools.getAll?.();
      const last = all?.features?.length
        ? all.features[all.features.length - 1]
        : undefined;
      const lastId =
        last?.id !== undefined && last?.id !== null ? String(last.id) : undefined;

      if (String(this.drawTools.getMode?.() ?? '') === mode) {
        if (lastId) {
          this.drawTools.changeMode('simple_select', { featureIds: [lastId] });
        } else {
          this.drawTools.changeMode('simple_select');
        }
      }

      this._setCanvasCursor('grab');
    } catch {
      return false;
    }

    this._emitEditGeometryHostUi();
    setTimeout(() => {
      this._emitEditGeometryHostUi();
    }, 0);
    return true;
  }

  private _simulateDrawEnterKey(): void {
    const container = this.getCanvasContainer?.();
    if (!container) return;
    container.dispatchEvent(
      new KeyboardEvent('keyup', {
        key: 'Enter',
        code: 'Enter',
        keyCode: 13,
        which: 13,
        bubbles: true,
      })
    );
  }

  getSelectedFeatureIds(): string[] {
    if (!this.drawMode || !this.drawTools?.getSelected) return [];
    try {
      const mode = this.drawTools.getMode?.();
      if (!mode) return [];
      const selected = this.drawTools.getSelected()?.features ?? [];
      return selected
        .map((f: any) => f?.id)
        .filter((id: any) => id !== undefined && id !== null)
        .map((id: any) => String(id));
    } catch {
      return [];
    }
  }

  editSelectedNodes(): boolean {
    const drawTools = this.drawTools;
    if (!drawTools) return false;
    this._ensureDrawFeaturePropertyIds();
    const selectedIds = this.getSelectedFeatureIds();
    let targetId: string | number | undefined = selectedIds[0];
    if (targetId == null) {
      const all = drawTools.getAll?.()?.features ?? [];
      targetId = all[0]?.id;
    }
    if (targetId == null || String(targetId).trim() === '') return false;
    const resolved = this._resolveDrawFeatureById(String(targetId));
    const featureId =
      resolved?.id != null ? String(resolved.id) : String(targetId);
    if (!this._resolveDrawFeatureById(featureId)) return false;
    try {
      drawTools.changeMode(drawTools.modes.DIRECT_SELECT, { featureId });
      this._syncCursorFromDrawMode();
      this._emitEditGeometryHostUi();
      setTimeout(() => this._emitEditGeometryHostUi(), 0);
      setTimeout(() => this._emitEditGeometryHostUi(), 48);
      return true;
    } catch {
      return false;
    }
  }

  selectedPolygonsOverlap(features?: any[]): boolean {
    const drawTools = this.drawTools;
    let selected = features;
    if (!selected) {
      try {
        selected = drawTools?.getSelected?.()?.features ?? [];
      } catch {
        return false;
      }
    }
    return this._selectedPolygonsOverlap(selected);
  }

  private _selectedPolygonsOverlap(features: any[]): boolean {
    if (!Array.isArray(features) || features.length < 2) return false;
    for (let i = 0; i < features.length; i += 1) {
      for (let j = i + 1; j < features.length; j += 1) {
        try {
          if (booleanOverlap(features[i], features[j])) return true;
          if (
            booleanContains(features[i], features[j]) ||
            booleanContains(features[j], features[i])
          ) {
            return true;
          }
        } catch {
          /* ignore invalid ring pairs */
        }
      }
    }
    return false;
  }

  combineSelectedFeatures(): boolean {
    const drawTools = this.drawTools;
    if (!drawTools || !this.drawMode) return false;
    let selected: any[] = [];
    try {
      selected = drawTools.getSelected?.()?.features ?? [];
    } catch {
      return false;
    }
    if (selected.length < 2) return false;
    if (!selected.every((f: any) => f?.geometry?.type === 'Polygon')) return false;

    const countBefore = drawTools.getAll?.()?.features?.length ?? 0;
    try {
      // Overlapping rings must be unioned — a naive MultiPolygon is invalid on the server.
      if (this._selectedPolygonsOverlap(selected)) {
        if (!this._unionSelectedPolygons(selected, drawTools)) return false;
        this._emitEditGeometryHostUi();
        return true;
      }

      let combined = false;
      if (typeof drawTools.combineFeatures === 'function') {
        drawTools.combineFeatures();
        const countAfter = drawTools.getAll?.()?.features?.length ?? 0;
        combined = countAfter === countBefore - selected.length + 1;
      }
      if (!combined) {
        combined = this._mergeSelectedPolygonsManually(selected, drawTools);
      }
      if (!combined) return false;
      this._emitEditGeometryHostUi();
      return true;
    } catch {
      try {
        if (this._selectedPolygonsOverlap(selected)) {
          if (this._unionSelectedPolygons(selected, drawTools)) {
            this._emitEditGeometryHostUi();
            return true;
          }
        } else if (this._mergeSelectedPolygonsManually(selected, drawTools)) {
          this._emitEditGeometryHostUi();
          return true;
        }
      } catch {
        /* ignore */
      }
      return false;
    }
  }

  uncombineSelectedFeatures(): boolean {
    const drawTools = this.drawTools;
    if (!drawTools || !this.drawMode) return false;
    let selected: any[] = [];
    try {
      selected = drawTools.getSelected?.()?.features ?? [];
    } catch {
      return false;
    }
    const multiPolygons = selected.filter(
      (f: any) => f?.geometry?.type === 'MultiPolygon'
    );
    if (!multiPolygons.length) return false;

    const countBefore = drawTools.getAll?.()?.features?.length ?? 0;
    try {
      // Prefer manual split so each part gets a distinct draw id (native uncombine
      // reuses the same properties/__tempFeatureId and breaks list sync).
      let uncombined = this._splitSelectedMultiPolygonsManually(multiPolygons, drawTools);
      if (!uncombined && typeof drawTools.uncombineFeatures === 'function') {
        drawTools.uncombineFeatures();
        const countAfter = drawTools.getAll?.()?.features?.length ?? 0;
        uncombined = countAfter > countBefore;
      }
      if (!uncombined) return false;
      this._emitEditGeometryHostUi();
      return true;
    } catch {
      try {
        if (this._splitSelectedMultiPolygonsManually(multiPolygons, drawTools)) {
          this._emitEditGeometryHostUi();
          return true;
        }
      } catch {
        /* ignore */
      }
      return false;
    }
  }

  private _mergeSelectedPolygonsManually(selected: any[], drawTools: any): boolean {
    const coordinates = selected
      .map((f: any) => f?.geometry?.coordinates)
      .filter((c: unknown) => Array.isArray(c));
    if (coordinates.length < 2) return false;

    const properties = { ...(selected[0]?.properties ?? {}) };
    const mergedTempId =
      properties.__tempFeatureId != null
        ? String(properties.__tempFeatureId)
        : selected[0]?.id != null
          ? String(selected[0].id)
          : undefined;
    if (mergedTempId) {
      properties.__tempFeatureId = mergedTempId;
    }
    const ids = selected
      .map((f: any) => f?.id)
      .filter((id: unknown) => id !== undefined && id !== null)
      .map((id: unknown) => String(id));
    if (ids.length < 2) return false;

    drawTools.delete(ids);
    const newId = drawTools.add({
      type: 'Feature',
      properties,
      geometry: {
        type: 'MultiPolygon',
        coordinates,
      },
    });
    if (newId == null) return false;

    try {
      drawTools.changeMode('simple_select', { featureIds: [String(newId)] });
    } catch {
      drawTools.changeMode?.('simple_select');
    }
    return true;
  }

  private _splitSelectedMultiPolygonsManually(multiPolygons: any[], drawTools: any): boolean {
    const countBefore = drawTools.getAll?.()?.features?.length ?? 0;
    const idsToDelete: string[] = [];
    const polygonsToAdd: any[] = [];

    for (const feature of multiPolygons) {
      const coords = feature?.geometry?.coordinates;
      if (!Array.isArray(coords) || coords.length <= 1) continue;

      idsToDelete.push(String(feature.id));
      const baseProps = { ...(feature.properties ?? {}) };
      delete baseProps.__tempFeatureId;

      for (const polygonCoords of coords) {
        polygonsToAdd.push({
          type: 'Feature',
          properties: { ...baseProps },
          geometry: {
            type: 'Polygon',
            coordinates: polygonCoords,
          },
        });
      }
    }

    if (!idsToDelete.length || polygonsToAdd.length <= idsToDelete.length) {
      return false;
    }

    for (const id of idsToDelete) {
      drawTools.delete(id);
    }
    const newIds: string[] = [];
    for (const poly of polygonsToAdd) {
      const newId = drawTools.add(poly);
      if (newId != null) newIds.push(String(newId));
    }
    if (!newIds.length) return false;

    try {
      drawTools.changeMode('simple_select', { featureIds: newIds });
    } catch {
      drawTools.changeMode?.('simple_select');
    }

    const countAfter = drawTools.getAll?.()?.features?.length ?? 0;
    return countAfter > countBefore;
  }

  rotateScaleSelectedFeatures(): boolean {
    return this._enterTransformMode({ canRotate: true, canScale: true });
  }

  rotateSelectedFeatures(): boolean {
    return this._enterTransformMode({ canRotate: true, canScale: false });
  }

  scaleSelectedFeatures(): boolean {
    return this._enterTransformMode({ canRotate: false, canScale: true });
  }

  enterFeatureTransformHandles(featureId?: string): boolean {
    const drawTools = this.drawTools;
    if (!drawTools || !this.drawMode) return false;

    const candidateId = featureId ?? this._resolveTransformFeatureId();
    if (!candidateId) return false;

    return this._enterTransformModeForFeature(candidateId, {
      canRotate: true,
      canScale: true,
    });
  }

  private _resolveTransformFeatureId(): string | undefined {
    const drawTools = this.drawTools;
    if (!drawTools) return undefined;

    const all = drawTools.getAll?.()?.features ?? [];
    const isNonPoint = (f: any) => {
      const t = f?.geometry?.type;
      return t && t !== 'Point' && t !== 'MultiPoint';
    };

    const drawSelectedIds = this.getSelectedFeatureIds();
    if (drawSelectedIds.length) {
      const sel = all.find((f: any) => String(f.id) === drawSelectedIds[0] && isNonPoint(f));
      if (sel) return String(sel.id);
    }

    const first = all.find(isNonPoint);
    return first ? String(first.id) : undefined;
  }

  private _enterTransformModeForFeature(
    featureId: string,
    options: { canRotate: boolean; canScale: boolean }
  ): boolean {
    const drawTools = this.drawTools;
    if (!drawTools) return false;
    try {
      drawTools.changeMode('scaleRotateMode', {
        featureId,
        canRotate: options.canRotate,
        canScale: options.canScale,
        useBboxHandles: true,
        canSelectFeatures: false,
        singleRotationPoint: true,
        hideUnselectedFeatures: true,
      });
      this._syncCursorFromDrawMode();
      this._emitEditGeometryHostUi();
      return true;
    } catch (err) {
      try {
        console.warn('[geobox-gl] transform mode failed', err);
      } catch {
        /* ignore */
      }
      return false;
    }
  }

  enterFeatureTransformMode(
    featureId: string,
    options: { canRotate: boolean; canScale: boolean }
  ): boolean {
    if (!featureId) return false;
    return this._enterTransformModeForFeature(featureId, options);
  }

  private _enterTransformMode(options: { canRotate: boolean; canScale: boolean }): boolean {
    const candidateId = this._resolveTransformFeatureId();
    if (!candidateId) return false;
    return this._enterTransformModeForFeature(candidateId, options);
  }

  splitSelectedPolygon(): boolean {
    const drawTools = this.drawTools;
    if (!drawTools) return false;
    try {
      drawTools.changeMode('splitPolygonMode');
      this._emitEditGeometryHostUi();
      return true;
    } catch {
      return false;
    }
  }

  enablePartialUpdate(options?: { allowSingleIntersection?: boolean }): boolean {
    const drawTools = this.drawTools;
    if (!drawTools) return false;

    const selected = drawTools.getSelected?.()?.features ?? [];
    if (selected.length !== 1) return false;

    const target = selected[0];
    const type = String(target?.geometry?.type ?? '');
    if (type !== 'LineString' && type !== 'Polygon') return false;

    try {
      drawTools.changeMode('partial_update', {
        featureId: String(target.id),
        allowSingleIntersection: options?.allowSingleIntersection === true,
      });
      this._emitEditGeometryHostUi();
      return true;
    } catch {
      return false;
    }
  }

  private _featureAsGeoJson(feature: any): any | null {
    if (!feature) return null;
    try {
      if (typeof feature.toGeoJSON === 'function') {
        return feature.toGeoJSON();
      }
    } catch {
      /* ignore */
    }
    if (feature.geometry?.type) return feature;
    return null;
  }

  private _isPolygonHoleTarget(feature: any): boolean {
    const geo = this._featureAsGeoJson(feature);
    const type = String(geo?.geometry?.type ?? '');
    if (type === 'Polygon') return true;
    if (type === 'MultiPolygon') {
      const coords = geo?.geometry?.coordinates;
      return Array.isArray(coords) && coords.length === 1;
    }
    return false;
  }

  private _resolveDrawFeatureById(featureId: string): any | null {
    const drawTools = this.drawTools;
    if (!drawTools) return null;
    const id = String(featureId);
    try {
      const fromGet = drawTools.get?.(id);
      const asGeo = this._featureAsGeoJson(fromGet);
      if (asGeo) return asGeo;
    } catch {
      /* ignore */
    }
    try {
      const all = drawTools.getAll?.()?.features ?? [];
      const match = all.find(
        (f: any) =>
          String(f.id) === id ||
          String(f.properties?.__tempFeatureId ?? '') === id
      );
      return this._featureAsGeoJson(match) ?? match ?? null;
    } catch {
      return null;
    }
  }

  enableCreateHole(
    featureId?: string,
    drawSubtype?: 'polygon' | 'rectangle' | 'circle' | 'freehand'
  ): boolean {
    const drawTools = this.drawTools;
    if (!drawTools || !this.drawMode) return false;

    let targetId =
      featureId != null && String(featureId).trim() !== ''
        ? String(featureId)
        : undefined;

    if (!targetId) {
      const selected = drawTools.getSelected?.()?.features ?? [];
      if (selected.length !== 1) return false;
      targetId = String(selected[0].id);
    }

    const target = this._resolveDrawFeatureById(targetId);
    if (!this._isPolygonHoleTarget(target)) return false;

    try {
      drawTools.changeMode('create_hole', {
        featureId: targetId,
        drawSubtype: drawSubtype ?? 'polygon',
      });
      this._syncCursorFromDrawMode();
      this._emitEditGeometryHostUi();
      return true;
    } catch {
      return false;
    }
  }

  splitSelectedFeatures(): boolean {
    const drawTools = this.drawTools;
    if (!drawTools || !this.drawMode) return false;

    const selected = drawTools.getSelected?.()?.features ?? [];
    if (selected.length < 1) return false;

    const types = new Set<string>(
      selected.map((f: any) => String(f?.geometry?.type ?? ''))
    );

    const isLine = types.has('LineString') || types.has('MultiLineString');
    const isPolygon = types.has('Polygon') || types.has('MultiPolygon');
    if (!isLine && !isPolygon) return false;

    try {
      if (isLine && !isPolygon) {
        drawTools.changeMode('splitLineMode');
      } else {
        drawTools.changeMode('splitPolygonMode');
      }
      this._syncCursorFromDrawMode();
      this._emitEditGeometryHostUi();
      return true;
    } catch {
      return false;
    }
  }

  dissolveSelectedPolygons(): boolean {
    const drawTools = this.drawTools;
    if (!drawTools || !this.drawMode) return false;
    let selected: any[] = [];
    try {
      selected = drawTools.getSelected?.()?.features ?? [];
    } catch {
      return false;
    }
    if (selected.length < 2) return false;
    if (!selected.every((f: any) => f?.geometry?.type === 'Polygon')) return false;
    try {
      const dissolved = this._unionSelectedPolygons(selected, drawTools);
      if (!dissolved) return false;
      this._emitEditGeometryHostUi();
      return true;
    } catch {
      return false;
    }
  }

  private _unionSelectedPolygons(selected: any[], drawTools: any): boolean {
    let merged: any = JSON.parse(JSON.stringify(selected[0]));
    if (!merged?.geometry) return false;

    for (let i = 1; i < selected.length; i += 1) {
      const next: any = turfUnion(merged, selected[i]);
      if (!next?.geometry) return false;
      const type = String(next.geometry.type ?? '');
      if (type !== 'Polygon' && type !== 'MultiPolygon') return false;
      merged = next;
    }

    if (!merged?.geometry) return false;

    const properties = { ...(selected[0]?.properties ?? {}) };
    const mergedTempId =
      properties.__tempFeatureId != null
        ? String(properties.__tempFeatureId)
        : selected[0]?.id != null
          ? String(selected[0].id)
          : undefined;
    if (mergedTempId) {
      properties.__tempFeatureId = mergedTempId;
    }

    const ids = selected
      .map((f: any) => f?.id)
      .filter((id: unknown) => id !== undefined && id !== null)
      .map((id: unknown) => String(id));
    if (ids.length < 2) return false;

    drawTools.delete(ids);
    const newId = drawTools.add({
      type: 'Feature',
      properties,
      geometry: JSON.parse(JSON.stringify(merged.geometry)),
    });
    if (newId == null) return false;

    try {
      drawTools.changeMode('simple_select', { featureIds: [String(newId)] });
    } catch {
      drawTools.changeMode?.('simple_select');
    }
    return true;
  }

  ensureDrawFeaturePropertyIds(): void {
    this._ensureDrawFeaturePropertyIds();
  }

  private _ensureDrawFeaturePropertyIds(): void {
    const drawTools = this.drawTools;
    if (!drawTools?.getAll || !drawTools.set) return;
    try {
      const all = drawTools.getAll()?.features ?? [];
      if (!all.length) return;
      let changed = false;
      const next = all.map((f: any) => {
        const topId = f?.id != null ? String(f.id) : '';
        if (!topId) return f;
        const props = { ...(f.properties ?? {}) };
        if (String(props.id ?? '') === topId) return f;
        changed = true;
        return {
          ...f,
          properties: {
            ...props,
            id: topId,
            __tempFeatureId: props.__tempFeatureId ?? topId,
          },
        };
      });
      if (changed) {
        drawTools.set({ type: 'FeatureCollection', features: next });
      }
    } catch {
      /* ignore */
    }
  }

  private _onEditGeometryHostDrawEvent = () => {
    this._ensureDrawFeaturePropertyIds();
    this._emitEditGeometryHostUi();
  };

  private _extractSelectionPointsFromDraw(): Position[] {
    const drawTools: any = this.drawTools;
    if (!drawTools) return [];
    const mode = String(drawTools.getMode?.() ?? '');
    if (mode !== 'direct_select') return [];

    try {
      const currentMode = drawTools._ctx?.events?.currentMode;
      if (!currentMode) return [];

      const state = currentMode.state ?? currentMode;
      const paths = state.selectedCoordPaths;
      if (
        Array.isArray(paths) &&
        paths.length > 0 &&
        typeof currentMode.pathsToCoordinates === 'function'
      ) {
        const featureId =
          paths[0]?.feature_id ?? state.featureId ?? state.feature?.id;
        const raw = currentMode.pathsToCoordinates(featureId, paths);
        if (Array.isArray(raw)) {
          return raw
            .filter((c: unknown) => Array.isArray(c) && c.length >= 2)
            .map((c: number[]) => [Number(c[0]), Number(c[1])] as Position)
            .filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat));
        }
      }
    } catch {
      /* ignore draw internals */
    }
    return [];
  }

  private _onEditGeometryHostSelectionChange = (e: { points?: unknown }) => {
    try {
      const fromEvent = positionsFromSelectionEvent(e?.points);
      this._lastEditGeometrySelectionPoints =
        fromEvent.length > 0 ? fromEvent : this._extractSelectionPointsFromDraw();
      this._emitEditGeometryHostUi();
    } catch {
      this._lastEditGeometrySelectionPoints = this._extractSelectionPointsFromDraw();
      try {
        this._emitEditGeometryHostUi();
      } catch {
        /* ignore host UI errors during selection change */
      }
    }
  };

  private _shouldEmitEditGeometryHostUi(): boolean {
    if (!this._editGeometryHostOptions || !this.drawTools) return false;
    if (!this.drawMode && !this.measurementMode) return false;
    const mode = String(this.drawTools.getMode());
    return (
      isDrawSketchMode(mode) ||
      isEditSelectMode(mode) ||
      mode === 'scaleRotateMode'
    );
  }

  private _emitEditGeometryHostUi(e?: MapMouseEvent): void {
    const opts = this._editGeometryHostOptions;
    if (!opts || !this._shouldEmitEditGeometryHostUi()) return;

    const mode = String(this.drawTools.getMode());

    if (
      e &&
      opts.onCursorMove &&
      (isDrawSketchMode(mode) || isEditSelectMode(mode))
    ) {
      opts.onCursorMove(this._resolveEditGeometryCursor(e));
    }

    if (opts.onGeometryChange) {
      const selectionPoints =
        this._lastEditGeometrySelectionPoints.length > 0
          ? this._lastEditGeometrySelectionPoints
          : this._extractSelectionPointsFromDraw();
      opts.onGeometryChange(
        buildEditGeometryPayload(this.drawTools, selectionPoints)
      );
    }
  }

  setEditGeometryVertexLngLat(
    vertexIndex: number,
    lng: number,
    lat: number,
    opts?: { featureId?: string; polygonRingIndex?: number }
  ): boolean {
    if ((!this.drawMode && !this.measurementMode) || !this.drawTools) return false;

    const all = this.drawTools.getAll();
    if (!all.features?.length) return false;

    let raw = opts?.featureId
      ? all.features.find((f) => String(f.id) === String(opts.featureId))
      : all.features[all.features.length - 1];

    // Some edit flows expose source feature id in host payload while drawTools uses
    // an internal id. Fall back to current draw selection / active sketch feature.
    if (!raw) {
      const selected = this.drawTools.getSelected?.()?.features;
      if (selected?.length) {
        raw = selected[0];
      } else if (all.features.length === 1) {
        raw = all.features[0];
      } else {
        raw = all.features[all.features.length - 1];
      }
    }

    if (!raw?.geometry) return false;

    const updated = applyWgs84VertexToDrawFeature(
      raw as Feature,
      vertexIndex,
      lng,
      lat,
      { polygonRingIndex: opts?.polygonRingIndex }
    );
    if (!updated) return false;

    try {
      this.drawTools.add(updated);
      const mode = String(this.drawTools.getMode?.() ?? '');
      if (mode === 'direct_select' && updated.id != null) {
        this.drawTools.changeMode('direct_select', {
          featureId: String(updated.id),
        });
      }
    } catch {
      const collection = this.drawTools.getAll();
      const features = collection.features.map((f) =>
        String(f.id) === String(updated.id) ? updated : f
      );
      this.drawTools.set({ type: 'FeatureCollection', features });
    }

    if (this.measurementMode) {
      this._updateMeasurements({ features: [updated] });
    } else {
      this._emitEditGeometryHostUi();
      setTimeout(() => this._emitEditGeometryHostUi(), 0);
    }
    return true;
  }

  deleteEditGeometryVertex(
    vertexIndex: number,
    opts?: { featureId?: string; polygonRingIndex?: number }
  ): boolean {
    if ((!this.drawMode && !this.measurementMode) || !this.drawTools) return false;

    const all = this.drawTools.getAll();
    if (!all.features?.length) return false;

    let raw = opts?.featureId
      ? all.features.find((f) => String(f.id) === String(opts.featureId))
      : all.features[all.features.length - 1];

    if (!raw) {
      const selected = this.drawTools.getSelected?.()?.features;
      if (selected?.length) {
        raw = selected[0];
      } else if (all.features.length === 1) {
        raw = all.features[0];
      } else {
        raw = all.features[all.features.length - 1];
      }
    }

    if (!raw?.geometry) return false;

    const updated = removeWgs84VertexFromDrawFeature(
      raw as Feature,
      vertexIndex,
      { polygonRingIndex: opts?.polygonRingIndex }
    );
    if (!updated) return false;

    try {
      this.drawTools.add(updated);
    } catch {
      const collection = this.drawTools.getAll();
      const features = collection.features.map((f) =>
        String(f.id) === String(updated.id) ? updated : f
      );
      this.drawTools.set({ type: 'FeatureCollection', features });
    }

    try {
      this.fire('draw.update', {
        action: 'change_coordinates',
        features: [updated],
      });
    } catch {
      /* ignore */
    }

    // After drawTools.add the mode may reset to simple_select.
    // Defer re-entry so selection/mode handlers finish first.
    if (updated.id != null) {
      const featureId = String(updated.id);
      setTimeout(() => {
        try {
          this.drawTools?.changeMode('direct_select', { featureId });
        } catch {
          /* ignore */
        }
      }, 0);
    }

    if (this.measurementMode) {
      this._updateMeasurements({ features: [updated] });
    } else {
      this._emitEditGeometryHostUi();
      setTimeout(() => this._emitEditGeometryHostUi(), 0);
    }
    return true;
  }

  enableMeasurement = async (options?: MeasurementOptions) => {
    if (this.loaded()) {

      if (this.selectByLocationMode) {
        throw new Error("Select By LocationMode mode is active!");
      }

      if (this.drawMode) {
       throw new Error("Draw mode is active!");
      }

      if (this.measurementMode) {
        throw new Error("Measurement mode is active!");
      }

      // Drop any leftover draw control (sketch resume, aborted session) without
      // calling removeControl when already detached (avoids null-ctx onRemove).
      try {
        this.off('draw.create', this._updateMeasurements);
        this.off('draw.update', this._updateMeasurements);
      } catch {
        /* ignore */
      }
      try {
        this.deleteMeasurements();
      } catch {
        /* ignore */
      }
      try {
        this.clearSnapRuntimeState();
      } catch {
        /* ignore */
      }
      this._detachDrawControl();

      this._measurementOptions = options;

      // Clear any pending panel update from a previous session before re-attaching draw.
      if (this._measurementPanelUpdateTimer != null) {
        clearTimeout(this._measurementPanelUpdateTimer);
        this._measurementPanelUpdateTimer = null;
      }
      this._measurementDraftPopupsCleared = false;

      // Attach draw first so getAll/getMode never run against a torn-down store.
      this._attachDrawControl();
      this.measurementMode = true;

      if (options?.initialMode) {
        try {
          this.drawTools.changeMode(options.initialMode as string);
        } catch {
          /* mode may need a tick; host also retries */
        }
      }

      this.scheduleUpdateSnapLayers();
      
      this._hideDrawToolbar();

      this.on('draw.create', this._updateMeasurements);
      this.on('draw.update', this._updateMeasurements);
      this._syncCursorFromDrawMode();
      //draw.changeMode('draw_line_string')
      //draw.changeMode('draw_polygon')
      return this.drawTools;
    }
    return new Promise((resolve) => {
      setTimeout(async () => {
        const result = await this.enableMeasurement(options);
        resolve(result);
      }, 100);
    });
  }

  _updateMeasurements(e: any) { 
    const feature = e.features[0];
    if (!feature?.geometry) {
      if (this.measurementMode && this._measurementOptions?.onGeometryChange) {
        this._measurementOptions.onGeometryChange(
          this._measurementPayloadFromFeature(null)
        );
      }
      if (this._editGeometryHostOptions) {
        this._emitEditGeometryHostUi();
      }
      return;
    }

    const hostOwnsResultPopups = this._measurementOptions?.showResultPopups === false;

    // Always clear draft cursor popups when a measurement feature is committed/updated.
    try {
      this._currentPopup?.remove();
      for (const popup of this._currentSubPopups) {
        popup.remove();
      }
      this._currentSubPopups = [];
      this._currentPopup = undefined;
    } catch {
      /* ignore */
    }

    if (!hostOwnsResultPopups) {
      let innerHTML;
      if (feature.geometry.type === 'Polygon') {
        const areaM2 = Utils.polygonAreaPostGIS(feature.geometry.coordinates);
        const measurementText = this._formatMeasurementArea(areaM2);

        innerHTML = `<div style="margin: 5px;" ><span>Total area: <strong class="geobox-gl-draw-value-span">${measurementText}</strong></span>
        <button id="geobox-gl-draw-${feature.id}" class="geobox-gl-close-btn" title="Close" style="position: absolute; top:2px; margin: 0 0 0 12px; padding: 0;font-size: 20px;">×</button>
        </div>`

      } else if (feature.geometry.type === 'LineString') {
        const lengthM = Utils.lineLengthPostGIS(feature.geometry.coordinates);
        const measurementText = this._formatMeasurementLength(lengthM);
       
        innerHTML = `<div style="margin: 5px;" ><span >Total length: <strong class="geobox-gl-draw-value-span">${measurementText}</strong></span>
        <button id="geobox-gl-draw-${feature.id}" class="geobox-gl-close-btn" title="Close" style="position: absolute; top:2px; margin: 0 0 0 12px; padding: 0;font-size: 20px;">×</button>
        </div>`

      }

      try {
        const target = this._popups.find(p => p.id === feature.id);
        if (target) {
          target.popup?.remove();

          for (const subPopup of target.subPopups){
            subPopup.remove();
          }

          const index = this._popups.indexOf(target);
          if (index > -1) { 
            this._popups.splice(index, 1); 
          }

        }
      } catch (error) {
      }

      let popup;
      if (feature.geometry.type === 'Polygon') {
        popup = new Popup({ closeOnClick: false, closeButton: false })
          .setLngLat({lng: feature.geometry.coordinates[0][feature.geometry.coordinates[0].length - 2][0], lat: feature.geometry.coordinates[0][feature.geometry.coordinates[0].length - 2][1]})
          .setHTML(<string>innerHTML)
          .addTo(this);

        this._popups.push({ id: feature.id, popup, subPopups: [] });
      } else if (feature.geometry.type === 'LineString') {
        popup = new Popup({ closeOnClick: false, closeButton: false })
        .setLngLat({lng: feature.geometry.coordinates[feature.geometry.coordinates.length - 1][0], lat: feature.geometry.coordinates[feature.geometry.coordinates.length - 1][1]})
        .setHTML(<string>innerHTML)
        .addTo(this);

        let subPopups = []
        const coords = feature.geometry.coordinates;
        for (let i = 0; i < coords.length - 2; i++) {

          if (i === 0){
            let segmentLengthHTML = 
              `<div style="margin: 5px;" ><span>Start point</span></div>`;

            let subPopup = new Popup({ closeOnClick: false, closeButton: false })
            .setLngLat({lng: coords[i][0], lat: coords[i][1]})
            .setHTML(<string>segmentLengthHTML)
            .addTo(this);
            
            subPopups.push(subPopup)
          }

          try {
            const segment = {
              type: "Feature",
              geometry: {
                type: "LineString",
                coordinates: [coords[i], coords[i + 1]]
              }
            };
            const segmentLengthM = Utils.lineLengthPostGIS(segment.geometry.coordinates);
            const segmentLengthText = this._formatMeasurementLength(segmentLengthM);

            let segmentLengthHTML = 
              `<div style="margin: 5px;" ><span><strong class="geobox-gl-draw-value-span">${segmentLengthText}</strong></span></div>`;


            let subPopup = new Popup({ closeOnClick: false, closeButton: false })
            .setLngLat({lng: coords[i + 1][0], lat: coords[i + 1][1]})
            .setHTML(<string>segmentLengthHTML)
            .addTo(this);
            
            subPopups.push(subPopup)
            
          } catch (error) {
            
          }
          
        }
        this._popups.push({ id: feature.id, popup, subPopups: subPopups });
      }

      const popupCloseBtn = document.getElementById(`geobox-gl-draw-${feature.id}`);

      if (popupCloseBtn && popupCloseBtn.parentElement instanceof HTMLElement) {
        popupCloseBtn.addEventListener('click', () => {

          try {
            const target = this._popups.find(p => p.id === feature.id);
            if (target) {
              target.popup?.remove();

              for (const subPopup of target.subPopups){
                subPopup.remove();
              }

              const index = this._popups.indexOf(target);
              if (index > -1) { 
                this._popups.splice(index, 1); 
              }

            }
            this.drawTools.delete(feature.id);

          } catch (error) {
          }
        })
      }
    }

    if (this.measurementMode && this._measurementOptions?.onGeometryChange) {
      this._measurementOptions.onGeometryChange(this._measurementPayloadFromFeature(feature as Feature));
    }

    if (this._editGeometryHostOptions) {
      this._emitEditGeometryHostUi();
    }

  }

  deleteMeasurements(){
    try {
      for (const target of this._popups){
        target.popup?.remove();

        for (const subPopup of target.subPopups){
          subPopup.remove();
        }
      }

      this._currentPopup?.remove();
      for (let popup of this._currentSubPopups){
        popup.remove();
      }

      this._currentSubPopups = [];
      this._currentPopup = undefined;
      this._popups = [];

      if (!this._isDrawStoreReady()) return;

      // Delete features BEFORE leaving the active draw tool. Otherwise onStop runs on
      // the draft, may nest changeMode(simple_select) / fire draw.create, and a later
      // deleteAll can be skipped when that throws — leaving geometry that breaks
      // length↔area mode switches until the user hits Delete.
      try {
        this.drawTools.deleteAll();
      } catch {
        /* ignore */
      }
      try {
        if (this._safeDrawGetMode() !== 'simple_select') {
          this.drawTools.changeMode('simple_select');
        }
      } catch {
        /* ignore */
      }
      try {
        this.drawTools.deleteAll();
      } catch {
        /* ignore */
      }
    } catch (error) {
      
    }
  }

  disableMeasurement(){
    try {
      this.measurementMode = false;
      this._measurementOptions = undefined;
      this._measurementDraftPopupsCleared = false;
      if (this._measurementPanelUpdateTimer != null) {
        clearTimeout(this._measurementPanelUpdateTimer);
        this._measurementPanelUpdateTimer = null;
      }
      this.off('draw.create', this._updateMeasurements);
      this.off('draw.update', this._updateMeasurements);
      try {
        this.deleteMeasurements();
      } catch {
        /* draw store may already be torn down */
      }
      try {
        this.clearSnapRuntimeState();
      } catch {
        /* ignore */
      }
      this._detachDrawControl();
      this._clearCanvasCursor();
    } catch (error) {
      
    }
  }


  enableDraw = async () => {


    if (this.loaded()) {

      if (this.selectByLocationMode) {
        console.error("Select By LocationMode mode is active!", "error");
        return;
      }

      if (this.measurementMode) {
        return;
      }

      if (this.drawMode) {
        return this.drawTools;
      }

      // try {
      //   this.disableMeasurement();
      // } catch (error) {
      // } 

      this.drawMode = true;

      this._attachDrawControl();
      this.scheduleUpdateSnapLayers();
      this._hideDrawToolbar();

      // this.on('draw.update', this._updateMeasurements);

      // this.drawTools.changeMode('draw_polygon');
      return this.drawTools;
    }
    return new Promise((resolve) => {
      setTimeout(async () => {
        const result = await this.enableDraw();
        resolve(result);
      }, 100);
    });
  }



  disableDraw() {
    const tools = this.drawTools;
    if (!tools) {
      this.drawMode = false;
      return;
    }

    try {
      if (this.drawMode) {
        try {
          const mode = tools.getMode?.();
          if (mode && mode !== 'simple_select') {
            tools.changeMode('simple_select');
          }
        } catch {
          /* draw internal state may already be torn down */
        }
      }
      try {
        tools.deleteAll();
      } catch {
        /* ignore */
      }
    } finally {
      this.drawMode = false;
      try {
        this.clearSnapRuntimeState();
      } catch {
        /* ignore */
      }
      this._detachDrawControl();
    }
  }

  private _hideDrawToolbar() {
    try {
      const drawTools = document.getElementsByClassName('mapbox-gl-draw_line')[0];
      if (drawTools && drawTools.parentElement instanceof HTMLElement) {
        drawTools.parentElement.style.display = 'none';
      }

      const snapTools = document.getElementsByClassName('mapbox-gl-draw_line')[0];
      if (snapTools && snapTools.parentElement instanceof HTMLElement) {
        snapTools.parentElement.style.display = 'none';
      }
    } catch {
      /* ignore */
    }
  }

  private _resolveFeatureLayerKey(renderedLayerId: string): string | null {
    const featureLayers = this.getFeatureLayers();
    if (featureLayers[renderedLayerId]) {
      return renderedLayerId;
    }

    const suffixes = [
      '$label',
      '$symbol',
      '$outline',
      '$extrusion',
      '$heatmap',
      '$cluster',
    ];
    for (const suffix of suffixes) {
      if (!renderedLayerId.endsWith(suffix)) continue;
      const base = renderedLayerId.slice(0, -suffix.length);
      if (featureLayers[base]) return base;
    }

    return null;
  }

  private _resolveSelectByLocationQueryLayerIds(layerIDs: string[]): string[] {
    if (!layerIDs.length) return [];

    const featureLayers = this.getFeatureLayers();
    const valid: string[] = [];

    for (const id of layerIDs) {
      if (featureLayers[id] || this.getLayer(id)) {
        valid.push(id);
      }
    }

    return valid;
  }

  private _expandLayerIdsForQuery(layerIDs: string[]): string[] {
    const expanded = new Set<string>();
    const suffixes = [
      '$label',
      '$symbol',
      '$outline',
      '$extrusion',
      '$heatmap',
      '$cluster',
    ];

    for (const id of layerIDs) {
      expanded.add(id);
      for (const suffix of suffixes) {
        const sub = `${id}${suffix}`;
        if (this.getLayer(sub)) expanded.add(sub);
      }
    }

    return [...expanded];
  }

  private _applyLegacySelectByLocation(
    layerIDs: string[],
    filterPolygon: Feature,
    accumulate = false,
  ) {
    const bbox = turfBbox(filterPolygon);
    let selectedFeatures: MapGeoJSONFeature[];
    const queryLayerIDs = this._resolveSelectByLocationQueryLayerIds(layerIDs);

    if (queryLayerIDs.length > 0) {
      selectedFeatures = this.queryRenderedFeatures(
        [this.project([bbox[0], bbox[1]]), this.project([bbox[2], bbox[3]])],
        {
          layers: this._expandLayerIdsForQuery(queryLayerIDs),
        },
      );
    } else if (layerIDs.length === 0) {
      selectedFeatures = this.queryRenderedFeatures([
        this.project([bbox[0], bbox[1]]),
        this.project([bbox[2], bbox[3]]),
      ]);
    } else {
      selectedFeatures = [];
    }

    selectedFeatures = selectedFeatures.filter(
      (data) =>
        data.layer.id != 'geobox-gl-splash-circle'
        && !isVectorBasemapStyleId(String(data.layer.id ?? '')),
    );

    selectedFeatures = selectedFeatures.filter(
      (data) => data.layer.id != 'geobox-gl-pois',
    );

    selectedFeatures = selectedFeatures.filter(
      (data) => !data.layer.id.includes('gl-draw-polygon'),
    );

    const filteredFeatures = selectedFeatures.filter((f) =>
      this._isWithinOrIntersectingSelectByLocation(filterPolygon, f),
    );

    const featureLayers = this.getFeatureLayers();

    if (!accumulate) {
      for (const key in featureLayers) {
        try {
          featureLayers[key].clearSelection();
        } catch {
          /* ignore */
        }
      }
      this.selectedFeatures = [];
    }

    const existingKeys = new Set(
      (this.selectedFeatures ?? []).map((f) => `${f.layer.id}:${f.id}`),
    );

    for (const feature of filteredFeatures) {
      const key = `${feature.layer.id}:${feature.id}`;
      if (existingKeys.has(key)) continue;
      existingKeys.add(key);
      try {
        const layerKey =
          this._resolveFeatureLayerKey(String(feature.layer.id)) ??
          String(feature.layer.id);
        featureLayers[layerKey]?.addToSelecion?.(<number>feature.id);
      } catch {
        /* ignore */
      }
      if (!this.selectedFeatures) {
        this.selectedFeatures = [];
      }
      this.selectedFeatures.push(feature);
    }
  }

  private _isWithinOrIntersectingSelectByLocation(filterPolygon: Feature, feature: any) {
    if (!feature?.geometry?.type) return false;

    if (feature.geometry.type === 'Point') {
      return booleanPointInPolygon(
        feature as Feature<import('geojson').Point>,
        filterPolygon as Feature<import('geojson').Polygon | import('geojson').MultiPolygon>,
      );
    }

    if (feature.geometry.type === 'LineString') {
      return (
        !booleanDisjoint(filterPolygon, feature)
        || booleanContains(filterPolygon, feature)
      );
    }

    if (feature.geometry.type === 'MultiPolygon') {
      const polygons = feature.geometry.coordinates.map((coords: any) => ({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: coords,
        },
      }));

      return polygons.some(
        (polygon: any) =>
          booleanOverlap(filterPolygon, polygon) ||
          booleanContains(filterPolygon, polygon),
      );
    }
    if (feature.geometry.type === 'MultiLineString') {
      const lineStrings = feature.geometry.coordinates.map((coords: any) => ({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: coords,
        },
      }));

      return lineStrings.some(
        (lineString: any) =>
          !booleanDisjoint(filterPolygon, lineString)
          || booleanContains(filterPolygon, lineString),
      );
    }

    return (
      booleanContains(filterPolygon, feature)
      || booleanOverlap(filterPolygon, feature)
      || !booleanDisjoint(filterPolygon, feature)
    );
  }

  disableSelectByLocation() {
    const onSessionEnd = this._selectByLocationOptions?.onSessionEnd;
    try {
      this.selectByLocationMode = false;
      this._selectByLocationOptions = undefined;
      this._selectByLocationLayerIds = [];
      if (this._selectByLocationPostDrawRaf) {
        cancelAnimationFrame(this._selectByLocationPostDrawRaf);
        this._selectByLocationPostDrawRaf = 0;
      }
      this.off('draw.create', this._selectByLocationOnDrawCreate);
      try {
        this.drawTools?.changeMode?.('simple_select');
      } catch {
        /* ignore */
      }
      this.drawTools?.deleteAll?.();
      this._detachDrawControl();
    } catch {
      /* ignore */
    }
    onSessionEnd?.();
  }

  updateSelectByLocationDrawMode = (mode: SelectByLocationDrawMode) => {
    if (!this.selectByLocationMode || !this._selectByLocationOptions) return;
    this._selectByLocationOptions = {
      ...this._selectByLocationOptions,
      drawMode: mode,
    };
    try {
      this.drawTools?.changeMode?.(mode as string);
    } catch {
      /* ignore */
    }
  };

  private _selectByLocationSelectionSnapshot() {
    return (this.selectedFeatures ?? []).map((f) => ({
      id: f.id as number | string,
      layer: {
        id:
          this._resolveFeatureLayerKey(String(f.layer?.id ?? '')) ??
          String(f.layer?.id ?? ''),
      },
    }));
  }

  private _isSelectByDrawShapeReady(feature: Feature | undefined): feature is Feature {
    if (!feature?.geometry) return false;

    const geom = feature.geometry as Geometry;
    if (geom.type === 'Polygon') {
      const coords = geom.coordinates as unknown;
      const ring = Array.isArray(coords) ? (coords as unknown[])[0] : null;
      return Array.isArray(ring) && ring.length >= 4;
    }

    if (geom.type === 'MultiPolygon') {
      const coords = geom.coordinates as unknown;
      if (!Array.isArray(coords)) return false;
      return (coords as unknown[]).some((polygon) => {
        if (!Array.isArray(polygon)) return false;
        const ring = (polygon as unknown[])[0];
        return Array.isArray(ring) && ring.length >= 4;
      });
    }

    if (geom.type === 'LineString') {
      const coords = geom.coordinates as unknown;
      return Array.isArray(coords) && coords.length >= 2;
    }

    if (geom.type === 'Point') {
      const coords = geom.coordinates as unknown;
      return Array.isArray(coords) && coords.length >= 2;
    }

    return false;
  }

  private _resolveSelectByDrawFeature(e?: { features?: Feature[] }): Feature | undefined {
    const eventFeatures = e?.features;
    const fromEvent =
      Array.isArray(eventFeatures) && eventFeatures.length
        ? eventFeatures[eventFeatures.length - 1]
        : undefined;
    if (this._isSelectByDrawShapeReady(fromEvent)) {
      return fromEvent;
    }

    const data = this.drawTools?.getAll?.();
    const fromAll =
      data?.features?.length
        ? data.features[data.features.length - 1]
        : undefined;
    if (this._isSelectByDrawShapeReady(fromAll)) {
      return fromAll;
    }

    return undefined;
  }

  private _toSelectByLocationFilterPolygon(
    input: Feature | Geometry,
  ): Feature | null {
    const feature: Feature =
      (input as Feature).type === 'Feature'
        ? (input as Feature)
        : { type: 'Feature', properties: {}, geometry: input as Geometry };

    const geom = feature.geometry;
    if (!geom) return null;

    if (geom.type === 'Polygon') {
      const ring = geom.coordinates?.[0];
      return Array.isArray(ring) && ring.length >= 4 ? feature : null;
    }

    if (geom.type === 'MultiPolygon') {
      const valid = geom.coordinates?.some(
        (polygon) => Array.isArray(polygon?.[0]) && polygon[0].length >= 4,
      );
      return valid ? feature : null;
    }

    if (geom.type === 'Point') {
      return turfBuffer(feature, 0.015, { units: 'kilometers' }) ?? null;
    }

    if (geom.type === 'LineString') {
      return turfBuffer(feature, 0.008, { units: 'kilometers' }) ?? null;
    }

    return null;
  }

  selectFeaturesByLocationGeometry = (
    layerIDs: string[] = [],
    geometry: Feature | Geometry,
    options?: SelectByLocationGeometryOptions,
  ) => {
    if (this.measurementMode) {
      throw new Error('Measurement mode is active!');
    }

    if (this.drawMode) {
      throw new Error('Draw mode is active!');
    }

    if (this.selectByLocationMode) {
      this.disableSelectByLocation();
    }

    const filterPolygon = this._toSelectByLocationFilterPolygon(geometry);
    if (!filterPolygon) return;

    this._applyLegacySelectByLocation(
      layerIDs,
      filterPolygon,
      options?.accumulate ?? false,
    );
    options?.onSelectionChange?.(this._selectByLocationSelectionSnapshot());
  };

  selectByLocation = (layerIDs: string[] = [], options?: SelectByLocationOptions) => {
    if (this.measurementMode) {
      throw new Error('Measurement mode is active!');
    }

    if (this.drawMode) {
      throw new Error('Draw mode is active!');
    }

    if (this.selectByLocationMode) {
      this.disableSelectByLocation();
    }

    this._detachDrawControl();

    this.selectByLocationMode = true;
    this._selectByLocationLayerIds =
      this._resolveSelectByLocationQueryLayerIds(layerIDs);
    this._selectByLocationOptions = options;

    this._attachDrawControl();
    this._hideDrawToolbar();

    const drawMode = options?.drawMode ?? 'draw_polygon';
    this.drawTools.changeMode(drawMode as string);
    this.on('draw.create', this._selectByLocationOnDrawCreate);
  };

  private _scheduleSelectByLocationPostDraw(
    filterPolygon: Feature,
    featureId: string | null,
  ) {
    if (this._selectByLocationPostDrawRaf) {
      cancelAnimationFrame(this._selectByLocationPostDrawRaf);
    }

    // Wait two frames so gl-draw can finish mode transitions (rectangle/circle/polygon).
    this._selectByLocationPostDrawRaf = requestAnimationFrame(() => {
      this._selectByLocationPostDrawRaf = requestAnimationFrame(() => {
        this._selectByLocationPostDrawRaf = 0;
        this._commitSelectByLocationPostDraw(filterPolygon, featureId);
      });
    });
  }

  private _commitSelectByLocationPostDraw(
    filterPolygon: Feature,
    featureId: string | null,
  ) {
    if (!this.selectByLocationMode) return;

    const opts = this._selectByLocationOptions;
    const multiDraw = opts?.multiDraw ?? false;

    if (opts?.onDrawComplete) {
      opts.onDrawComplete(filterPolygon);
    } else {
      this._applyLegacySelectByLocation(
        this._selectByLocationLayerIds,
        filterPolygon,
        multiDraw,
      );
      opts?.onSelectionChange?.(this._selectByLocationSelectionSnapshot());
    }

    const clearDraw = opts?.clearDrawAfterSelect ?? multiDraw;
    if (clearDraw) {
      this._cleanupSelectByDrawSketch(featureId);
    }

    if (multiDraw) {
      this._restartSelectByLocationDrawMode();
      return;
    }

    this.disableSelectByLocation();
  }

  private _cleanupSelectByDrawSketch(featureId: string | null) {
    const draw = this.drawTools;
    if (!draw) return;

    let deleteId = featureId;
    if (!deleteId) {
      const all = draw.getAll?.();
      const last = all?.features?.length
        ? all.features[all.features.length - 1]
        : undefined;
      deleteId = last?.id != null ? String(last.id) : null;
    }
    if (!deleteId) return;

    try {
      const mode = String(draw.getMode?.() ?? '');
      if (mode !== 'simple_select') {
        draw.changeMode('simple_select');
      }
      draw.delete(deleteId);
    } catch {
      /* ignore */
    }
  }

  private _restartSelectByLocationDrawMode() {
    if (!this.selectByLocationMode) return;
    const mode = this._selectByLocationOptions?.drawMode ?? 'draw_polygon';
    try {
      this.drawTools?.changeMode?.(mode as string);
    } catch {
      /* ignore */
    }
  }



  addScaleBar =() =>{
    if (!this.scaleBar){
      this.scaleBar = new ScaleBar({map: this});
      this.addControl(this.scaleBar, "bottom-left");
    }
    // this._scaleControl = new maplibregl.ScaleControl({
    //   position: "bottom-right",
    //   maxWidth: 100,
    //   unit: "meter",
    // });

    // this.addControl(this._scaleControl);
  }

  removeScaleBar =() =>{
    if (this.scaleBar){
      this.removeControl(this.scaleBar);
      this.scaleBar = undefined;
    }
  }

  enableHostScaleBar(options: ScaleBarHostOptions): void {
    this.disableHostScaleBar();
    const maxWidth = options.maxWidth;
    const unit = options.unit;
    const handler = () => {
      const snap = computeScaleBarSnapshot(this, { maxWidth, unit });
      options.onUpdate(snap);
    };
    this._hostScaleBarMove = handler;
    this.on('move', handler);
    handler();
  }

  enableHostMiniMap(options: MiniMapHostOptions): void {
    this.disableHostMiniMap();
    const server = this.server;
    this._hostMiniMap = createMiniMapHost(this, {
      ...options,
      transformRequest:
        options.transformRequest ??
        ((url) => {
          if (
            server._apikey &&
            url.indexOf('apikey') === -1
          ) {
            if (url.indexOf('?') > -1) {
              return { url: `${url}&apikey=${server._apikey}` };
            }
            return { url: `${url}?apikey=${server._apikey}` };
          }
          if (server._access_token) {
            return {
              url,
              headers: { Authorization: `Bearer ${server._access_token}` },
            };
          }
        }),
    });
  }

  disableHostMiniMap(): void {
    destroyMiniMapHost(this, this._hostMiniMap);
    this._hostMiniMap = undefined;
  }

  resizeHostMiniMap(): void {
    resizeMiniMapHost(this._hostMiniMap);
  }

  disableHostScaleBar(): void {
    if (this._hostScaleBarMove) {
      this.off('move', this._hostScaleBarMove);
      this._hostScaleBarMove = undefined;
    }
  }


  private _layerMatchesSnapSettings(layerKey: string, featureLayer?: any): boolean {
    const snappable = this.mapSettings?.snap_settings?.snappable_layers;
    if (!snappable || snappable === 'None') return false;
    if (snappable === 'ALL') return true;
    if (!Array.isArray(snappable)) return false;
    if (snappable.includes(layerKey)) return true;
    const aliases = [
      featureLayer?.layerId,
      featureLayer?.sourceLayer,
      featureLayer?.source?.name,
    ].filter((v): v is string => typeof v === 'string' && v.length > 0);
    return aliases.some((alias) => snappable.includes(alias));
  }

  private _isMapLayerVisible(layerKey: string): boolean {
    for (const layer of this.getStyle().layers) {
      if (layer.id !== layerKey) continue;
      if (layer.layout && layer.layout.visibility === 'none') return false;
      const mapZoomLevel = this.getZoom();
      if (layer.minzoom != null && layer.minzoom > mapZoomLevel) return false;
      if (layer.maxzoom != null && layer.maxzoom < mapZoomLevel) return false;
      return true;
    }
    return true;
  }

  private _pushSnapVerticesFromRings(
    feature: Feature,
    rings: Position[][],
    vertexList: Feature[],
    snapCache: number
  ): void {
    for (const ring of rings) {
      if (vertexList.length >= snapCache) return;
      if (!Array.isArray(ring) || ring.length === 0) continue;
      const coords = ring.filter(
        (c) =>
          Array.isArray(c) &&
          c.length >= 2 &&
          Number.isFinite(c[0]) &&
          Number.isFinite(c[1])
      );
      if (coords.length === 0) continue;
      vertexList.push({
        type: 'Feature',
        properties: { ...(feature.properties ?? {}), id: feature.id },
        geometry: { type: 'MultiPoint', coordinates: coords },
      } as Feature);
    }
  }

  private _ingestSnapServerFeatures(
    geoJsonFeature: { features: Feature[] },
    layerType: string,
    lists: {
      pointList: Feature[];
      lineList: Feature[];
      polygonList: Feature[];
      vertexList: Feature[];
    },
    snapCache: number
  ): void {
    const { pointList, lineList, polygonList, vertexList } = lists;
    const features = geoJsonFeature?.features;
    if (!Array.isArray(features)) return;

    if (layerType === 'Point' || layerType === 'MultiPoint') {
      for (const feature of features) {
        if (pointList.length >= snapCache) break;
        if (!feature.geometry) continue;
        const next = {
          ...feature,
          geometry: geometryWebMercatorToLngLat(feature.geometry),
        } as Feature;
        pointList.push(next);
        // Points are also vertex snap targets.
        if (vertexList.length < snapCache) {
          const geom = next.geometry as any;
          if (geom?.type === 'Point' && Array.isArray(geom.coordinates)) {
            vertexList.push({
              type: 'Feature',
              properties: { ...(next.properties ?? {}), id: next.id },
              geometry: {
                type: 'MultiPoint',
                coordinates: [geom.coordinates],
              },
            } as Feature);
          } else if (geom?.type === 'MultiPoint') {
            vertexList.push({
              type: 'Feature',
              properties: { ...(next.properties ?? {}), id: next.id },
              geometry: geom,
            } as Feature);
          }
        }
      }
      return;
    }

    if (layerType === 'Polygon') {
      for (const feature of features) {
        if (polygonList.length >= snapCache) break;
        if (!feature.geometry) continue;
        const next = {
          ...JSON.parse(JSON.stringify(feature)),
          geometry: geometryWebMercatorToLngLat(feature.geometry),
        } as Feature;
        polygonList.push(next);

        const geom = next.geometry as any;
        if (geom?.type === 'Polygon' && Array.isArray(geom.coordinates)) {
          this._pushSnapVerticesFromRings(
            next,
            geom.coordinates,
            vertexList,
            snapCache
          );
        } else if (
          geom?.type === 'MultiPolygon' &&
          Array.isArray(geom.coordinates)
        ) {
          for (const poly of geom.coordinates) {
            if (!Array.isArray(poly)) continue;
            this._pushSnapVerticesFromRings(next, poly, vertexList, snapCache);
          }
        }
      }
      return;
    }

    if (layerType === 'Polyline') {
      for (const feature of features) {
        if (lineList.length >= snapCache) break;
        if (!feature.geometry) continue;
        const next = {
          ...JSON.parse(JSON.stringify(feature)),
          geometry: geometryWebMercatorToLngLat(feature.geometry),
        } as Feature;
        lineList.push(next);

        const geom = next.geometry as any;
        if (geom?.type === 'LineString' && Array.isArray(geom.coordinates)) {
          this._pushSnapVerticesFromRings(
            next,
            [geom.coordinates],
            vertexList,
            snapCache
          );
        } else if (
          geom?.type === 'MultiLineString' &&
          Array.isArray(geom.coordinates)
        ) {
          this._pushSnapVerticesFromRings(
            next,
            geom.coordinates,
            vertexList,
            snapCache
          );
        }
      }
    }
  }

  private _applySnapLayerData(lists: {
    pointList: Feature[];
    lineList: Feature[];
    polygonList: Feature[];
    vertexList: Feature[];
  }): void {
    try {
      const pointSrc = this.getSource('geobox-gl-snap-point') as Source | undefined;
      const polygonSrc = this.getSource('geobox-gl-snap-polygon') as Source | undefined;
      const lineSrc = this.getSource('geobox-gl-snap-line') as Source | undefined;
      const vertexSrc = this.getSource('geobox-gl-snap-vertex') as Source | undefined;
      if (!pointSrc || !polygonSrc || !lineSrc || !vertexSrc) return;

      const snapPointSource = pointSrc._data;
      const snapPolygonSource = polygonSrc._data;
      const snapLineSource = lineSrc._data;
      const snapVertexSource = vertexSrc._data;
      if (!snapPointSource || !snapPolygonSource || !snapLineSource || !snapVertexSource) {
        return;
      }

      snapPointSource.features = lists.pointList;
      snapPolygonSource.features = lists.polygonList;
      snapLineSource.features = lists.lineList;
      snapVertexSource.features = lists.vertexList;

      pointSrc.setData(snapPointSource);
      polygonSrc.setData(snapPolygonSource);
      lineSrc.setData(snapLineSource);
      vertexSrc.setData(snapVertexSource);
    } catch {
      /* snap sources may not exist yet (map style still loading) */
    }
  }

  async _updateSnapLayers() {
    const drawOpts: any = this.drawTools?.options;
    if (!this._isSnapDrawSessionActive() || !drawOpts?.snap) return;
    if (
      !this.getSource('geobox-gl-snap-point') ||
      !this.getSource('geobox-gl-snap-line') ||
      !this.getSource('geobox-gl-snap-polygon') ||
      !this.getSource('geobox-gl-snap-vertex')
    ) {
      return;
    }

    const requestId = ++this._snapLayersRequestId;
    const snapCache = Math.min(this.drawTools.options.snapCache ?? 500, 500);
    const mapBBox = this.getBounds();
    const mapBBoxCoord = [
      mapBBox['_sw']['lng'],
      mapBBox['_sw']['lat'],
      mapBBox['_ne']['lng'],
      mapBBox['_ne']['lat'],
    ];

    const lists = {
      pointList: [] as Feature[],
      polygonList: [] as Feature[],
      lineList: [] as Feature[],
      vertexList: [] as Feature[],
    };

    // Server default geometry is EPSG:3857; convert to WGS84 in _ingestSnapServerFeatures.
    const requestJSON = {
      f: 'json',
      select_fields: 'id',
      bbox: mapBBoxCoord,
      bbox_srid: 4326,
      limit: snapCache,
    };

    const featureLayers = this.getFeatureLayers();
    const fetchTasks: Promise<void>[] = [];

    for (const key in featureLayers) {
      const featureLayer = featureLayers[key];
      if (featureLayer.layerType === 'foreign') continue;
      if (!this._layerMatchesSnapSettings(key, featureLayer)) continue;
      if (!this._isMapLayerVisible(key)) continue;
      if (!featureLayer.source?.getFeatures) continue;

      fetchTasks.push(
        (async () => {
          try {
            const geoJsonFeature = await featureLayer.source.getFeatures(requestJSON);
            if (requestId !== this._snapLayersRequestId) return;
            this._ingestSnapServerFeatures(
              geoJsonFeature,
              featureLayer.layerType,
              lists,
              snapCache
            );
          } catch {
            /* ignore per-layer fetch errors */
          }
        })()
      );
    }

    await Promise.all(fetchTasks);
    if (requestId !== this._snapLayersRequestId) return;
    this._applySnapLayerData(lists);
  }

  flashCoordinate = async (longitude :number, latitude :number) => {
    try {
      const splashCircleSource = (<any>this.getSource(
        "geobox-gl-splash-circle"
      ))._data;

      splashCircleSource.features = [
        {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [longitude, latitude],
          },
        },
      ];

      (<any>this.getSource("geobox-gl-splash-circle")).setData(
        splashCircleSource
      );

      this.pulsingDot.start();

      setTimeout(() => {
        this.pulsingDot.stop();
      }, 1200);
    } catch (error) {
      throw new Error(String(error));
    }
  };

  changeFlashColor = (color:string) => {
    this.mapSettings["general_settings"]["flash_color"]= color;
  }

  changeSelectionColor = (selectionColor:string) => {
    this.mapSettings["general_settings"]["selection_color"]= selectionColor;
    for(const key in this.libreMapFeatureLayers){
      let layer = this.libreMapFeatureLayers[key];
      const paintProperty:any = this.getPaintProperty(
          layer.layerId,
          <string>layer.colorType
        );

        if (paintProperty[0] === "case") {
          paintProperty[2] = selectionColor;
        } else if (paintProperty[0] === "interpolate") {
          for (let i = 4; i < paintProperty.length; i += 2) {
            paintProperty[i][2] = selectionColor;
          }
        }

        this.setPaintProperty(
          layer.layerId,
          <string>layer.colorType,
          paintProperty
        );
    }
    

  };

  changeHighlightColor = (highlightColor:string) => {
    this.mapSettings["general_settings"]["highlight_color"]= highlightColor;
    for(const key in this.libreMapFeatureLayers){
      let layer = this.libreMapFeatureLayers[key];
      const paintProperty:any = this.getPaintProperty(
        layer.layerId,
        <string>layer.colorType
      );

      if (paintProperty[0] === "case") {
        if (layer.colorType !== "fill-color") paintProperty[4] = highlightColor;
      } else if (paintProperty[0] === "interpolate") {
        for (let i = 4; i < paintProperty.length; i += 2) {
          if (layer.colorType !== "fill-color")
            paintProperty[i][4] = highlightColor;
        }
      }

      this.setPaintProperty(
        layer.layerId,
        <string>layer.colorType,
        paintProperty
      );
    }
  };



  // goToXYCoordinate = (x: number = 0,y: number = 0,coordinateSystem: 'lnglat'| 'utm' = 'lnglat', zoonNumber: number =39) => {
  //   coordinateSystem = coordinateSystem || 'lnglat';
  //   let latitude: number = x, longitude: number= y;
  //   try {
  //      if (coordinateSystem === 'utm') {

  //       const xCoordinate = x;
  //       const yCoordinate = y;
        
  //       var utm = `+proj=utm +zone=${zoonNumber}`;
  //       var wgs84 = "+proj=longlat +ellps=WGS84 +datum=WGS84 +no_defs";

  //       [longitude, latitude] = (proj4(utm, wgs84, [Number(xCoordinate), Number(yCoordinate)]));

  //     }

  //     this.flyTo({
  //       ...{
  //         center: [longitude, latitude],
  //         zoom: 12,
  //       },
  //       duration: 1000,
  //       essential: true
  //     });

  //     this.flashCoordinate(longitude, latitude);

  //     // try {
  //     //   this.gotoMarker.remove();
  //     // } catch (error) { }

  //     // this.gotoMarker = new maplibregl.Marker({
  //     //   color: "red",
  //     //   // draggable: true
  //     // }).setLngLat([longitude, latitude])
  //     //   .addTo(this.map.LibreMap);
  //   } catch (error) {
   
  //   }

  // }







  // _addSnapMarkerLayer = () => {
  //   // setTimeout(() => {
  //   //   if (!this.getLayer("gl-draw-polygon-fill-inactive.cold.cold")) {
  //   //     this._addSnapMarkerLayer();
  //   //     return;
  //   //   }
  //     this.addLayer({
  //       id: "geobox-gl-snap-marker",
  //       type: "circle",
  //       source: "geobox-gl-snap-marker",
  //       paint: {
  //         "circle-color": "rgba(255, 255, 255, 1)",
  //         "circle-stroke-color": "rgba(255, 0, 0, 1)",
  //         "circle-stroke-width": 2,
  //         "circle-radius": 4,
  //       },
  //     });
  //   // }, 100);
  // };


  enableDualMap = async (
    serverMap: geoboxMap,
    userID?: number,
    containerId: string = Map.DUAL_MAP_CONTAINER_ID,
  ): Promise<void> => {
    const shell = document.querySelector(`.${Map.DUAL_MAP_SHELL_CLASS}`);
    const dualEl = document.getElementById(containerId);
    if (!dualEl) {
      throw new Error(`Dual map container #${containerId} not found`);
    }

    shell?.classList.add('geo-map-shell--dual');
    dualEl.removeAttribute('hidden');

    // Always recreate the secondary map so openMap does not style-diff against
    // a previous dual style (triggers setLayerProperty / serialize failures).
    if (this.dualMap) {
      this._unbindDualMapSync();
      try {
        this.dualMap.remove();
      } catch {
        /* ignore */
      }
      this.dualMap = null;
    }

    this.dualMap = new Map(containerId, this.server);

    await this.dualMap.openMap(serverMap, userID ?? this.userID);

    // openMap returns before style/basemap settle — force basemap onto the
    // secondary pane the same way the host does for the primary map.
    try {
      const generalSettings = serverMap.settings?.general_settings as
        | { custom_basemaps?: unknown; base_map?: string }
        | undefined;
      const customs = this.dualMap._normalizeCustomBasemaps(
        generalSettings?.custom_basemaps,
      );
      this.dualMap.setCustomBasemaps(customs);
      await this.dualMap._waitForStyleReady();
      await this.dualMap._applyBasemapFromSettings();
    } catch {
      /* ignore basemap hydration errors */
    }

    try {
      this.dualMap.stop();
    } catch {
      /* ignore */
    }

    // Always open the secondary map on its own saved view/extent.
    // Live sync (if enabled) only follows subsequent user pans/zooms.
    // Re-apply after layout settles — fitBounds is unreliable at 0×0 pane size.
    const applyDualCamera = () => {
      if (!this.dualMap) return;
      this._dualMapSyncing = true;
      try {
        this.dualMap._applySavedMapCamera(serverMap);
      } catch {
        /* ignore */
      }
      this._dualMapSyncing = false;
    };

    if (this._dualMapSyncEnabled) {
      this._bindDualMapSync();
    }

    const resizeBoth = () => {
      this.resize();
      this.dualMap?.resize();
    };
    requestAnimationFrame(() => {
      resizeBoth();
      applyDualCamera();
      requestAnimationFrame(() => {
        resizeBoth();
        applyDualCamera();
      });
    });
  };

  setDualMapSync = (enabled: boolean): void => {
    this._dualMapSyncEnabled = enabled;
    if (!this.dualMap) return;

    if (enabled) {
      if (!this._dualMapSyncPrimary) {
        this._bindDualMapSync();
      }
      this._dualMapSyncing = true;
      try {
        const center = this.getCenter();
        this.dualMap.jumpTo({
          center: [center.lng, center.lat],
          zoom: this.getZoom(),
          bearing: this.getBearing(),
          pitch: this.getPitch(),
        });
      } catch {
        /* ignore */
      }
      this._dualMapSyncing = false;
      return;
    }

    this._unbindDualMapSync();
  };

  setMapModels = (models: MapPlacedModel[]): void => {
    this.models3d.setModels(models);
  };

  setMapModelsFromDocument = (doc: unknown): void => {
    this.models3d.setFromDocument(doc);
  };

  getMapModels = (): MapPlacedModel[] => {
    return this.models3d.getModels();
  };

  getMapModelsDocument = (): MapModelsDocument => {
    return this.models3d.toDocument();
  };

  syncMapModels = async (): Promise<void> => {
    await this.models3d.sync();
  };

  disableDualMap = (): void => {
    this._unbindDualMapSync();
    if (this.dualMap) {
      this.dualMap.remove();
      this.dualMap = null;
    }

    const shell = document.querySelector(`.${Map.DUAL_MAP_SHELL_CLASS}`);
    const dualEl = document.getElementById(Map.DUAL_MAP_CONTAINER_ID);
    shell?.classList.remove('geo-map-shell--dual');
    dualEl?.setAttribute('hidden', '');

    requestAnimationFrame(() => this.resize());
  };

  private _bindDualMapSync(): void {
    if (!this.dualMap) return;
    this._unbindDualMapSync();

    this._dualMapSyncPrimary = () => {
      if (!this._dualMapSyncEnabled || this._dualMapSyncing || !this.dualMap) return;
      this._dualMapSyncing = true;
      const center = this.getCenter();
      this.dualMap.jumpTo({
        center: [center.lng, center.lat],
        zoom: this.getZoom(),
        bearing: this.getBearing(),
        pitch: this.getPitch(),
      });
      this._dualMapSyncing = false;
    };

    this._dualMapSyncSecondary = () => {
      if (!this._dualMapSyncEnabled || this._dualMapSyncing || !this.dualMap) return;
      this._dualMapSyncing = true;
      const center = this.dualMap.getCenter();
      this.jumpTo({
        center: [center.lng, center.lat],
        zoom: this.dualMap.getZoom(),
        bearing: this.dualMap.getBearing(),
        pitch: this.dualMap.getPitch(),
      });
      this._dualMapSyncing = false;
    };

    this.on('move', this._dualMapSyncPrimary);
    this.dualMap.on('move', this._dualMapSyncSecondary);
  }

  private _unbindDualMapSync(): void {
    if (this._dualMapSyncPrimary) {
      this.off('move', this._dualMapSyncPrimary);
      this._dualMapSyncPrimary = undefined;
    }
    if (this._dualMapSyncSecondary && this.dualMap) {
      this.dualMap.off('move', this._dualMapSyncSecondary);
      this._dualMapSyncSecondary = undefined;
    }
  }


}
